import * as React9 from 'react';
import { useStream } from '@authe/streaming';
import { createLogger } from '@authe/fe-commons/logging';
import { Position, Handle, MarkerType, useInternalNode, getBezierPath, BaseEdge, EdgeLabelRenderer, ReactFlowProvider, useNodesState, useEdgesState, useReactFlow, ReactFlow, Background, Controls, Panel, MiniMap } from '@xyflow/react';
import { GRAPH_EDGE_VARIANTS, GRAPH_EDGE_VARIANT_COLOR, GRAPH_EDGE_VARIANT_LABELS, GraphNode, RichTooltip, cn, Button, SearchIcon, Input, ChevronUpIcon, ChevronDownIcon, graphEdgeColor, graphNodeColor, ViewModeToggle, notifyError, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem, rankedCounts, GRAPH_NODE_VARIANT_LABELS, GRAPH_NODE_VARIANT_COLOR, NativeSelect, PlusIcon, LinkIcon, TooltipProvider, Slider, Badge, ChainAvatar, EmptyState, ErrorState, Spinner, FinalityPill, Popover, PopoverAnchor, PopoverContent, NodeAssignMenu, Tooltip, TooltipTrigger, TooltipContent, SettingsIcon, CheckIcon, DropdownMenuCheckboxItem, Label, TidyIcon, FlowIcon, BundleIcon, MinimizeIcon, MaximizeIcon } from '@authe/ui-core';
import { toSceneView, filterSceneViewByDetailTier, nodesWithHiddenChildren, toGraphView, hasAssignmentActions } from '@authe/fe-commons/transpose';
import { FinalityState, resolveChain, normalizeAddress, shortenAddress, chainDisplayName, getChainGroupColors } from '@authe/fe-commons/chains';
import { NODE_KIND_META, asTrimmedString, domainKindForSceneKind, nodeKindLabel, badgeForKind, GRAPH_VIEW_MODES, GRAPH_VIEW_MODE_LABELS, isOffChainKind, AUTHORABLE_NODE_KINDS, displayChainText, GRAPH_PANEL_LABELS, safeTicker, GRAPH_DETAIL_TIERS, GRAPH_DETAIL_TIER_LABELS } from '@authe/fe-commons/display';
export { AUTHORABLE_NODE_KINDS, DEFAULT_GRAPH_DETAIL_TIER, GRAPH_DETAIL_TIERS, GRAPH_DETAIL_TIER_LABELS, GRAPH_NODE_KINDS, GRAPH_VIEW_MODES, GRAPH_VIEW_MODE_LABELS, NODE_KIND_META, badgeForKind, domainKindForSceneKind, isOffChainKind, labelForKind, nodeKindLabel } from '@authe/fe-commons/display';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { AMOUNT_BASIS, MERGED_COUNT_ATTR } from '@authe/fe-commons/models';
import { formatGraphAmount, sumAmounts, formatTokenDisplay, formatUsd, formatUnits } from '@authe/fe-commons/value';
import dagre from 'dagre';
import ELK from 'elkjs/lib/elk.bundled.js';
import { parseKeyAttributeGroups } from '@authe/fe-commons/storage';
import { exportDataset, useExportLedger, ExportLedgerStatus, buildExportFilename } from '@authe/export';
import { toPng } from 'html-to-image';
import { sagaIdLabel, roleLabel } from '@authe/fe-commons/grouping';
import { formatTimestamp } from '@authe/fe-commons/time';
import { useDepsChanged } from '@authe/ui-core/api';
import { useResolveShare, AccessWarningBanner } from '@authe/saved-views';

var __defProp = Object.defineProperty;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __esm = (fn, res) => function __init() {
  return fn && (res = (0, fn[__getOwnPropNames(fn)[0]])(fn = 0)), res;
};
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};
function mergeNeighborhood(nodes, edges, incoming) {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const edgeIds = new Set(edges.map((e) => e.id));
  return {
    nodes: [...nodes, ...incoming.nodes.filter((n) => !nodeIds.has(n.id))],
    edges: [...edges, ...incoming.edges.filter((e) => !edgeIds.has(e.id))]
  };
}
function useGraphLiveUpdates(options) {
  const { channel, parse, onUpdate, enabled = true } = options;
  const parseRef = React9.useRef(parse);
  parseRef.current = parse;
  const onUpdateRef = React9.useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const handleMessage = React9.useCallback((message) => {
    const neighborhood = parseRef.current(message);
    if (neighborhood && (neighborhood.nodes.length || neighborhood.edges.length)) {
      onUpdateRef.current(neighborhood, message);
    }
  }, []);
  useStream(channel, { onMessage: handleMessage, enabled });
}
var init_useGraphLiveUpdates = __esm({
  "src/live/useGraphLiveUpdates.ts"() {
  }
});

// src/live/index.ts
var init_live = __esm({
  "src/live/index.ts"() {
    init_useGraphLiveUpdates();
  }
});

// src/share/LiveGraphLayer.tsx
var LiveGraphLayer_exports = {};
__export(LiveGraphLayer_exports, {
  default: () => LiveGraphLayer_default
});
function LiveGraphLayer({
  channel,
  nodesRef,
  edgesRef,
  parse,
  onMerge
}) {
  useGraphLiveUpdates({
    channel,
    parse,
    onUpdate: (incoming) => {
      onMerge(mergeNeighborhood(nodesRef.current, edgesRef.current, incoming));
    }
  });
  return null;
}
var LiveGraphLayer_default;
var init_LiveGraphLayer = __esm({
  "src/share/LiveGraphLayer.tsx"() {
    init_live();
    LiveGraphLayer_default = LiveGraphLayer;
  }
});
var NODE_KIND_VARIANT = {
  eoa: "address",
  contract: "contract",
  entity: "entity",
  cluster: "cluster",
  tx: "tx",
  exchange: "entity",
  defi: "contract",
  token: "contract",
  // On-chain (so it stays inside its chain rectangle) but reads as a structured
  // protocol artifact rather than a wallet.
  message: "contract",
  oracle: "oracle",
  bridge: "bridge",
  don: "don",
  mixer: "mixer",
  // Reuses the oracle visual: the design system has no dedicated verification
  // variant, and a verification step is off-chain infra like the DON it hangs
  // under.
  verification: "oracle",
  // The bridge visual family, since a relayer serves a bridge — its own badge is
  // what separates it from the hub.
  relayer: "bridge",
  // A DON rather than a bridge: like an oracle network it is a set of competing
  // agents the transaction hands work to, not a route value travels along.
  settlement: "don"
};
var NODE_KIND_REGISTRY = Object.fromEntries(
  Object.keys(NODE_KIND_META).map((kind) => [
    kind,
    { ...NODE_KIND_META[kind], variant: NODE_KIND_VARIANT[kind] }
  ])
);
function variantForKind(kind, fallback = "address") {
  return kind ? NODE_KIND_VARIANT[kind] : fallback;
}
function formatFactValue(value, kind) {
  if (value == null) return "";
  if (Array.isArray(value)) return value.length === 1 ? "1 item" : `${value.length} items`;
  if (typeof value === "object") return "\u2026";
  if (kind === "chain" && typeof value === "string") return chainDisplayName(value) || value;
  return String(value);
}
function factRows(facts) {
  return (facts ?? []).map((f) => ({ label: f.label || f.key, value: formatFactValue(f.value, f.kind) })).filter((r) => r.value !== "");
}
function withContextRows(rows, extra) {
  const out = [...rows];
  const seen = new Set(out.map((r) => r.label.toLowerCase()));
  for (const [label, value] of extra) {
    if (!value || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    out.push({ label, value });
  }
  return out;
}
var ORIGIN_SUBTITLE = {
  synthesized: "Expected \u2014 inferred, not observed in this transaction",
  user: "Added manually by an analyst"
};
function contestedRow(claims) {
  if (!claims || claims.length < 2) return ["Disputed", void 0];
  const field = claims[0]?.field ?? "value";
  const rivals = claims.map((c) => c.by ? `${c.value} (${c.by})` : c.value).join(" vs ");
  return [`Disputed ${field}`, `${rivals} \u2014 same tier, no precedence between them`];
}
var GraphAssignmentMenuContext = React9.createContext(null);
var GraphExpandContext = React9.createContext(null);
var GraphGroupBandContext = React9.createContext(null);
var DEFAULT_GRAPH_RENDER_OPTIONS = {
  particles: false,
  direction: "LR"
};
var GraphRenderContext = React9.createContext(DEFAULT_GRAPH_RENDER_OPTIONS);
function useGraphRenderOptions() {
  return React9.useContext(GraphRenderContext);
}
function assetRows(assets) {
  return (assets ?? []).map((asset) => {
    const name = asset.symbol ?? shortenAddress(asset.tokenAddress ?? "") ?? "Token";
    const label = asset.direction === "out" ? "Sold" : asset.direction === "in" ? "Bought" : "Moved";
    const floor = asset.basis === "quoted_minimum" ? " (min)" : "";
    return {
      label,
      value: asset.amount ? `${asset.amount} ${name}${floor}` : name
    };
  });
}
function nodeTooltipRows(node) {
  return withContextRows([...factRows(node.facts), ...assetRows(node.assets)], [
    ["Chain", node.chain],
    ["Address", node.address],
    ["Value", node.value],
    // Only worth stating when attribution is actually uncertain.
    [
      "Confidence",
      node.confidence != null && node.confidence < 1 ? `${Math.round(node.confidence * 100)}%` : void 0
    ],
    contestedRow(node.contested)
  ]);
}
function txNode(id, data, position = { x: 0, y: 0 }) {
  return { id, type: "authe", position, data };
}
function AutheGraphNode({ id, data, selected, isConnectable }) {
  const node = data;
  const assignMenu = React9.useContext(GraphAssignmentMenuContext);
  const canAssign = Boolean(assignMenu?.enabled && node.address);
  const expand = React9.useContext(GraphExpandContext);
  const expandable = Boolean(expand?.enabled) && expand.isExpandable(id);
  const expanded = Boolean(expand?.enabled) && expand.isExpanded(id);
  const showExpand = expandable || expanded;
  const handleExpand = React9.useCallback(
    (event) => {
      event.stopPropagation();
      expand?.toggle(id);
    },
    [expand, id]
  );
  const handleAssign = React9.useCallback(
    (event) => {
      event.stopPropagation();
      assignMenu?.open(
        {
          id,
          label: node.label,
          address: node.address,
          chainId: node.chain,
          entityId: node.entityId
        },
        event.clientX,
        event.clientY
      );
    },
    [assignMenu, id, node.label, node.address, node.chain, node.entityId]
  );
  const rows = nodeTooltipRows(node);
  const body = /* @__PURE__ */ jsx(
    GraphNode,
    {
      label: node.label,
      subtitle: node.subtitle,
      chain: node.chain,
      icon: node.icon,
      iconAlt: node.iconAlt,
      badge: node.badge ?? badgeForKind(node.kind),
      contested: Boolean(node.contested?.length),
      variant: node.variant ?? variantForKind(node.kind),
      accentClassName: node.accentClassName,
      riskLevel: node.riskLevel,
      riskScore: node.riskScore,
      tags: node.tags,
      value: node.value,
      confidence: node.confidence,
      dimmed: node.dimmed,
      highlighted: node.highlighted,
      failed: node.failed,
      virtual: node.virtual,
      selected,
      onAssign: canAssign ? handleAssign : void 0
    }
  );
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(ConnectionHandles, { type: "target", isConnectable }),
    rows.length > 0 && !node.dimmed ? /* @__PURE__ */ jsx(
      RichTooltip,
      {
        title: node.label,
        subtitle: (node.origin ? ORIGIN_SUBTITLE[node.origin] : void 0) ?? node.subtitle ?? void 0,
        rows,
        side: "top",
        align: "center",
        children: body
      }
    ) : body,
    showExpand ? /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: handleExpand,
        "aria-label": expanded ? "Hide node detail" : "Show node detail",
        "aria-expanded": expanded,
        title: expanded ? "Hide detail" : "Show detail",
        className: "nodrag absolute -bottom-2 left-1/2 z-10 flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:text-foreground",
        children: /* @__PURE__ */ jsx(
          "svg",
          {
            viewBox: "0 0 24 24",
            width: "12",
            height: "12",
            fill: "none",
            stroke: "currentColor",
            strokeWidth: "2.5",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            "aria-hidden": true,
            style: { transform: expanded ? "rotate(180deg)" : void 0 },
            children: /* @__PURE__ */ jsx("path", { d: "M6 9l6 6 6-6" })
          }
        )
      }
    ) : null,
    /* @__PURE__ */ jsx(ConnectionHandles, { type: "source", isConnectable })
  ] });
}
var ANCHOR = {
  TB: { source: Position.Bottom, target: Position.Top },
  BT: { source: Position.Top, target: Position.Bottom },
  LR: { source: Position.Right, target: Position.Left },
  RL: { source: Position.Left, target: Position.Right }
};
var ALL_SIDES = [Position.Top, Position.Right, Position.Bottom, Position.Left];
function ConnectionHandles({
  type,
  isConnectable
}) {
  const { direction } = useGraphRenderOptions();
  const anchor = (ANCHOR[direction] ?? ANCHOR.LR)[type];
  return /* @__PURE__ */ jsx(Fragment, { children: ALL_SIDES.map((position) => /* @__PURE__ */ jsx(
    Handle,
    {
      id: position === anchor ? void 0 : `${type}-${position}`,
      type,
      position,
      isConnectable,
      className: handleClass(isConnectable, position === anchor)
    },
    position
  )) });
}
function handleClass(connectable, drawn) {
  if (!connectable) return "!h-2 !w-2 !border-0 !bg-transparent !pointer-events-none opacity-0";
  if (!drawn) return "!h-3 !w-3 !border-0 !bg-transparent";
  return "!h-2 !w-2 !border-0 !bg-muted-foreground";
}
function chainGroupNode(id, data, rect) {
  return {
    id,
    type: "chain-group",
    position: { x: rect.x, y: rect.y },
    data,
    // Behind the regular nodes, and inert: no drag/select/connect, and the DOM
    // ignores pointer events (set in the component) so the canvas stays pannable.
    zIndex: -1,
    selectable: false,
    draggable: false,
    connectable: false,
    focusable: false,
    style: { width: rect.width, height: rect.height, pointerEvents: "none" }
  };
}
function ChainGroupNodeView({ data }) {
  const group = data;
  const bands = React9.useContext(GraphGroupBandContext);
  const groupId = group.groupId;
  const openable = Boolean(bands?.enabled && groupId);
  const labelClass = "absolute left-2 top-1 rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide";
  const labelStyle = {
    color: group.text,
    backgroundColor: "var(--card, rgba(255,255,255,0.7))"
  };
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "pointer-events-none h-full w-full rounded-xl border-2 border-dashed transition-opacity",
      style: {
        backgroundColor: group.fill,
        borderColor: group.border,
        opacity: group.dimmed ? 0.25 : 1
      },
      children: openable ? /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => bands.open(groupId),
          title: `Show what is in ${group.label}`,
          className: `${labelClass} pointer-events-auto cursor-pointer underline-offset-2 transition-opacity hover:underline hover:opacity-80`,
          style: labelStyle,
          children: group.label
        }
      ) : /* @__PURE__ */ jsx("span", { className: `${labelClass} pointer-events-none`, style: labelStyle, children: group.label })
    }
  );
}
var nodeTypes = { authe: AutheGraphNode, "chain-group": ChainGroupNodeView };
function centre(box) {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}
function boundaryPoint(box, toward) {
  const c = centre(box);
  const dx = toward.x - c.x;
  const dy = toward.y - c.y;
  const halfWidth = Math.max(box.width, 1) / 2;
  const halfHeight = Math.max(box.height, 1) / 2;
  if (dx === 0 && dy === 0) {
    return { x: c.x, y: c.y + halfHeight, position: Position.Bottom };
  }
  const scaleX = halfWidth / Math.abs(dx || Number.EPSILON);
  const scaleY = halfHeight / Math.abs(dy || Number.EPSILON);
  const scale = Math.min(scaleX, scaleY);
  const x = c.x + dx * scale;
  const y = c.y + dy * scale;
  const horizontal = scaleX < scaleY;
  const position = horizontal ? dx > 0 ? Position.Right : Position.Left : dy > 0 ? Position.Bottom : Position.Top;
  return { x, y, position };
}
function floatingEdgeGeometry(source, target) {
  if (!source || !target) return null;
  if (!source.width || !source.height || !target.width || !target.height) return null;
  const from = boundaryPoint(source, centre(target));
  const to = boundaryPoint(target, centre(source));
  return {
    sourceX: from.x,
    sourceY: from.y,
    sourcePosition: from.position,
    targetX: to.x,
    targetY: to.y,
    targetPosition: to.position
  };
}
function nodeBox(internal) {
  const node = internal;
  if (!node) return null;
  const position = node.internals?.positionAbsolute ?? node.position;
  const width = node.measured?.width ?? node.width;
  const height = node.measured?.height ?? node.height;
  if (!position || !width || !height) return null;
  return { x: position.x, y: position.y, width, height };
}
var EDGE_STROKE = GRAPH_EDGE_VARIANT_COLOR;
function edgeColor(data) {
  if (data?.failed) return EDGE_STROKE.failed;
  return EDGE_STROKE[data?.variant ?? "default"];
}
function edgeStrokeWidth(data, selected = false) {
  let width = 1.5;
  const usd = data?.valueUsd;
  if (usd != null && usd > 0) {
    width = 1.5 + Math.min(6, Math.log10(usd + 1) * 0.9);
  } else {
    const amt = typeof data?.amount === "number" ? data.amount : Number.parseFloat(String(data?.amount ?? ""));
    if (Number.isFinite(amt) && amt > 0) width = 1.5 + Math.min(4, Math.log10(amt + 1) * 0.6);
  }
  if (data?.aggregated && data.count && data.count > 1) {
    width = Math.min(8, width + Math.log2(data.count));
  }
  if (data?.highlighted) width += 1.25;
  if (selected) width = Math.max(width, 2.5);
  return Math.round(width * 10) / 10;
}
function txEdge(id, source, target, data = {}) {
  return {
    id,
    source,
    target,
    type: "authe",
    data,
    // Demixing/mixer-traced edges animate so heuristic links stand out.
    animated: data.demix === true,
    markerEnd: { type: MarkerType.ArrowClosed, color: edgeColor(data), width: 16, height: 16 }
  };
}
function edgeUsd(value) {
  return formatUsd(value, { compact: true });
}
function hasTokenFlow(data) {
  return data?.amount != null || data?.ticker != null || data?.tokenLogo != null;
}
function EdgeLabelContent({ data }) {
  if (hasTokenFlow(data)) {
    const display = formatGraphAmount(data.amount, { maxChars: 14 });
    const minDisplay = formatGraphAmount(data.minAmount, { maxChars: 12 });
    const ticker = data.ticker ? safeTicker(data.ticker) : void 0;
    const isFloor = data.basis === AMOUNT_BASIS.QUOTED_MINIMUM;
    return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
      data.tokenLogo && /* @__PURE__ */ jsx("img", { src: data.tokenLogo, alt: "", "aria-hidden": true, className: "h-3 w-3 shrink-0 rounded-full object-cover" }),
      (display || data.ticker) && /* @__PURE__ */ jsxs(
        "span",
        {
          className: "font-mono tabular-nums",
          title: display?.abbreviated || ticker?.truncated ? `${display?.title ?? ""}${ticker ? ` ${ticker.title}` : ""}`.trim() : void 0,
          children: [
            display?.text,
            display && data.ticker ? " " : "",
            ticker?.text,
            isFloor && /* @__PURE__ */ jsx("span", { className: "ml-1 text-muted-foreground", children: "(min)" }),
            minDisplay && /* @__PURE__ */ jsxs(
              "span",
              {
                className: "ml-1 text-muted-foreground",
                title: minDisplay.abbreviated ? minDisplay.title : void 0,
                children: [
                  "(min ",
                  minDisplay.text,
                  ticker ? ` ${ticker.text}` : "",
                  ")"
                ]
              }
            )
          ]
        }
      ),
      data.valueUsd != null && /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: edgeUsd(data.valueUsd) })
    ] });
  }
  if (data.label) {
    const shown = displayChainText(data.label, { maxChars: 80 });
    return /* @__PURE__ */ jsx("span", { className: "block truncate font-medium", title: shown.title, children: shown.text });
  }
  return null;
}
function edgeKindLabel(kind) {
  if (!kind) return "Flow";
  const spaced = kind.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
function edgeTooltipRows(data) {
  const amount = [data.amount != null ? String(data.amount) : void 0, data.ticker].filter(Boolean).join(" ");
  return withContextRows(factRows(data.facts), [
    ["Amount", amount || void 0],
    ["Value", data.valueUsd != null ? edgeUsd(data.valueUsd) : void 0],
    // Only meaningful when the endpoints actually differ.
    [
      "Route",
      data.fromChain && data.toChain && data.fromChain !== data.toChain ? `${chainDisplayName(data.fromChain)} \u2192 ${chainDisplayName(data.toChain)}` : void 0
    ],
    [
      "Bundled flows",
      data.aggregated && data.count && data.count > 1 ? String(data.count) : void 0
    ],
    // Named rather than implied: on the netted view an amount is what survived
    // the opposing movements, not a transfer anyone can point at on-chain.
    [
      "Netted",
      data.netZero ? "cancels out \u2014 equal value moved both ways" : data.netted ? "net of opposing movements" : void 0
    ],
    // An edge that hides a router has to say so, or the settlement view reads as
    // a direct payment between two addresses that never dealt with each other.
    [
      "Through",
      data.compressedVia?.length ? `${data.compressedVia.length} intermediar${data.compressedVia.length === 1 ? "y" : "ies"} (netted out)` : void 0
    ],
    contestedRow(data.contested)
  ]);
}
function FlowParticles({
  path,
  color,
  bidirectional,
  width
}) {
  const r = Math.max(2, Math.min(3.5, width));
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("circle", { r, fill: color, children: /* @__PURE__ */ jsx("animateMotion", { dur: "2.4s", repeatCount: "indefinite", path }) }),
    bidirectional ? /* @__PURE__ */ jsx("circle", { r, fill: color, opacity: 0.85, children: /* @__PURE__ */ jsx(
      "animateMotion",
      {
        dur: "2.4s",
        repeatCount: "indefinite",
        path,
        keyPoints: "1;0",
        keyTimes: "0;1",
        calcMode: "linear"
      }
    ) }) : null
  ] });
}
var PARALLEL_ARC_SPACING = 30;
var PARALLEL_LABEL_SPREAD = 0.6;
function quadraticPoint(p0, control, p2, t) {
  const mt = 1 - t;
  return mt * mt * p0 + 2 * mt * t * control + t * t * p2;
}
function parallelEdgeGeometry(sourceX, sourceY, targetX, targetY, index, count) {
  if (count <= 1) return null;
  const flip = sourceY > targetY || sourceY === targetY && sourceX > targetX;
  const ax = flip ? targetX : sourceX;
  const ay = flip ? targetY : sourceY;
  const bx = flip ? sourceX : targetX;
  const by = flip ? sourceY : targetY;
  const midX = (ax + bx) / 2;
  const midY = (ay + by) / 2;
  const dx = bx - ax;
  const dy = by - ay;
  const length = Math.hypot(dx, dy) || 1;
  const perpX = -dy / length;
  const perpY = dx / length;
  const centred = index - (count - 1) / 2;
  const offset = centred * PARALLEL_ARC_SPACING;
  const controlX = midX + perpX * offset * 2;
  const controlY = midY + perpY * offset * 2;
  const spread = Math.min(PARALLEL_LABEL_SPREAD, 0.24 * (count - 1));
  const t = 0.5 + centred / (count - 1) * spread;
  const labelX = quadraticPoint(ax, controlX, bx, t);
  const labelY = quadraticPoint(ay, controlY, by, t);
  return {
    path: `M ${sourceX},${sourceY} Q ${controlX},${controlY} ${targetX},${targetY}`,
    labelX,
    labelY
  };
}
function AutheGraphEdge(props) {
  const { selected } = props;
  const sourceNode = useInternalNode(props.source);
  const targetNode = useInternalNode(props.target);
  const floating = floatingEdgeGeometry(nodeBox(sourceNode), nodeBox(targetNode));
  const sourceX = floating?.sourceX ?? props.sourceX;
  const sourceY = floating?.sourceY ?? props.sourceY;
  const targetX = floating?.targetX ?? props.targetX;
  const targetY = floating?.targetY ?? props.targetY;
  const sourcePosition = floating?.sourcePosition ?? props.sourcePosition;
  const targetPosition = floating?.targetPosition ?? props.targetPosition;
  const data = props.data;
  const demix = data?.demix === true;
  const dimmed = data?.dimmed === true;
  const { particles } = useGraphRenderOptions();
  const fan = parallelEdgeGeometry(
    sourceX,
    sourceY,
    targetX,
    targetY,
    data?.parallelIndex ?? 0,
    data?.parallelCount ?? 1
  );
  const [bezierPath, bezierLabelX, bezierLabelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition
  });
  const edgePath = fan?.path ?? bezierPath;
  const labelX = fan?.labelX ?? bezierLabelX;
  const labelY = fan?.labelY ?? bezierLabelY;
  const color = edgeColor(data);
  const width = edgeStrokeWidth(data, selected);
  const hasLabel = Boolean(data && (data.label || hasTokenFlow(data)));
  const showParticles = particles && data?.animated === true && !dimmed;
  const rows = data ? edgeTooltipRows(data) : [];
  const hoverable = Boolean(data) && !dimmed && rows.length > 0;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      BaseEdge,
      {
        id: props.id,
        path: edgePath,
        markerEnd: props.markerEnd,
        style: {
          stroke: color,
          strokeWidth: width,
          opacity: dimmed ? 0.12 : 1,
          // Demixing edges always render as a traced (dashed) line.
          strokeDasharray: demix ? "2 4" : data?.dashed ? "5 4" : void 0,
          strokeLinecap: demix ? "round" : void 0,
          transition: "opacity 120ms ease, stroke-width 120ms ease"
        }
      }
    ),
    showParticles ? /* @__PURE__ */ jsx(FlowParticles, { path: edgePath, color, bidirectional: data?.bidirectional === true, width }) : null,
    hoverable && data ? /* @__PURE__ */ jsx(
      RichTooltip,
      {
        title: data.label || edgeKindLabel(data.kind),
        subtitle: data.origin ? ORIGIN_SUBTITLE[data.origin] : void 0,
        rows,
        side: "top",
        align: "center",
        children: /* @__PURE__ */ jsx(
          "path",
          {
            d: edgePath,
            fill: "none",
            stroke: "transparent",
            strokeWidth: Math.max(16, width + 12),
            style: { pointerEvents: "stroke", cursor: "help" }
          }
        )
      }
    ) : null,
    hasLabel && data && !dimmed ? /* @__PURE__ */ jsx(EdgeLabelRenderer, { children: /* @__PURE__ */ jsx(
      "div",
      {
        style: {
          position: "absolute",
          transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
          // Inert so a hover over the label falls through to the interaction
          // path beneath it — otherwise the label would be a dead zone in the
          // middle of the very edge it describes.
          pointerEvents: "none"
        },
        className: "max-w-[16rem] rounded bg-card px-1.5 py-0.5 text-[10px] text-foreground shadow-sm",
        children: /* @__PURE__ */ jsx(EdgeLabelContent, { data })
      }
    ) }) : null
  ] });
}
var edgeTypes = { authe: AutheGraphEdge };

// src/engines/flow/sceneGroups.ts
var DEFAULT_NODE_W = 260;
var DEFAULT_NODE_H = 100;
var DEFAULT_PAD = 30;
var HEADER_PAD = 14;
var NEST_GAP = 16;
var BAND_BREATHING = 24;
var MAX_NEST_DEPTH = 2;
var MAX_ENTANGLED_BANDS = 4;
var GROUP_Z_BASE = -10;
function rectArea(r) {
  return Math.max(0, r.maxX - r.minX) * Math.max(0, r.maxY - r.minY);
}
function intersectionArea(a, b) {
  const w = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  const h = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  return w > 0 && h > 0 ? w * h : 0;
}
function unionRect(a, b) {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY)
  };
}
function isSubset(child, parent) {
  if (child.size > parent.size) return false;
  for (const id of child) if (!parent.has(id)) return false;
  return true;
}
function sharesMember(a, b) {
  const [small, large] = a.size <= b.size ? [a, b] : [b, a];
  for (const id of small) if (large.has(id)) return true;
  return false;
}
function sizeOf(measured, styled, fallback) {
  return measured ?? (typeof styled === "number" ? styled : fallback);
}
function footprints(nodes, w, h, positions, sizes) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return (id) => {
    const node = byId.get(id);
    if (!node) return void 0;
    const at = positions?.[id] ?? node.position;
    const measured = sizes?.[id];
    return {
      x: at.x,
      y: at.y,
      w: measured?.width ?? sizeOf(node.measured?.width, node.style?.width, w),
      h: measured?.height ?? sizeOf(node.measured?.height, node.style?.height, h)
    };
  };
}
function memberBox(memberNodeIds, at) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const members = /* @__PURE__ */ new Set();
  for (const memberId of memberNodeIds) {
    const box = at(memberId);
    if (!box) continue;
    members.add(memberId);
    minX = Math.min(minX, box.x);
    minY = Math.min(minY, box.y);
    maxX = Math.max(maxX, box.x + box.w);
    maxY = Math.max(maxY, box.y + box.h);
  }
  return members.size === 0 ? null : { members, raw: { minX, minY, maxX, maxY } };
}
function groupBoxes(groups, at) {
  const boxes = [];
  for (const group of groups) {
    const box = memberBox(group.memberNodeIds, at);
    if (box) boxes.push({ group, ...box });
  }
  return boxes;
}
function byAreaDesc(boxes) {
  return boxes.map((_, i) => i).sort(
    (a, b) => rectArea(boxes[b].raw) - rectArea(boxes[a].raw) || (boxes[a].group.id < boxes[b].group.id ? -1 : 1)
  );
}
function entangledTypes(boxes) {
  const byType = /* @__PURE__ */ new Map();
  for (const box of boxes) {
    const bucket = byType.get(box.group.groupType);
    if (bucket) bucket.push(box);
    else byType.set(box.group.groupType, [box]);
  }
  const loose = /* @__PURE__ */ new Set();
  for (const [type, bucket] of byType) {
    if (bucket.length <= MAX_ENTANGLED_BANDS) continue;
    let entangled = 0;
    for (let i = 0; i < bucket.length; i++) {
      const shares = bucket.some(
        (other, j) => i !== j && sharesMember(bucket[i].members, other.members)
      );
      if (shares) entangled += 1;
    }
    if (entangled > MAX_ENTANGLED_BANDS) loose.add(type);
  }
  return loose;
}
function computeParents(boxes) {
  const loose = entangledTypes(boxes);
  const rank = /* @__PURE__ */ new Map();
  byAreaDesc(boxes).forEach((idx, r) => rank.set(idx, r));
  return boxes.map((child, ci) => {
    if (loose.has(child.group.groupType)) return null;
    const ca = rectArea(child.raw);
    let best = null;
    let bestArea = Infinity;
    for (let pi = 0; pi < boxes.length; pi++) {
      if (pi === ci) continue;
      const parent = boxes[pi];
      if (loose.has(parent.group.groupType)) continue;
      const pa = rectArea(parent.raw);
      const larger = pa > ca || pa === ca && rank.get(pi) < rank.get(ci);
      if (!larger) continue;
      const nestable = isSubset(child.members, parent.members) || sharesMember(child.members, parent.members) && intersectionArea(child.raw, parent.raw) > 0;
      if (!nestable) continue;
      if (pa < bestArea) {
        bestArea = pa;
        best = pi;
      }
    }
    return best;
  });
}
function capNesting(parent, boxes) {
  const capped = [...parent];
  const depth = new Array(boxes.length).fill(0);
  for (const i of byAreaDesc(boxes)) {
    const p = capped[i];
    if (p == null) continue;
    const d = depth[p] + 1;
    if (d > MAX_NEST_DEPTH) capped[i] = null;
    else depth[i] = d;
  }
  return capped;
}
function buildChainGroupNodes(nodes, groups, options = {}) {
  if (groups.length === 0 || nodes.length === 0) return [];
  const w = options.nodeWidth ?? DEFAULT_NODE_W;
  const h = options.nodeHeight ?? DEFAULT_NODE_H;
  const pad = options.padding ?? DEFAULT_PAD;
  const boxes = groupBoxes(groups, footprints(nodes, w, h, void 0, options.sizes));
  if (boxes.length === 0) return [];
  const parent = capNesting(computeParents(boxes), boxes);
  const depthCache = new Array(boxes.length);
  const depthOf = (i) => {
    const cached = depthCache[i];
    if (cached !== void 0) return cached;
    const p = parent[i];
    const d = p == null ? 0 : depthOf(p) + 1;
    depthCache[i] = d;
    return d;
  };
  const depths = boxes.map((_, i) => depthOf(i));
  const maxDepth = depths.reduce((m, d) => Math.max(m, d), 0);
  const eff = boxes.map((b) => ({ ...b.raw }));
  for (let i = 0; i < boxes.length; i++) {
    let a = parent[i];
    while (a != null) {
      eff[a] = unionRect(eff[a], boxes[i].raw);
      a = parent[a];
    }
  }
  const built = boxes.map((box, i) => {
    const depth = depths[i];
    const extra = (maxDepth - depth) * NEST_GAP;
    const r = eff[i];
    const node = chainGroupNode(
      box.group.id,
      {
        label: box.group.label,
        // Stated rather than left implicit in the node id: it is what makes the
        // label clickable, and a band synthesized from something other than a
        // scene group must stay inert.
        groupId: box.group.id,
        fill: box.group.fill,
        border: box.group.border,
        text: box.group.text
      },
      {
        x: r.minX - pad - extra,
        y: r.minY - pad - extra - HEADER_PAD,
        width: r.maxX - r.minX + (pad + extra) * 2,
        height: r.maxY - r.minY + (pad + extra) * 2 + HEADER_PAD
      }
    );
    return { node: { ...node, zIndex: GROUP_Z_BASE + depth }, depth };
  });
  built.sort((a, b) => a.depth - b.depth);
  return built.map((b) => b.node);
}
function pairwiseDisjoint(boxes) {
  const seen = /* @__PURE__ */ new Set();
  for (const box of boxes) {
    for (const id of box.members) {
      if (seen.has(id)) return false;
      seen.add(id);
    }
  }
  return true;
}
function dominantPartition(boxes) {
  const byType = /* @__PURE__ */ new Map();
  for (const box of boxes) {
    const bucket = byType.get(box.group.groupType);
    if (bucket) bucket.push(box);
    else byType.set(box.group.groupType, [box]);
  }
  let best = [];
  let bestReach = 0;
  for (const [, bucket] of [...byType].sort(([a], [b]) => a < b ? -1 : 1)) {
    if (bucket.length < 2 || !pairwiseDisjoint(bucket)) continue;
    const reach = bucket.reduce((total, box) => total + box.members.size, 0);
    if (bucket.length > best.length || bucket.length === best.length && reach > bestReach) {
      best = bucket;
      bestReach = reach;
    }
  }
  return best;
}
function separateGroupBands(positions, nodes, groups, options = {}) {
  if (groups.length < 2 || nodes.length === 0) return positions;
  const pad = options.padding ?? DEFAULT_PAD;
  const at = footprints(
    nodes,
    options.nodeWidth ?? DEFAULT_NODE_W,
    options.nodeHeight ?? DEFAULT_NODE_H,
    positions,
    options.sizes
  );
  const bands = dominantPartition(groupBoxes(groups, at));
  if (bands.length < 2) return positions;
  const gap = 2 * (pad + NEST_GAP) + HEADER_PAD + BAND_BREATHING;
  const acrossY = (options.direction ?? "LR") === "LR" || options.direction === "RL";
  const start = (b) => acrossY ? b.raw.minY : b.raw.minX;
  const end = (b) => acrossY ? b.raw.maxY : b.raw.maxX;
  let edge = null;
  let moved = null;
  for (const band of [...bands].sort((a, b) => start(a) - start(b))) {
    const shift = edge === null ? 0 : Math.max(0, edge + gap - start(band));
    if (shift > 0) {
      moved ??= { ...positions };
      for (const id of band.members) {
        const box = at(id);
        if (box) moved[id] = acrossY ? { x: box.x, y: box.y + shift } : { x: box.x + shift, y: box.y };
      }
    }
    edge = Math.max(edge ?? -Infinity, end(band) + shift);
  }
  return moved ?? positions;
}
function PanelCloseButton({ onClick, label, className }) {
  return /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      onClick,
      "aria-label": label,
      title: label,
      className: cn(
        "pointer-events-auto flex h-4 w-4 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
        className
      ),
      children: /* @__PURE__ */ jsx(
        "svg",
        {
          viewBox: "0 0 24 24",
          width: "11",
          height: "11",
          fill: "none",
          stroke: "currentColor",
          strokeWidth: "2.5",
          strokeLinecap: "round",
          "aria-hidden": true,
          children: /* @__PURE__ */ jsx("path", { d: "M6 6l12 12M18 6L6 18" })
        }
      )
    }
  );
}
var DEFAULT_EDGE_LEGEND = GRAPH_EDGE_VARIANTS.map((variant) => ({
  label: GRAPH_EDGE_VARIANT_LABELS[variant],
  color: GRAPH_EDGE_VARIANT_COLOR[variant],
  shape: variant === "flow" ? "dashed" : "line"
}));
function Swatch({ color, shape = "line" }) {
  if (shape === "dot") {
    return /* @__PURE__ */ jsx("span", { className: "h-2.5 w-2.5 rounded-full", style: { backgroundColor: color } });
  }
  if (shape === "box") {
    return /* @__PURE__ */ jsx("span", { className: "h-2.5 w-3 rounded-sm border", style: { borderColor: color, backgroundColor: `${color}` } });
  }
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: "h-0 w-4",
      style: {
        borderTopWidth: 2,
        borderTopStyle: shape === "dashed" ? "dashed" : "solid",
        borderTopColor: color
      }
    }
  );
}
function LegendRows({ items }) {
  return /* @__PURE__ */ jsx("ul", { className: "space-y-1", children: items.map((item) => /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-2 text-muted-foreground", children: [
    /* @__PURE__ */ jsx(Swatch, { color: item.color, shape: item.shape }),
    /* @__PURE__ */ jsx("span", { children: item.label })
  ] }, `${item.group ?? ""}:${item.label}`)) });
}
function partitionByGroup(items) {
  const ungrouped = [];
  const groups = [];
  const index = /* @__PURE__ */ new Map();
  for (const item of items) {
    if (!item.group) {
      ungrouped.push(item);
      continue;
    }
    let gi = index.get(item.group);
    if (gi === void 0) {
      gi = groups.length;
      index.set(item.group, gi);
      groups.push({ name: item.group, items: [] });
    }
    groups[gi].items.push(item);
  }
  return { ungrouped, groups };
}
function GraphLegend({ items, title = "Legend", className, onClose }) {
  const { ungrouped, groups } = partitionByGroup(items);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "pointer-events-none relative select-none rounded-md border bg-card/90 p-2 text-xs shadow-sm backdrop-blur",
        onClose && "pr-6",
        className
      ),
      children: [
        onClose ? /* @__PURE__ */ jsx(PanelCloseButton, { className: "absolute right-1 top-1", onClick: onClose, label: "Hide legend" }) : null,
        ungrouped.length > 0 ? /* @__PURE__ */ jsxs("div", { children: [
          title ? /* @__PURE__ */ jsx("p", { className: "mb-1 font-medium text-foreground", children: title }) : null,
          /* @__PURE__ */ jsx(LegendRows, { items: ungrouped })
        ] }) : null,
        groups.map((group, gi) => /* @__PURE__ */ jsxs("div", { className: cn((ungrouped.length > 0 || gi > 0) && "mt-2"), children: [
          /* @__PURE__ */ jsx("p", { className: "mb-1 font-medium text-foreground", children: group.name }),
          /* @__PURE__ */ jsx(LegendRows, { items: group.items })
        ] }, group.name))
      ]
    }
  );
}
var DEFAULTS = {
  direction: "LR",
  nodeWidth: 260,
  nodeHeight: 100,
  rankSep: 110,
  nodeSep: 90
};
function positionsOf(nodes) {
  const positions = {};
  for (const node of nodes) positions[node.id] = node.position;
  return positions;
}
function nodeSetSignature(nodes) {
  return nodes.map((node) => node.id).sort().join("\0");
}
var WITH_FLOW = {
  TB: { source: "bottom", target: "top" },
  BT: { source: "top", target: "bottom" },
  LR: { source: "right", target: "left" },
  RL: { source: "left", target: "right" }
};
var OPPOSITE = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left"
};
function ranksVertically(direction) {
  return direction === "TB" || direction === "BT";
}
function chooseEdgeSides(positions, nodes, edges, direction, sizes) {
  const canonical = WITH_FLOW[direction] ?? WITH_FLOW.LR;
  const vertical = ranksVertically(direction);
  const out = {};
  if (edges.length === 0) return out;
  const centre2 = (id) => {
    const at = positions[id];
    if (!at) return void 0;
    const size = sizes?.[id];
    return {
      x: at.x + (size?.width ?? DEFAULTS.nodeWidth) / 2,
      y: at.y + (size?.height ?? DEFAULTS.nodeHeight) / 2
    };
  };
  const span = vertical ? "height" : "width";
  let typical = 0;
  for (const node of nodes) {
    typical = Math.max(typical, sizes?.[node.id]?.[span] ?? 0);
  }
  const sameRank = (typical || (vertical ? 100 : 260)) * 0.5;
  for (const edge of edges) {
    const from = centre2(edge.source);
    const to = centre2(edge.target);
    if (!from || !to) continue;
    const along = vertical ? to.y - from.y : to.x - from.x;
    const across = vertical ? to.x - from.x : to.y - from.y;
    const forward = direction === "TB" || direction === "LR" ? along : -along;
    if (forward > sameRank) continue;
    if (Math.abs(along) <= sameRank) {
      const near = vertical ? across >= 0 ? "right" : "left" : across >= 0 ? "bottom" : "top";
      out[edge.id] = { source: near, target: OPPOSITE[near] };
      continue;
    }
    out[edge.id] = { source: canonical.target, target: canonical.source };
  }
  return out;
}
function sizesInvalidate(previous, next) {
  for (const [id, size] of Object.entries(next)) {
    const before = previous[id];
    if (!before) continue;
    if (before.width !== size.width || before.height !== size.height) return true;
  }
  return false;
}
function placeAddedNodes(known, nodes, edges, options = {}) {
  const opts = { ...DEFAULTS, ...options };
  const present = new Set(nodes.map((node) => node.id));
  const placed = {};
  for (const node of nodes) {
    const position = known[node.id];
    if (position) placed[node.id] = position;
  }
  let pending = nodes.filter((node) => !placed[node.id]);
  if (pending.length === 0) return placed;
  const neighbours = /* @__PURE__ */ new Map();
  const link = (from, to) => {
    const list = neighbours.get(from);
    if (list) list.push(to);
    else neighbours.set(from, [to]);
  };
  for (const edge of edges) {
    if (!present.has(edge.source) || !present.has(edge.target)) continue;
    link(edge.source, edge.target);
    link(edge.target, edge.source);
  }
  while (pending.length > 0) {
    const stuck = [];
    for (const node of pending) {
      const anchors = (neighbours.get(node.id) ?? []).map((id) => placed[id]).filter((position) => position !== void 0);
      if (anchors.length === 0) {
        stuck.push(node);
        continue;
      }
      placed[node.id] = freeSpot(centroid(anchors), placed, opts);
    }
    if (stuck.length === pending.length) break;
    pending = stuck;
  }
  let next = { x: rightEdge(placed) + opts.nodeWidth + opts.nodeSep, y: 0 };
  for (const node of pending) {
    placed[node.id] = next;
    next = { x: next.x, y: next.y + opts.nodeHeight + opts.nodeSep };
  }
  return placed;
}
function centroid(positions) {
  const sum = positions.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / positions.length, y: sum.y / positions.length };
}
function rightEdge(placed) {
  let max = 0;
  for (const position of Object.values(placed)) max = Math.max(max, position.x);
  return max;
}
function freeSpot(start, placed, opts) {
  const stepX = opts.nodeWidth + opts.nodeSep;
  const stepY = opts.nodeHeight + opts.nodeSep;
  const taken = Object.values(placed);
  const collides = (candidate) => taken.some(
    (p) => Math.abs(p.x - candidate.x) < stepX * 0.9 && Math.abs(p.y - candidate.y) < stepY * 0.9
  );
  for (let rank = 0; rank <= 4; rank += 1) {
    for (const row of rowsFrom(rank)) {
      const candidate = { x: start.x + rank * stepX, y: start.y + row * stepY };
      if (!collides(candidate)) return candidate;
    }
  }
  return { x: start.x + 5 * stepX, y: start.y };
}
function rowsFrom(reach) {
  const rows = [0];
  for (let step = 1; step <= reach; step += 1) rows.push(step, -step);
  return rows;
}
function dims(node, opts) {
  const measured = opts.sizes?.[node.id];
  return {
    width: measured?.width ?? node.measured?.width ?? opts.nodeWidth,
    height: measured?.height ?? node.measured?.height ?? opts.nodeHeight
  };
}
var SIZE_GRID = 4;
function collectNodeSizes(nodes) {
  const sizes = {};
  for (const node of nodes) {
    const { width, height } = node.measured ?? {};
    if (width == null || height == null || width <= 0 || height <= 0) continue;
    sizes[node.id] = {
      width: Math.ceil(width / SIZE_GRID) * SIZE_GRID,
      height: Math.ceil(height / SIZE_GRID) * SIZE_GRID
    };
  }
  return sizes;
}
function nodeSizeSignature(sizes) {
  return Object.keys(sizes).sort().map((id) => `${id}:${sizes[id].width}x${sizes[id].height}`).join("\0");
}
function connectedEdges(nodes, edges) {
  const present = new Set(nodes.map((node) => node.id));
  return edges.filter((edge) => present.has(edge.source) && present.has(edge.target));
}
function layoutWithDagre(nodes, edges, options = {}) {
  const opts = { ...DEFAULTS, ...options };
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: opts.direction, ranksep: opts.rankSep, nodesep: opts.nodeSep });
  for (const node of nodes) {
    const { width, height } = dims(node, opts);
    graph.setNode(node.id, { width, height });
  }
  for (const edge of connectedEdges(nodes, edges)) {
    graph.setEdge(edge.source, edge.target);
  }
  dagre.layout(graph);
  return nodes.map((node) => {
    const laid = graph.node(node.id);
    if (!laid) return node;
    const { width, height } = dims(node, opts);
    return { ...node, position: { x: laid.x - width / 2, y: laid.y - height / 2 } };
  });
}
var ELK_DIRECTION = {
  TB: "DOWN",
  BT: "UP",
  LR: "RIGHT",
  RL: "LEFT"
};
async function layoutWithElk(nodes, edges, options = {}) {
  const opts = { ...DEFAULTS, ...options };
  const elk = new ELK();
  const layoutOptions = {
    "elk.algorithm": "layered",
    "elk.direction": ELK_DIRECTION[opts.direction],
    "elk.layered.spacing.nodeNodeBetweenLayers": String(opts.rankSep),
    "elk.spacing.nodeNode": String(opts.nodeSep)
  };
  const graph = {
    id: "root",
    layoutOptions,
    children: nodes.map((node) => {
      const { width, height } = dims(node, opts);
      return { id: node.id, width, height };
    }),
    edges: connectedEdges(nodes, edges).map((edge) => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target]
    }))
  };
  const result = await elk.layout(graph);
  const positioned = new Map((result.children ?? []).map((child) => [child.id, child]));
  return nodes.map((node) => {
    const laid = positioned.get(node.id);
    if (!laid || laid.x === void 0 || laid.y === void 0) return node;
    return { ...node, position: { x: laid.x, y: laid.y } };
  });
}
function layoutDirectionForView(view) {
  return view === "temporal" ? "LR" : "TB";
}
function layoutForView(view, overrides) {
  if (view === "force") return false;
  return { direction: layoutDirectionForView(view), ...overrides };
}
function engineForView(view) {
  return view === "force" ? "force" : "flow";
}

// src/search/match.ts
var GRAPH_SEARCH_FIELDS = [
  "id",
  "label",
  "subtitle",
  "chain",
  "badge",
  "tags",
  "address",
  "entity",
  "protocol",
  "kind",
  "value",
  "facts",
  "members"
];
var GRAPH_SEARCH_MODES = ["contains", "prefix", "exact"];
var DEFAULT_SEARCH_FIELDS = [
  "id",
  "label",
  "subtitle",
  "chain",
  "badge",
  "tags",
  "address",
  "entity",
  "protocol",
  "kind",
  "members"
];
var ALL_SEARCH_FIELDS = GRAPH_SEARCH_FIELDS;
function factText(facts) {
  if (!Array.isArray(facts)) return [];
  const out = [];
  for (const fact of facts) {
    if (!fact || typeof fact !== "object") continue;
    const { label, value } = fact;
    if (typeof label === "string") out.push(label);
    if (typeof value === "string" || typeof value === "number") out.push(String(value));
  }
  return out;
}
function fieldText(node, field) {
  const data = node.data;
  switch (field) {
    case "id":
      return [node.id];
    case "label":
      return [data.label];
    case "subtitle":
      return [data.subtitle];
    case "chain":
      return [data.chain];
    case "badge":
      return [data.badge];
    case "tags":
      return data.tags ?? [];
    case "address":
      return [data.address];
    case "entity":
      return [data.entityId];
    case "protocol":
      return [data.protocol];
    case "kind":
      return [data.kind];
    case "value":
      return [data.value];
    case "facts":
      return factText(data.facts);
    case "members":
      return data.memberSearchText ? [...data.memberSearchText] : [];
    default:
      return [];
  }
}
function hit(text2, query, mode) {
  switch (mode) {
    case "prefix":
      return text2.startsWith(query);
    case "exact":
      return text2 === query;
    default:
      return text2.includes(query);
  }
}
function matchGraphNodes(nodes, query, spec = {}) {
  const q = query.trim().toLowerCase();
  if (!q) return new Set(nodes.map((n) => n.id));
  const fields = spec.fields ?? DEFAULT_SEARCH_FIELDS;
  const mode = spec.mode ?? "contains";
  const matched = /* @__PURE__ */ new Set();
  for (const node of nodes) {
    for (const field of fields) {
      const texts = fieldText(node, field);
      const found = texts.some(
        (text2) => typeof text2 === "string" && text2.length > 0 && hit(text2.toLowerCase(), q, mode)
      );
      if (found) {
        matched.add(node.id);
        break;
      }
    }
  }
  return matched;
}
function allNodesMatch(nodes, matched) {
  return matched.size >= nodes.length;
}
function useGraphSearch(nodes, spec = {}, options = {}) {
  const [query, setQueryState] = React9.useState(options.initialQuery ?? "");
  const [index, setIndex] = React9.useState(0);
  const fields = spec.fields;
  const mode = spec.mode;
  const matches = React9.useMemo(
    () => matchGraphNodes(nodes, query, { fields, mode }),
    [nodes, query, fields, mode]
  );
  const trimmed = query.trim();
  const active = trimmed.length > 0 && matches.size < nodes.length;
  const matchIds = React9.useMemo(
    () => active ? nodes.filter((node) => matches.has(node.id)).map((node) => node.id) : [],
    [active, nodes, matches]
  );
  const setQuery = React9.useCallback((next) => {
    setQueryState(next);
    setIndex(0);
  }, []);
  const step = React9.useCallback(
    (delta) => {
      setIndex((prev) => {
        if (matchIds.length === 0) return 0;
        return (prev + delta + matchIds.length) % matchIds.length;
      });
    },
    [matchIds.length]
  );
  const clear = React9.useCallback(() => {
    setQueryState("");
    setIndex(0);
  }, []);
  const safeIndex = matchIds.length === 0 ? -1 : Math.min(index, matchIds.length - 1);
  return {
    query,
    setQuery,
    matches,
    matchIds,
    active,
    index: safeIndex,
    focusId: safeIndex >= 0 ? matchIds[safeIndex] : void 0,
    next: React9.useCallback(() => step(1), [step]),
    prev: React9.useCallback(() => step(-1), [step]),
    clear
  };
}
function GraphSearchBox({
  search,
  placeholder = "Search nodes",
  className
}) {
  const { query, setQuery, matchIds, index, next, prev, clear } = search;
  const inputRef = React9.useRef(null);
  const [open, setOpen] = React9.useState(false);
  const expanded = open || query.length > 0;
  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (event.shiftKey) prev();
      else next();
    } else if (event.key === "Escape") {
      event.preventDefault();
      clear();
      setOpen(false);
      inputRef.current?.blur();
    }
  };
  if (!expanded) {
    return /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "sm",
        "aria-label": "Search the graph",
        title: "Search the graph",
        className: cn("h-7 w-7 p-0", className),
        onClick: () => {
          setOpen(true);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        },
        children: /* @__PURE__ */ jsx(SearchIcon, { size: 13 })
      }
    );
  }
  const count = matchIds.length;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-1", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx("span", { className: "pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground", children: /* @__PURE__ */ jsx(SearchIcon, { size: 13 }) }),
      /* @__PURE__ */ jsx(
        Input,
        {
          ref: inputRef,
          value: query,
          onChange: (event) => setQuery(event.target.value),
          onKeyDown,
          onBlur: () => {
            if (query.length === 0) setOpen(false);
          },
          placeholder,
          "aria-label": "Search the graph",
          className: "h-7 w-40 pl-7 pr-2 text-xs"
        }
      )
    ] }),
    query.trim().length > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "span",
        {
          className: "min-w-[3.5rem] text-center text-xs tabular-nums text-muted-foreground",
          "aria-live": "polite",
          children: count === 0 ? "No matches" : `${index + 1} of ${count}`
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          variant: "outline",
          size: "sm",
          className: "h-7 w-7 p-0",
          "aria-label": "Previous match",
          title: "Previous match (Shift+Enter)",
          disabled: count === 0,
          onClick: prev,
          children: /* @__PURE__ */ jsx(ChevronUpIcon, { size: 13 })
        }
      ),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          variant: "outline",
          size: "sm",
          className: "h-7 w-7 p-0",
          "aria-label": "Next match",
          title: "Next match (Enter)",
          disabled: count === 0,
          onClick: next,
          children: /* @__PURE__ */ jsx(ChevronDownIcon, { size: 13 })
        }
      )
    ] }) : null
  ] });
}

// src/cluster/clustering.ts
var clusterNodeId = (clusterId) => `cluster:${clusterId}`;
function clusterIdFromNodeId(nodeId) {
  return nodeId.startsWith("cluster:") ? nodeId.slice("cluster:".length) : null;
}
function buildClusters(nodes, keyFn, options = {}) {
  const minSize = options.minSize ?? 2;
  const map = /* @__PURE__ */ new Map();
  const order = [];
  for (const node of nodes) {
    const key = keyFn(node);
    if (!key) continue;
    let def = map.get(key.id);
    if (!def) {
      def = { id: key.id, label: key.label ?? key.id, memberIds: [], chain: key.chain };
      map.set(key.id, def);
      order.push(key.id);
    }
    def.memberIds.push(node.id);
  }
  return order.map((id) => map.get(id)).filter((c) => c.memberIds.length >= minSize);
}
var dataStr = (value) => typeof value === "string" && value ? value : void 0;
var clusterByEntity = (node) => {
  const id = dataStr(node.data.entityId) ?? dataStr(node.data.clusterId);
  if (!id) return null;
  const label = dataStr(node.data.entityLabel) ?? dataStr(node.data.clusterLabel) ?? id;
  return { id: `entity:${id}`, label, chain: dataStr(node.data.chain) };
};
var clusterByKind = (node) => {
  const kind = dataStr(node.data.kind);
  if (!kind) return null;
  return { id: `kind:${kind}`, label: kind };
};
var clusterByChain = (node) => {
  const chain = dataStr(node.data.chain);
  if (!chain) return null;
  return { id: `chain:${chain}`, label: chain, chain };
};
function searchTextOf(node) {
  const data = node.data;
  const out = [node.id];
  for (const value of [
    data.label,
    data.subtitle,
    data.address,
    data.entityId,
    data.protocol,
    data.chain,
    data.badge,
    data.kind
  ]) {
    if (typeof value === "string" && value) out.push(value);
  }
  for (const tag of data.tags ?? []) {
    if (typeof tag === "string" && tag) out.push(tag);
  }
  return out;
}
function centroid2(nodes) {
  if (nodes.length === 0) return { x: 0, y: 0 };
  const sum = nodes.reduce((acc, n) => ({ x: acc.x + n.position.x, y: acc.y + n.position.y }), { x: 0, y: 0 });
  return { x: sum.x / nodes.length, y: sum.y / nodes.length };
}
function applyClusters(nodes, edges, clusters, collapsed) {
  const collapsedClusters = clusters.filter((c) => collapsed.has(c.id));
  if (collapsedClusters.length === 0) return { nodes, edges };
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const memberToCluster = /* @__PURE__ */ new Map();
  for (const c of collapsedClusters) {
    const cid = clusterNodeId(c.id);
    for (const m of c.memberIds) if (nodeById.has(m)) memberToCluster.set(m, cid);
  }
  const outNodes = nodes.filter((n) => !memberToCluster.has(n.id));
  for (const c of collapsedClusters) {
    const members = c.memberIds.map((id) => nodeById.get(id)).filter((n) => Boolean(n));
    if (members.length === 0) continue;
    outNodes.push(
      txNode(
        clusterNodeId(c.id),
        {
          label: c.label,
          subtitle: `${members.length} nodes`,
          variant: "cluster",
          badge: String(members.length),
          chain: c.chain,
          cluster: true,
          clusterId: c.id,
          memberCount: members.length,
          // The members themselves, so a host can list what was folded in here.
          // Expanding is the other answer to that question, and it answers it by
          // putting all of them back on the canvas.
          memberIds: members.map((member) => member.id),
          memberSearchText: members.flatMap(searchTextOf)
        },
        centroid2(members)
      )
    );
  }
  const remap = (id) => memberToCluster.get(id) ?? id;
  const agg = /* @__PURE__ */ new Map();
  for (const e of edges) {
    const source = remap(e.source);
    const target = remap(e.target);
    if (source === target) continue;
    const variant = e.data?.variant ?? "default";
    const key = `${source}|${target}|${variant}`;
    const existing = agg.get(key);
    if (existing) existing.members.push(e);
    else agg.set(key, { edge: { ...e, source, target }, members: [e] });
  }
  const outEdges = [];
  for (const [key, { edge, members }] of agg) {
    const count = members.length;
    if (count > 1) {
      outEdges.push(txEdge(`agg:${key}`, edge.source, edge.target, aggregateData(members, count)));
    } else {
      outEdges.push(edge);
    }
  }
  return { nodes: outNodes, edges: outEdges };
}
function aggregateData(members, count) {
  const first = members[0];
  const failed = members.some((e) => e.data?.failed === true);
  const animated = members.some((e) => e.data?.animated === true);
  let usd = 0;
  let hasUsd = false;
  for (const e of members) {
    if (typeof e.data?.valueUsd === "number" && Number.isFinite(e.data.valueUsd)) {
      usd += e.data.valueUsd;
      hasUsd = true;
    }
  }
  const agreed = (read) => {
    const value = read(first);
    return value !== void 0 && members.every((e) => read(e) === value) ? value : void 0;
  };
  return {
    variant: failed ? "failed" : first.data?.variant ?? "default",
    label: `\xD7${count}`,
    valueUsd: hasUsd ? usd : void 0,
    failed,
    animated: animated && !failed,
    kind: agreed((e) => e.data?.kind),
    fromChain: agreed((e) => e.data?.fromChain),
    toChain: agreed((e) => e.data?.toChain),
    aggregated: true,
    count,
    memberIds: members.map((e) => e.id)
  };
}

// src/cluster/useGraphClusters.ts
function useGraphClusters(options) {
  const { nodes, edges, keyFn, minSize, initialCollapsed = "all" } = options;
  const clusters = React9.useMemo(() => buildClusters(nodes, keyFn, { minSize }), [nodes, keyFn, minSize]);
  const clusterIds = React9.useMemo(() => clusters.map((c) => c.id), [clusters]);
  const seed = React9.useCallback(
    (ids) => {
      if (initialCollapsed === "none") return /* @__PURE__ */ new Set();
      if (initialCollapsed === "all") return new Set(ids);
      return new Set(initialCollapsed.filter((id) => ids.includes(id)));
    },
    [initialCollapsed]
  );
  const [collapsed, setCollapsed] = React9.useState(() => seed(clusterIds));
  const knownRef = React9.useRef(clusterIds);
  React9.useEffect(() => {
    const known = new Set(knownRef.current);
    const fresh = clusterIds.filter((id) => !known.has(id));
    if (fresh.length === 0 && knownRef.current.length === clusterIds.length) return;
    setCollapsed((prev) => {
      const next = new Set([...prev].filter((id) => clusterIds.includes(id)));
      for (const id of seed(fresh)) next.add(id);
      return next;
    });
    knownRef.current = clusterIds;
  }, [clusterIds, seed]);
  const derived = React9.useMemo(
    () => applyClusters(nodes, edges, clusters, collapsed),
    [nodes, edges, clusters, collapsed]
  );
  const collapse = React9.useCallback((id) => {
    setCollapsed((prev) => prev.has(id) ? prev : new Set(prev).add(id));
  }, []);
  const expand = React9.useCallback((id) => {
    setCollapsed((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const toggle = React9.useCallback(
    (id) => {
      if (!id) return;
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    []
  );
  const collapseAll = React9.useCallback(() => setCollapsed(new Set(clusterIds)), [clusterIds]);
  const expandAll = React9.useCallback(() => setCollapsed(/* @__PURE__ */ new Set()), []);
  const isCollapsed = React9.useCallback((id) => collapsed.has(id), [collapsed]);
  return {
    clusters,
    collapsed,
    nodes: derived.nodes,
    edges: derived.edges,
    isCollapsed,
    collapse,
    expand,
    toggle,
    collapseAll,
    expandAll
  };
}
var log = createLogger("tx-graph:layout");
var NO_LAYOUT = { key: "", nonce: 0, groupKey: "", sizeEpoch: -1, positions: {} };
function needsFreshArrangement(cache, next) {
  return cache.key === "" || cache.nonce !== next.nonce || cache.sizeEpoch !== next.sizeEpoch || cache.groupKey !== next.groupKey;
}
var NO_SIZES = { sizes: {}, key: "", epoch: 0 };
function mergeSizes(prev, nodes) {
  const fresh = collectNodeSizes(nodes.filter((node) => node.type !== "chain-group"));
  const merged = {};
  for (const node of nodes) {
    if (node.type === "chain-group") continue;
    const size = fresh[node.id] ?? prev.sizes[node.id];
    if (size) merged[node.id] = size;
  }
  const key = nodeSizeSignature(merged);
  if (key === prev.key) return prev;
  return {
    sizes: merged,
    key,
    epoch: sizesInvalidate(prev.sizes, merged) ? prev.epoch + 1 : prev.epoch
  };
}
var NO_CLUSTER = () => null;
function TransactionGraphInner({
  nodes,
  edges,
  layout,
  viewMode = "hierarchical",
  legend,
  showMiniMap = true,
  showControls = true,
  showBackground = true,
  fitView = true,
  className,
  onNodeClick,
  onNodeDoubleClick,
  onEdgeClick,
  onConnect,
  layoutNonce = 0,
  focusNodeId,
  searchQuery,
  onSelectionChange,
  live,
  layoutEngine = "dagre",
  autoCluster = true,
  pinnedPositions,
  onNodePositionChange,
  chainGroups,
  flowParticles = true,
  highlightNeighbors = true,
  assignment
}) {
  const clusterCfg = typeof autoCluster === "object" ? autoCluster : {};
  const clusterThreshold = clusterCfg.threshold ?? 100;
  const clusterEnabled = autoCluster !== false && nodes.length > clusterThreshold;
  const keyFn = clusterEnabled ? clusterCfg.by ?? clusterByEntity : NO_CLUSTER;
  const clusters = useGraphClusters({ nodes, edges, keyFn, initialCollapsed: "all" });
  const sourceNodes = clusterEnabled ? clusters.nodes : nodes;
  const sourceEdges = clusterEnabled ? clusters.edges : edges;
  const [dragOverrides, setDragOverrides] = React9.useState(
    () => pinnedPositions ?? {}
  );
  const seenPins = React9.useRef({});
  React9.useEffect(() => {
    const incoming = pinnedPositions ?? {};
    const arrived = {};
    let any = false;
    for (const [id, position] of Object.entries(incoming)) {
      const seen = seenPins.current[id];
      if (seen && seen.x === position.x && seen.y === position.y) continue;
      arrived[id] = position;
      any = true;
    }
    seenPins.current = incoming;
    if (any) setDragOverrides((prev) => ({ ...prev, ...arrived }));
  }, [pinnedPositions]);
  const lastTidy = React9.useRef(layoutNonce);
  React9.useEffect(() => {
    if (lastTidy.current === layoutNonce) return;
    lastTidy.current = layoutNonce;
    setDragOverrides(pinnedPositions ?? {});
  }, [layoutNonce, pinnedPositions]);
  const pinned = React9.useMemo(
    () => ({ ...pinnedPositions ?? {}, ...dragOverrides }),
    [dragOverrides, pinnedPositions]
  );
  const resolvedLayout = React9.useMemo(
    () => layout !== void 0 ? layout : layoutForView(viewMode),
    [layout, viewMode]
  );
  const useElk = layoutEngine === "elk" && resolvedLayout !== false;
  const [measured, setMeasured] = React9.useState(NO_SIZES);
  const sizedLayout = React9.useMemo(
    () => resolvedLayout === false ? false : { ...resolvedLayout, sizes: measured.sizes },
    [resolvedLayout, measured.sizes]
  );
  const nodeSetKey = React9.useMemo(() => nodeSetSignature(sourceNodes), [sourceNodes]);
  const groupKey = React9.useMemo(
    () => (chainGroups ?? []).map((group) => `${group.id}=${[...group.memberNodeIds].sort().join(",")}`).sort().join("\0"),
    [chainGroups]
  );
  const layoutKey = `${nodeSetKey}${groupKey}`;
  const [dagreLayout, setDagreLayout] = React9.useState(NO_LAYOUT);
  const dagreActive = resolvedLayout !== false && !useElk;
  const dagreStale = dagreLayout.key !== layoutKey || dagreLayout.nonce !== layoutNonce || dagreLayout.sizeEpoch !== measured.epoch;
  if (dagreActive && sizedLayout !== false && dagreStale) {
    const laid = needsFreshArrangement(dagreLayout, {
      nonce: layoutNonce,
      groupKey,
      sizeEpoch: measured.epoch
    }) ? positionsOf(layoutWithDagre(sourceNodes, sourceEdges, sizedLayout)) : placeAddedNodes(dagreLayout.positions, sourceNodes, sourceEdges, sizedLayout);
    setDagreLayout({
      key: layoutKey,
      nonce: layoutNonce,
      groupKey,
      sizeEpoch: measured.epoch,
      // Pull the chain bands apart before anything is drawn: the rectangles are
      // derived from these positions, so the nodes are what has to move.
      positions: separateGroupBands(laid, sourceNodes, chainGroups ?? [], sizedLayout)
    });
  }
  const dagrePositions = dagreActive ? dagreLayout.positions : null;
  const [elkPositions, setElkPositions] = React9.useState(null);
  React9.useEffect(() => {
    if (sizedLayout === false || !useElk) {
      setElkPositions(null);
      return void 0;
    }
    let alive = true;
    void layoutWithElk(sourceNodes, sourceEdges, sizedLayout).then((laid) => {
      if (alive) {
        setElkPositions(separateGroupBands(positionsOf(laid), sourceNodes, chainGroups ?? [], sizedLayout));
      }
    }).catch((error) => {
      if (!alive) return;
      log.warn("elk layout failed; falling back to dagre", { error: String(error) });
      const fallback = positionsOf(layoutWithDagre(sourceNodes, sourceEdges, sizedLayout));
      setElkPositions(separateGroupBands(fallback, sourceNodes, chainGroups ?? [], sizedLayout));
    });
    return () => {
      alive = false;
    };
  }, [useElk, nodeSetKey, groupKey, layoutNonce, resolvedLayout, measured.epoch]);
  const layoutDirection = resolvedLayout === false ? "LR" : resolvedLayout.direction ?? "LR";
  const laidOutNodes = React9.useMemo(() => {
    const computed = useElk ? elkPositions : dagrePositions;
    if (!computed && Object.keys(pinned).length === 0) return sourceNodes;
    return sourceNodes.map((node) => {
      const position = pinned[node.id] ?? computed?.[node.id];
      return position ? { ...node, position } : node;
    });
  }, [useElk, elkPositions, dagrePositions, sourceNodes, pinned]);
  const edgeSides = React9.useMemo(
    () => chooseEdgeSides(
      positionsOf(laidOutNodes),
      laidOutNodes,
      sourceEdges,
      layoutDirection,
      measured.sizes
    ),
    [laidOutNodes, sourceEdges, layoutDirection, measured.sizes]
  );
  const groupNodes = React9.useMemo(() => {
    if (!chainGroups || chainGroups.length === 0) return [];
    const dims2 = resolvedLayout ? { nodeWidth: resolvedLayout.nodeWidth, nodeHeight: resolvedLayout.nodeHeight } : {};
    return buildChainGroupNodes(laidOutNodes, chainGroups, { ...dims2, sizes: measured.sizes });
  }, [laidOutNodes, chainGroups, resolvedLayout, measured.sizes]);
  const matched = React9.useMemo(
    () => searchQuery && searchQuery.trim() ? matchGraphNodes(sourceNodes, searchQuery) : null,
    [sourceNodes, searchQuery]
  );
  const adjacency = React9.useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    const link = (a, b) => {
      let set = map.get(a);
      if (!set) map.set(a, set = /* @__PURE__ */ new Set());
      set.add(b);
    };
    for (const edge of sourceEdges) {
      link(edge.source, edge.target);
      link(edge.target, edge.source);
    }
    return map;
  }, [sourceEdges]);
  const [hoverId, setHoverId] = React9.useState(null);
  const focusId = highlightNeighbors ? hoverId ?? focusNodeId ?? null : null;
  const focusSet = React9.useMemo(() => {
    if (!focusId) return null;
    const set = /* @__PURE__ */ new Set([focusId]);
    for (const neighbor of adjacency.get(focusId) ?? []) set.add(neighbor);
    return set;
  }, [focusId, adjacency]);
  const [rfNodes, setNodes, onNodesChange] = useNodesState(laidOutNodes);
  const [rfEdges, setEdges, onEdgesChange] = useEdgesState(sourceEdges);
  const reactFlow = useReactFlow();
  React9.useEffect(() => {
    setNodes((prev) => {
      const previous = new Map(prev.map((n) => [n.id, n]));
      const decorated = laidOutNodes.map((node) => {
        const carried = previous.get(node.id);
        return {
          ...node,
          // Carry the size the renderer already measured for this node. It isn't
          // on `laidOutNodes` (those come from the props array), so rebuilding
          // without it makes every node unmeasured again — which would drop the
          // sizes the layout above depends on and re-run it on the fallbacks.
          measured: carried?.measured ?? node.measured,
          data: {
            ...node.data,
            dimmed: Boolean(carried?.data.dimmed),
            highlighted: Boolean(carried?.data.highlighted)
          }
        };
      });
      return [...groupNodes, ...decorated];
    });
  }, [laidOutNodes, groupNodes, setNodes]);
  React9.useEffect(() => {
    setMeasured((prev) => mergeSizes(prev, rfNodes));
  }, [rfNodes]);
  React9.useEffect(() => {
    setNodes(
      (prev) => prev.map((node) => {
        if (node.type === "chain-group") return node;
        const searchDimmed = matched ? !matched.has(node.id) : false;
        const focusDimmed = focusSet ? !focusSet.has(node.id) : false;
        const highlighted = focusSet ? focusSet.has(node.id) && node.id !== focusId : false;
        const dimmed = searchDimmed || focusDimmed;
        const selected = focusNodeId != null && node.id === focusNodeId;
        if (node.data.dimmed === dimmed && node.data.highlighted === highlighted && node.selected === selected) {
          return node;
        }
        return { ...node, selected, data: { ...node.data, dimmed, highlighted } };
      })
    );
  }, [matched, focusSet, focusId, focusNodeId, setNodes]);
  const parallelInfo = React9.useMemo(() => {
    const groups = /* @__PURE__ */ new Map();
    for (const edge of sourceEdges) {
      const key = edge.source === edge.target ? `self\0${edge.id}` : edge.source < edge.target ? `${edge.source}\0${edge.target}` : `${edge.target}\0${edge.source}`;
      const list = groups.get(key);
      if (list) list.push(edge.id);
      else groups.set(key, [edge.id]);
    }
    const info = /* @__PURE__ */ new Map();
    for (const ids of groups.values()) {
      ids.forEach((id, index) => info.set(id, { index, count: ids.length }));
    }
    return info;
  }, [sourceEdges]);
  React9.useEffect(() => {
    const decorated = sourceEdges.map((edge) => {
      let dimmed = false;
      let highlighted = false;
      if (focusSet && focusId) {
        const incident = edge.source === focusId || edge.target === focusId;
        highlighted = incident;
        dimmed = !incident;
      } else if (matched) {
        dimmed = !(matched.has(edge.source) && matched.has(edge.target));
      }
      const parallel = parallelInfo.get(edge.id);
      const sides = edgeSides[edge.id];
      return {
        ...edge,
        ...sides ? { sourceHandle: `source-${sides.source}`, targetHandle: `target-${sides.target}` } : { sourceHandle: void 0, targetHandle: void 0 },
        data: {
          ...edge.data,
          dimmed,
          highlighted,
          parallelIndex: parallel?.index,
          parallelCount: parallel?.count
        }
      };
    });
    setEdges(decorated);
  }, [sourceEdges, focusSet, focusId, matched, parallelInfo, edgeSides, setEdges]);
  const renderOptions = React9.useMemo(
    () => ({ particles: flowParticles, direction: layoutDirection }),
    [flowParticles, layoutDirection]
  );
  const handleNodeClick = React9.useCallback(
    (node) => {
      if (node.type === "chain-group") return;
      const clusterId = clusterIdFromNodeId(node.id);
      if (clusterId) clusters.toggle(clusterId);
      onNodeClick?.(node);
    },
    [clusters, onNodeClick]
  );
  const handleNodeDragStop = React9.useCallback(
    (_event, node) => {
      if (node.type === "chain-group") return;
      setDragOverrides((prev) => ({ ...prev, [node.id]: node.position }));
      onNodePositionChange?.(node.id, node.position);
    },
    [onNodePositionChange]
  );
  const handleNodeMouseEnter = React9.useCallback((_event, node) => {
    if (node.type !== "chain-group") setHoverId(node.id);
  }, []);
  const handleNodeMouseLeave = React9.useCallback(() => setHoverId(null), []);
  const containerRef = React9.useRef(null);
  const assignmentEnabled = assignment != null && (hasAssignmentActions(assignment.handlers) || assignment.renderEditor != null);
  const [menu, setMenu] = React9.useState(null);
  const openMenuAt = React9.useCallback((node, clientX, clientY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    setMenu({ node, x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) });
  }, []);
  const handleNodeContextMenu = React9.useCallback(
    (event, node) => {
      if (node.type === "chain-group") return;
      event.preventDefault();
      const data = node.data;
      openMenuAt(
        { id: node.id, label: data.label, address: data.address, chainId: data.chain, entityId: data.entityId },
        event.clientX,
        event.clientY
      );
    },
    [openMenuAt]
  );
  const assignMenuApi = React9.useMemo(
    () => ({ enabled: assignmentEnabled, open: openMenuAt }),
    [assignmentEnabled, openMenuAt]
  );
  const handleConnect = React9.useCallback(
    (connection) => {
      const { source, target } = connection;
      if (!source || !target || source === target) return;
      onConnect?.(source, target);
    },
    [onConnect]
  );
  React9.useEffect(() => {
    if (!focusNodeId) return void 0;
    const handle = window.setTimeout(() => {
      if (!reactFlow.getNode(focusNodeId)) return;
      reactFlow.fitView({ nodes: [{ id: focusNodeId }], duration: 400, padding: 0.6, maxZoom: 1.5 });
    }, 50);
    return () => window.clearTimeout(handle);
  }, [focusNodeId, reactFlow]);
  return /* @__PURE__ */ jsx(GraphRenderContext.Provider, { value: renderOptions, children: /* @__PURE__ */ jsx(GraphAssignmentMenuContext.Provider, { value: assignMenuApi, children: /* @__PURE__ */ jsxs("div", { ref: containerRef, className: cn("relative h-full w-full", className), children: [
    /* @__PURE__ */ jsxs(
      ReactFlow,
      {
        nodes: rfNodes,
        edges: rfEdges,
        nodeTypes,
        edgeTypes,
        onNodesChange,
        onEdgesChange,
        onNodeClick: (_event, node) => handleNodeClick(node),
        onEdgeClick: onEdgeClick ? (_event, edge) => onEdgeClick(edge) : void 0,
        nodesConnectable: Boolean(onConnect),
        onConnect: handleConnect,
        onNodeContextMenu: assignmentEnabled ? handleNodeContextMenu : void 0,
        onNodeDoubleClick: onNodeDoubleClick ? (_event, node) => {
          if (node.type !== "chain-group") onNodeDoubleClick(node);
        } : void 0,
        onNodeDragStop: handleNodeDragStop,
        onNodeMouseEnter: highlightNeighbors ? handleNodeMouseEnter : void 0,
        onNodeMouseLeave: highlightNeighbors ? handleNodeMouseLeave : void 0,
        onSelectionChange: onSelectionChange ? ({ nodes: selected }) => onSelectionChange(selected.map((n) => n.id)) : void 0,
        fitView,
        minZoom: 0.1,
        maxZoom: 2,
        children: [
          showBackground ? /* @__PURE__ */ jsx(Background, { gap: 16 }) : null,
          showControls ? /* @__PURE__ */ jsx(Controls, {}) : null,
          live ? /* @__PURE__ */ jsx(Panel, { position: "top-left", children: live }) : null,
          showMiniMap ? /* @__PURE__ */ jsx(
            MiniMap,
            {
              pannable: true,
              zoomable: true,
              nodeColor: (node) => {
                if (node.type === "chain-group") return "transparent";
                const variant = node.data.variant ?? "tx";
                return GRAPH_NODE_VARIANT_COLOR[variant];
              }
            }
          ) : null,
          legend && legend.length > 0 ? /* @__PURE__ */ jsx(Panel, { position: "top-right", children: /* @__PURE__ */ jsx(GraphLegend, { items: legend }) }) : null
        ]
      }
    ),
    assignment && menu ? /* @__PURE__ */ jsxs(Popover, { open: true, onOpenChange: (open) => !open && setMenu(null), children: [
      /* @__PURE__ */ jsx(PopoverAnchor, { asChild: true, children: /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": true,
          style: { position: "absolute", left: menu.x, top: menu.y, width: 0, height: 0 }
        }
      ) }),
      /* @__PURE__ */ jsx(PopoverContent, { align: "start", side: "right", className: "w-auto p-3", children: /* @__PURE__ */ jsx(
        NodeAssignMenu,
        {
          node: menu.node,
          state: assignment.getState?.(menu.node.id),
          handlers: assignment.handlers,
          entityOptions: assignment.entityOptions,
          editor: assignment.renderEditor?.(menu.node, () => setMenu(null)),
          onDone: () => setMenu(null)
        }
      ) })
    ] }) : null
  ] }) }) });
}
function TransactionGraph(props) {
  return /* @__PURE__ */ jsx(ReactFlowProvider, { children: /* @__PURE__ */ jsx(TransactionGraphInner, { ...props }) });
}

// src/engines/flow/edgeBundling.ts
function pairKey(a, b) {
  return a < b ? `${a}\0${b}` : `${b}\0${a}`;
}
function bundleParallelEdges(edges) {
  const groups = /* @__PURE__ */ new Map();
  const order = [];
  for (const edge of edges) {
    if (edge.source === edge.target) {
      const key2 = `self\0${edge.id}`;
      groups.set(key2, [edge]);
      order.push(key2);
      continue;
    }
    const key = pairKey(edge.source, edge.target);
    const list = groups.get(key);
    if (list) {
      list.push(edge);
    } else {
      groups.set(key, [edge]);
      order.push(key);
    }
  }
  const out = [];
  for (const key of order) {
    const list = groups.get(key);
    if (list.length === 1) {
      out.push(list[0]);
      continue;
    }
    const first = list[0];
    const orientations = new Set(list.map((e) => `${e.source}\0${e.target}`));
    const failed = list.some((e) => e.data?.failed === true);
    const animated = list.some((e) => e.data?.animated === true);
    let usd = 0;
    let hasUsd = false;
    for (const e of list) {
      if (e.data?.valueUsd != null) {
        usd += e.data.valueUsd;
        hasUsd = true;
      }
    }
    const variant = failed ? "failed" : list.find((e) => e.data?.variant && e.data.variant !== "default")?.data?.variant ?? first.data?.variant ?? "default";
    out.push(
      txEdge(`bundle:${first.source}->${first.target}:${list.length}`, first.source, first.target, {
        variant,
        failed,
        label: `\xD7${list.length}`,
        valueUsd: hasUsd ? usd : void 0,
        animated: animated && !failed,
        bidirectional: orientations.size > 1,
        aggregated: true,
        count: list.length,
        memberIds: list.map((edge) => edge.id)
      })
    );
  }
  return out;
}
var DIMMED_NODE_COLOR = "rgba(148, 163, 184, 0.35)";
var DIMMED_EDGE_COLOR = "rgba(148, 163, 184, 0.15)";
function ForceGraphUnavailable() {
  return /* @__PURE__ */ jsxs("div", { className: "flex h-full w-full items-center justify-center px-6 text-center text-sm text-muted-foreground", children: [
    "Force view needs the optional\xA0",
    /* @__PURE__ */ jsx("code", { className: "rounded bg-muted px-1 py-0.5", children: "react-force-graph-2d" }),
    "\xA0package."
  ] });
}
var ForceGraph2D = React9.lazy(async () => {
  try {
    const mod = await import('react-force-graph-2d');
    return { default: mod.default };
  } catch {
    return { default: ForceGraphUnavailable };
  }
});
var GROUP_PAD = 26;
function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
function ForceGraph({
  nodes,
  edges,
  legend,
  nodeRelSize = 6,
  className,
  loadingFallback,
  onNodeClick,
  groups,
  flowParticles = true,
  searchQuery
}) {
  const containerRef = React9.useRef(null);
  const [size, setSize] = React9.useState({ width: 0, height: 0 });
  const nodesRef = React9.useRef([]);
  React9.useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect) setSize({ width: rect.width, height: rect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const matched = React9.useMemo(
    () => searchQuery && searchQuery.trim() ? matchGraphNodes(nodes, searchQuery) : null,
    [nodes, searchQuery]
  );
  const graphData = React9.useMemo(() => {
    const forceNodes = nodes.map((node) => ({
      id: node.id,
      label: node.data.label,
      // `resolved` → concrete hsl() for the canvas (var() can't be used there).
      // Failed/reverted actors read in the critical-risk red.
      color: matched && !matched.has(node.id) ? DIMMED_NODE_COLOR : node.data.failed ? graphEdgeColor("failed", true) : graphNodeColor(node.data.variant ?? "tx", true),
      val: node.data.variant === "cluster" || node.data.variant === "entity" ? 4 : 2,
      ref: node
    }));
    const forceLinks = edges.map((edge) => {
      const failed = edge.data?.failed === true;
      const animated = flowParticles && edge.data?.animated === true && !failed;
      const dimmed = matched != null && !(matched.has(edge.source) && matched.has(edge.target));
      return {
        source: edge.source,
        target: edge.target,
        color: dimmed ? DIMMED_EDGE_COLOR : failed ? graphEdgeColor("failed", true) : graphEdgeColor(edge.data?.variant ?? "default", true),
        label: edge.data?.label,
        dashed: edge.data?.dashed,
        width: edgeStrokeWidth(edge.data),
        // 4 particles when value moves both ways, 2 one-way, 0 when static.
        particles: animated && !dimmed ? edge.data?.bidirectional ? 4 : 2 : 0
      };
    });
    return { nodes: forceNodes, links: forceLinks };
  }, [nodes, edges, flowParticles, matched]);
  nodesRef.current = graphData.nodes;
  const handleNodeClick = React9.useCallback(
    (node) => onNodeClick?.(node.ref),
    [onNodeClick]
  );
  const drawGroups = React9.useCallback(
    (ctx, globalScale) => {
      if (!groups || groups.length === 0) return;
      const byId = new Map(nodesRef.current.map((n) => [n.id, n]));
      for (const group of groups) {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        let count = 0;
        for (const memberId of group.memberNodeIds) {
          const node = byId.get(memberId);
          if (!node || node.x == null || node.y == null) continue;
          minX = Math.min(minX, node.x);
          minY = Math.min(minY, node.y);
          maxX = Math.max(maxX, node.x);
          maxY = Math.max(maxY, node.y);
          count += 1;
        }
        if (count === 0) continue;
        const x = minX - GROUP_PAD;
        const y = minY - GROUP_PAD;
        const w = maxX - minX + GROUP_PAD * 2;
        const h = maxY - minY + GROUP_PAD * 2;
        roundRect(ctx, x, y, w, h, 10);
        ctx.fillStyle = group.fill;
        ctx.fill();
        ctx.lineWidth = 1.5 / globalScale;
        ctx.strokeStyle = group.border;
        ctx.setLineDash([6 / globalScale, 4 / globalScale]);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = group.text;
        ctx.font = `${11 / globalScale}px ui-sans-serif, system-ui, sans-serif`;
        ctx.textBaseline = "bottom";
        ctx.fillText(group.label, x + 6 / globalScale, y - 2 / globalScale);
      }
    },
    [groups]
  );
  return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: cn("relative h-full w-full", className), children: [
    /* @__PURE__ */ jsx(
      React9.Suspense,
      {
        fallback: loadingFallback ?? /* @__PURE__ */ jsx("div", { className: "flex h-full w-full items-center justify-center text-sm text-muted-foreground", children: "Loading force graph\u2026" }),
        children: size.width > 0 && size.height > 0 ? /* @__PURE__ */ jsx(
          ForceGraph2D,
          {
            graphData,
            nodeId: "id",
            nodeLabel: "label",
            nodeVal: "val",
            nodeRelSize,
            nodeColor: (node) => node.color,
            linkColor: (link) => link.color,
            linkLabel: (link) => link.label ?? "",
            linkLineDash: (link) => link.dashed ? [4, 4] : null,
            linkWidth: (link) => link.width,
            linkDirectionalArrowLength: 4,
            linkDirectionalArrowRelPos: 1,
            linkDirectionalParticles: (link) => link.particles,
            linkDirectionalParticleWidth: (link) => Math.max(2, link.width),
            linkDirectionalParticleSpeed: 6e-3,
            width: size.width,
            height: size.height,
            backgroundColor: "rgba(0,0,0,0)",
            onRenderFramePre: groups && groups.length > 0 ? drawGroups : void 0,
            onNodeClick: handleNodeClick
          }
        ) : null
      }
    ),
    legend && legend.length > 0 ? /* @__PURE__ */ jsx("div", { className: "absolute right-2 top-2", children: /* @__PURE__ */ jsx(GraphLegend, { items: legend }) }) : null
  ] });
}
var TOOLBAR_TOOLTIP_DELAY_MS = 200;
function ToolbarButton({
  active,
  label,
  hint,
  shape = "icon",
  disabled,
  onClick,
  children
}) {
  return /* @__PURE__ */ jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: active ? "secondary" : "outline",
        size: "sm",
        "aria-pressed": active,
        "aria-label": label,
        disabled,
        onClick,
        className: cn(
          shape === "icon" ? "h-7 w-7 p-0" : "h-7 px-2 text-xs",
          active && "ring-1 ring-brand/50"
        ),
        children
      }
    ) }),
    /* @__PURE__ */ jsx(TooltipContent, { side: "bottom", children: hint ?? label })
  ] });
}
function GraphToolbar({ fullscreen, bundle, particles, relayout, extra, className }) {
  const hasBuiltins = Boolean(fullscreen || bundle || particles || relayout);
  if (!hasBuiltins && !extra) return null;
  return /* @__PURE__ */ jsx(TooltipProvider, { delayDuration: TOOLBAR_TOOLTIP_DELAY_MS, children: /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-1 rounded-md bg-card/80 p-1 shadow-sm backdrop-blur", className), children: [
    relayout ? /* @__PURE__ */ jsx(
      ToolbarButton,
      {
        label: "Tidy layout",
        hint: "Arrange every node again, discarding manual moves",
        onClick: relayout.onClick,
        children: /* @__PURE__ */ jsx(TidyIcon, { size: 14 })
      }
    ) : null,
    particles ? /* @__PURE__ */ jsx(ToolbarButton, { active: particles.active, label: "Animate value flow", onClick: particles.onToggle, children: /* @__PURE__ */ jsx(FlowIcon, { size: 14 }) }) : null,
    bundle ? /* @__PURE__ */ jsx(ToolbarButton, { active: bundle.active, label: "Bundle parallel edges", onClick: bundle.onToggle, children: /* @__PURE__ */ jsx(BundleIcon, { size: 14 }) }) : null,
    fullscreen ? /* @__PURE__ */ jsx(ToolbarButton, { active: fullscreen.active, label: fullscreen.active ? "Exit fullscreen" : "Fullscreen", onClick: fullscreen.onToggle, children: fullscreen.active ? /* @__PURE__ */ jsx(MinimizeIcon, { size: 14 }) : /* @__PURE__ */ jsx(MaximizeIcon, { size: 14 }) }) : null,
    extra ? /* @__PURE__ */ jsxs(Fragment, { children: [
      hasBuiltins ? /* @__PURE__ */ jsx("span", { className: "mx-0.5 h-4 w-px bg-border", "aria-hidden": true }) : null,
      extra
    ] }) : null
  ] }) });
}
function useFullscreen(ref) {
  const supported = typeof document !== "undefined" && Boolean(document.fullscreenEnabled);
  const [isFullscreen, setIsFullscreen] = React9.useState(false);
  React9.useEffect(() => {
    if (typeof document === "undefined") return void 0;
    const onChange = () => setIsFullscreen(document.fullscreenElement === ref.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [ref]);
  const enter = React9.useCallback(() => {
    const el = ref.current;
    if (el && document.fullscreenElement !== el) void el.requestFullscreen?.().catch(() => void 0);
  }, [ref]);
  const exit = React9.useCallback(() => {
    if (typeof document !== "undefined" && document.fullscreenElement) {
      void document.exitFullscreen?.().catch(() => void 0);
    }
  }, []);
  const toggle = React9.useCallback(() => {
    if (typeof document === "undefined") return;
    if (document.fullscreenElement === ref.current) exit();
    else enter();
  }, [ref, enter, exit]);
  return { isFullscreen, toggle, enter, exit, supported };
}
function GraphViewModeToggle({
  value,
  onChange,
  modes = GRAPH_VIEW_MODES,
  size = "sm",
  className
}) {
  const options = React9.useMemo(
    () => modes.map((mode) => ({ value: mode, label: GRAPH_VIEW_MODE_LABELS[mode] })),
    [modes]
  );
  return /* @__PURE__ */ jsx(
    ViewModeToggle,
    {
      "aria-label": "Graph view mode",
      options,
      value,
      onChange,
      size,
      className
    }
  );
}
function GraphCanvas({
  viewMode = "hierarchical",
  onViewModeChange,
  showViewModeToggle = false,
  viewModes,
  engine,
  loadingFallback,
  showFullscreen = false,
  showEdgeBundleToggle = false,
  bundleParallelEdges: bundleInitial = false,
  showFlowToggle = false,
  showRelayout = false,
  extraToolbar,
  containerRef: containerRefProp,
  ...graphProps
}) {
  const [layoutNonce, setLayoutNonce] = React9.useState(0);
  const controlled = onViewModeChange != null;
  const [internalMode, setInternalMode] = React9.useState(viewMode);
  const mode = controlled ? viewMode : internalMode;
  const setMode = React9.useCallback(
    (next) => {
      if (!controlled) setInternalMode(next);
      onViewModeChange?.(next);
    },
    [controlled, onViewModeChange]
  );
  const [bundle, setBundle] = React9.useState(bundleInitial);
  const [particles, setParticles] = React9.useState(graphProps.flowParticles ?? true);
  const ownContainerRef = React9.useRef(null);
  const containerRef = containerRefProp ?? ownContainerRef;
  const fullscreen = useFullscreen(containerRef);
  const resolvedEngine = engine ?? engineForView(mode);
  const edges = React9.useMemo(
    () => bundle ? bundleParallelEdges(graphProps.edges) : graphProps.edges,
    [bundle, graphProps.edges]
  );
  const canvas = resolvedEngine === "force" ? /* @__PURE__ */ jsx(
    ForceGraph,
    {
      nodes: graphProps.nodes,
      edges,
      legend: graphProps.legend,
      className: graphProps.className,
      loadingFallback,
      onNodeClick: graphProps.onNodeClick,
      groups: graphProps.chainGroups,
      flowParticles: particles,
      searchQuery: graphProps.searchQuery
    }
  ) : /* @__PURE__ */ jsx(
    TransactionGraph,
    {
      ...graphProps,
      edges,
      viewMode: mode,
      flowParticles: particles,
      layoutNonce
    }
  );
  const canTidy = showRelayout && resolvedEngine !== "force" && graphProps.layout !== false;
  const toolbar = /* @__PURE__ */ jsx(
    GraphToolbar,
    {
      fullscreen: showFullscreen && fullscreen.supported ? { active: fullscreen.isFullscreen, onToggle: fullscreen.toggle } : void 0,
      bundle: showEdgeBundleToggle ? { active: bundle, onToggle: () => setBundle((b) => !b) } : void 0,
      particles: showFlowToggle ? { active: particles, onToggle: () => setParticles((p) => !p) } : void 0,
      relayout: canTidy ? { onClick: () => setLayoutNonce((n) => n + 1) } : void 0,
      extra: extraToolbar
    }
  );
  const hasToolbar = showFullscreen || showEdgeBundleToggle || showFlowToggle || canTidy || extraToolbar != null;
  if (!showViewModeToggle && !hasToolbar) return canvas;
  return /* @__PURE__ */ jsxs("div", { ref: containerRef, className: "relative h-full w-full bg-background", children: [
    showViewModeToggle ? /* @__PURE__ */ jsx("div", { className: "absolute left-2 top-2 z-10", children: /* @__PURE__ */ jsx(GraphViewModeToggle, { value: mode, onChange: setMode, modes: viewModes, size: "sm" }) }) : null,
    hasToolbar ? /* @__PURE__ */ jsx("div", { className: "absolute right-2 top-2 z-10", children: toolbar }) : null,
    canvas
  ] });
}
var COPY = {
  no_semantic_tree: {
    title: "Nothing decoded",
    description: "No protocol was recognised in this transaction, so there is no story to draw. The raw call and logs are still available on the transaction detail."
  },
  decluttered_empty: {
    title: "Nothing left to show",
    description: "Everything in this transaction was classified as plumbing by its protocol's scene settings. Raise the detail level to see it."
  },
  no_flow_source: {
    title: "No value flow to draw",
    description: "This view is built from token movements and none were available for this transaction. The semantic graph may still describe what happened."
  },
  no_value_moved: {
    title: "No tokens moved",
    description: "This transaction was read successfully and moved no value \u2014 a message, an approval or a contract call. The semantic graph shows what it did instead."
  }
};
var FALLBACK = {
  title: "Nothing to show",
  description: "This graph came back empty."
};
function GraphEmptyState({
  reason,
  className,
  size = "md"
}) {
  const copy = reason && COPY[reason] || FALLBACK;
  return /* @__PURE__ */ jsx(
    EmptyState,
    {
      title: copy.title,
      description: copy.description,
      className,
      size
    }
  );
}
var OFFCHAIN_CHAIN_ID = "offchain";
var CHAIN_LABEL_ATTR = "chain_label";
var SAGA_KEY_ATTR = "saga_key";
var SAGA_LABEL_ATTR = "saga_label";
var SAGA_AWAITING_ATTR = "saga_awaiting";
var PROTOCOL_COLOR = "#8b5cf6";
var ENTITY_COLOR = "#14b8a6";
var TAG_COLOR = "#f97316";
var LABEL_COLOR = "#64748b";
var KIND_COLOR = "#64748b";
var SAGA_COLOR = "#0ea5e9";
var SAGA_AWAITING_COLOR = "#f59e0b";
function shortCorrelation(key) {
  const value = key.includes(":") ? key.slice(key.lastIndexOf(":") + 1) : key;
  return value.length > 14 ? `${value.slice(0, 8)}\u2026${value.slice(-4)}` : value;
}
function firstTag(node, overlay) {
  const backend = node.attributes?.tags;
  if (Array.isArray(backend)) {
    for (const tag of backend) {
      const value = asTrimmedString(tag);
      if (value) return value;
    }
  }
  for (const tag of overlay?.tags ?? []) {
    const value = asTrimmedString(tag);
    if (value) return value;
  }
  return void 0;
}
function overlayFor(node, ctx) {
  const address = node.address ?? void 0;
  return address ? ctx.resolveOverlay?.(address, node.chain_id ?? void 0) : void 0;
}
var CHAIN = {
  id: "chain",
  label: "Blockchain",
  groupType: "chain",
  // A chain band of one is the chain's name — worth drawing on its own.
  minMembers: 1,
  resolve(node) {
    const chainId = node.chain_id;
    if (!chainId || chainId === OFFCHAIN_CHAIN_ID || node.off_chain) return null;
    return {
      key: chainId,
      // Defaults to the id: `toSceneView` resolves the human name from the FE
      // chain registry, and a member's hint covers the ids it can't name.
      label: asTrimmedString(node.attributes?.[CHAIN_LABEL_ATTR]) ?? chainId,
      chainId
    };
  }
};
var PROTOCOL = {
  id: "protocol",
  label: "Protocol",
  groupType: "protocol",
  minMembers: 2,
  resolve(node) {
    const protocol = asTrimmedString(node.attributes?.protocol);
    if (!protocol) return null;
    return { key: protocol.toLowerCase(), label: protocol, color: PROTOCOL_COLOR };
  }
};
var ENTITY = {
  id: "entity",
  label: "Entity",
  groupType: "cluster",
  minMembers: 2,
  resolve(node, ctx) {
    const overlay = overlayFor(node, ctx);
    const entityId = asTrimmedString(node.entity_id) ?? overlay?.primaryEntityId;
    if (!entityId) return null;
    return {
      key: entityId.toLowerCase(),
      // The host's resolver first (it may know entities this graph never drew),
      // then the name the overlay already resolved for this very node, and only
      // then the id. The middle step was missing, and since nothing supplies
      // `entityLabel`, every band was titled with a hash — the id is a fallback
      // for a band we genuinely cannot name, not the normal case.
      label: ctx.entityLabel?.(entityId) ?? overlay?.primaryEntityName ?? entityId,
      color: ENTITY_COLOR
    };
  }
};
var TAG = {
  id: "tag",
  label: "Tag",
  groupType: "tag",
  minMembers: 2,
  resolve(node, ctx) {
    const tag = firstTag(node, overlayFor(node, ctx));
    if (!tag) return null;
    return { key: tag.toLowerCase(), label: tag, color: TAG_COLOR };
  }
};
var LABEL = {
  id: "label",
  label: "Catalog label",
  groupType: "label",
  minMembers: 2,
  resolve(node, ctx) {
    const label = overlayFor(node, ctx)?.label;
    if (!label) return null;
    return { key: label.toLowerCase(), label, color: LABEL_COLOR };
  }
};
var KIND = {
  id: "kind",
  label: "Node type",
  groupType: "kind",
  minMembers: 2,
  /**
   * Resolved through the node-kind registry, which is the same thing the CARD
   * reads for its variant and badge — so a band and the nodes inside it agree
   * about what they are.
   *
   * It used to title itself by prettifying the raw wire string, and that is two
   * bugs rather than one. The label was wrong (`don` → "Don", `eoa` → "Eoa",
   * `defi` → "Defi", where the registry says "Oracle network", "Wallet", "DeFi
   * protocol"), and the KEY was the raw string — so `address`, `wallet` and
   * `eoa` drew three separate bands for one kind, as did `cex` and `exchange`.
   * Grouping by a vocabulary nobody had normalised is grouping by spelling.
   */
  resolve(node) {
    const raw = asTrimmedString(node.kind);
    if (!raw) return null;
    const offChain = node.off_chain === true;
    const kind = domainKindForSceneKind(raw, { offChain });
    return {
      key: kind ?? raw.toLowerCase(),
      label: nodeKindLabel(raw, { offChain }) ?? raw,
      color: KIND_COLOR
    };
  }
};
var SAGA = {
  id: "saga",
  label: "Cross-chain transfer",
  groupType: "saga",
  minMembers: 2,
  resolve(node) {
    const key = asTrimmedString(node.attributes?.[SAGA_KEY_ATTR]);
    if (!key) return null;
    const name = asTrimmedString(node.attributes?.[SAGA_LABEL_ATTR]);
    return {
      key: key.toLowerCase(),
      // The correlation id is what makes one transfer distinguishable from the
      // next: a case with ten CCIP messages drew ten bands titled "Chainlink
      // CCIP" before this, which reads as one grouping gone wrong.
      label: name ? `${name} ${shortCorrelation(key)}` : shortCorrelation(key),
      color: node.attributes?.[SAGA_AWAITING_ATTR] ? SAGA_AWAITING_COLOR : SAGA_COLOR
    };
  }
};
var DIMENSIONS = [CHAIN, PROTOCOL, ENTITY, TAG, LABEL, KIND, SAGA];
var GROUPING_DIMENSION_LIST = DIMENSIONS;
var BY_ID = new Map(DIMENSIONS.map((d) => [d.id, d]));
function groupingDimension(id) {
  return BY_ID.get(id);
}
function groupingDimensionLabel(id) {
  return BY_ID.get(id)?.label ?? id;
}

// src/grouping/applyGrouping.ts
var DEFAULT_MAX_BANDS = 12;
var MAX_TOTAL_BANDS = 48;
function bandId(parentId, dimension, key) {
  const own = `${dimension.id}:${key}`;
  return parentId ? `${parentId}/${own}` : own;
}
function resolveDimensions(spec) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const id of spec.dimensions ?? []) {
    if (seen.has(id)) continue;
    seen.add(id);
    const dimension = groupingDimension(id);
    if (dimension) out.push(dimension);
  }
  return out;
}
function applyCaps(bands, spec) {
  const dropped = /* @__PURE__ */ new Set();
  const byDimension = /* @__PURE__ */ new Map();
  for (const band of bands) {
    const bucket = byDimension.get(band.dimension.id);
    if (bucket) bucket.push(band);
    else byDimension.set(band.dimension.id, [band]);
  }
  for (const [id, bucket] of byDimension) {
    const options = spec.options?.[id];
    const minMembers = options?.min_members ?? groupingDimension(id)?.minMembers ?? 1;
    const maxBands = options?.max_bands ?? DEFAULT_MAX_BANDS;
    const kept = bucket.filter((band) => {
      if (band.members.length >= minMembers) return true;
      dropped.add(band.id);
      return false;
    }).sort((a, b) => b.members.length - a.members.length || a.seq - b.seq);
    for (const band of kept.slice(maxBands)) dropped.add(band.id);
  }
  const survives = (band) => {
    if (dropped.has(band.id)) return false;
    if (!band.parentId) return true;
    return !dropped.has(band.parentId);
  };
  const out = [];
  for (const band of bands.sort((a, b) => a.level - b.level || a.seq - b.seq)) {
    if (survives(band)) out.push(band);
    else dropped.add(band.id);
  }
  return out.slice(0, MAX_TOTAL_BANDS);
}
function uniqueLabels(bands) {
  const used = /* @__PURE__ */ new Map();
  const labels = /* @__PURE__ */ new Map();
  for (const band of bands) {
    const scope = `${band.parentId ?? ""}\0${band.value.label.toLowerCase()}`;
    const seen = (used.get(scope) ?? 0) + 1;
    used.set(scope, seen);
    labels.set(band.id, seen === 1 ? band.value.label : `${band.value.label} (${seen})`);
  }
  return labels;
}
function applyGrouping(scene, spec, options = {}) {
  const dimensions = resolveDimensions(spec);
  if (dimensions.length === 0) return scene;
  const bands = /* @__PURE__ */ new Map();
  const paths = /* @__PURE__ */ new Map();
  let seq = 0;
  for (const node of scene.nodes) {
    const path = [];
    let parentId = null;
    for (let level = 0; level < dimensions.length; level++) {
      const dimension = dimensions[level];
      const value = dimension.resolve(node, options);
      if (!value) {
        if (path.length === 0) continue;
        break;
      }
      const id = bandId(parentId, dimension, value.key);
      let band = bands.get(id);
      if (!band) {
        band = { id, parentId, dimension, level, value, members: [], seq: seq++ };
        bands.set(id, band);
      } else if (!band.value.color && value.color) {
        band.value = { ...band.value, color: value.color };
      }
      band.members.push(node.id);
      path.push(id);
      parentId = id;
    }
    if (path.length > 0) paths.set(node.id, path);
  }
  const kept = applyCaps([...bands.values()], spec);
  const keptIds = new Set(kept.map((band) => band.id));
  const labels = uniqueLabels(kept);
  const groups = kept.map((band) => ({
    id: band.id,
    group_type: band.dimension.groupType,
    chain_id: band.value.chainId ?? null,
    label: labels.get(band.id) ?? band.value.label,
    member_node_ids: band.members,
    color: band.value.color ?? null,
    icon: null
  }));
  const nodes = scene.nodes.map((node) => {
    const path = paths.get(node.id);
    let groupId = null;
    for (const id of path ?? []) {
      if (keptIds.has(id)) groupId = id;
    }
    return node.group_id === groupId ? node : { ...node, group_id: groupId };
  });
  return { ...scene, nodes, groups };
}
function applicableDimensions(scene, options = {}) {
  const out = /* @__PURE__ */ new Set();
  for (const dimension of GROUPING_DIMENSION_LIST) {
    const counts = /* @__PURE__ */ new Map();
    for (const node of scene.nodes) {
      const value = dimension.resolve(node, options);
      if (!value) continue;
      counts.set(value.key, (counts.get(value.key) ?? 0) + 1);
    }
    const min = dimension.minMembers ?? 1;
    for (const count of counts.values()) {
      if (count >= min) {
        out.add(dimension.id);
        break;
      }
    }
  }
  return out;
}

// src/edgeKinds.ts
var EDGE_KIND_TO_VARIANT = {
  // Value movement.
  transfer: "transfer",
  erc20: "transfer",
  native: "transfer",
  // Calls / verification steps.
  call: "call",
  internal: "call",
  verify: "call",
  // Structural relations (parent → child). `creates` shares the variant because
  // it is the same shape of claim — a factory produced this pool, a pool is made
  // of this token — and deliberately not a value-movement variant: nothing moved.
  contains: "inheritance",
  creates: "inheritance",
  // Cross-chain / bridge legs.
  cross_chain: "bridge",
  bridge: "bridge",
  bridge_in: "bridge",
  bridge_out: "bridge",
  // Generic.
  flow: "flow"
};
function edgeVariantForKind(kind) {
  return kind && EDGE_KIND_TO_VARIANT[kind] || "flow";
}
var FLOW_EDGE_KINDS = /* @__PURE__ */ new Set([
  "transfer",
  "erc20",
  "native",
  "flow",
  "cross_chain",
  "bridge",
  "bridge_in",
  "bridge_out"
]);
function isFlowEdgeKind(kind) {
  return kind != null && FLOW_EDGE_KINDS.has(kind);
}

// src/transpose/toScene.ts
var AUTHORED_ACCENT_CLASS = "border-amber-500/70";
var AUTHORED_BADGE = "Added";
function toNode(view, brandAccent) {
  const authored = view.origin === "user";
  const brandClass = view.offChain && view.protocol && brandAccent ? brandAccent(view.protocol) : void 0;
  const accentClassName = brandClass ?? (authored ? AUTHORED_ACCENT_CLASS : void 0);
  return txNode(view.id, {
    label: view.label,
    subtitle: view.sublabel,
    chain: view.chainId,
    icon: view.icon?.url,
    iconAlt: view.icon?.alt,
    badge: authored ? AUTHORED_BADGE : view.badge,
    value: view.value,
    confidence: view.confidence,
    tags: view.tags,
    // `GraphNode` has always rendered a `RiskBadge` from these two; until the
    // catalog overlay carried a severity there was nothing to put in them, so a
    // node labelled "Fake_Phishing7575952" drew the same neutral card as a
    // router. See `@authe/fe-commons` `overlays/risk`.
    riskLevel: view.riskLevel,
    riskScore: view.riskScore,
    failed: view.failed,
    virtual: view.virtual,
    // Hover detail, mirroring the edges: the SSOT facts placed on this node plus
    // its provenance.
    facts: view.facts,
    // The whole token list, not just the representative one `value` names — see
    // `SceneNodeView.assets`.
    assets: view.assets,
    sourceRefs: view.sourceRefs,
    origin: view.origin,
    contested: view.contested,
    address: view.address,
    entityId: view.entityId,
    kind: domainKindForSceneKind(view.kind, { offChain: view.offChain }),
    protocol: view.protocol,
    accentClassName: accentClassName ?? void 0
  });
}
function toEdge(view) {
  return txEdge(view.id, view.from, view.to, {
    // A netted edge is the residue of several movements; the tooltip says so.
    netted: view.netted,
    netZero: view.netZero,
    contested: view.contested,
    // Which intermediaries were netted away to leave this edge — the tooltip
    // reads "N intermediaries (netted out)" from it.
    compressedVia: view.compressedVia,
    // A case feed's merge folds every repeat movement between the same pair onto
    // ONE arrow, and it has to say so: three transfers in the table beside one
    // arrow reads as a missing arrow. Fed through the existing "this edge stands
    // for N" channel rather than a second vocabulary, so it thickens the stroke
    // and lands in the tooltip the way a bundled edge already does.
    aggregated: (view.mergedCount ?? 1) > 1 ? true : void 0,
    count: (view.mergedCount ?? 1) > 1 ? view.mergedCount : void 0,
    // Failed/reverted calls override the kind colour with the critical-risk red;
    // an analyst's own edge takes the authored variant, so it can't be read as
    // an observed flow. Failure wins over provenance — an authored edge marked
    // failed is still, first, a failure.
    variant: view.failed ? "failed" : view.origin === "user" ? "authored" : edgeVariantForKind(view.kind),
    failed: view.failed,
    label: view.label,
    // Hover detail: the SSOT facts placed on this edge + the context needed to
    // read them (what kind of hop, whether it was observed, which chains).
    facts: view.facts,
    origin: view.origin,
    kind: view.kind,
    fromChain: view.fromChain,
    toChain: view.toChain,
    amount: view.amount,
    ticker: view.symbol,
    valueUsd: view.valueUsd,
    basis: view.basis,
    minAmount: view.minAmount,
    tokenLogo: view.icon?.url,
    // Value-carrying flows animate (a particle travels the edge); a failed or
    // virtual hop stays static. Bidirectional pairs animate a particle each way.
    animated: !view.failed && isFlowEdgeKind(view.kind),
    bidirectional: view.bidirectional,
    // Synthesised cross-chain hops (e.g. a CCIP bridge-destination leg) render
    // dashed so analysts can tell them from observed on-chain flows.
    dashed: view.dashed
  });
}
function sceneViewToElements(view, options = {}) {
  return {
    nodes: view.nodes.map((n) => toNode(n, options.brandAccent)),
    edges: view.edges.map(toEdge),
    groups: view.groups,
    layoutHint: view.layoutHint
  };
}
function toScene(scene, options = {}) {
  return sceneViewToElements(toSceneView(scene, options), { brandAccent: options.brandAccent });
}

// src/export/exportGraphData.ts
function graphExportElements(graph, options = {}) {
  if (!graph) return { nodes: [], edges: [] };
  const scene = graph.scene;
  if (scene) {
    const elements = toScene(scene, options);
    return { nodes: elements.nodes, edges: elements.edges };
  }
  return {
    nodes: Array.isArray(graph.nodes) ? [...graph.nodes] : [],
    edges: Array.isArray(graph.edges) ? [...graph.edges] : []
  };
}
var NODE_COLUMNS = [
  { key: "id", header: "node_id" },
  { key: "label", header: "label", value: (n) => n.data.label },
  { key: "subtitle", header: "subtitle", value: (n) => n.data.subtitle ?? "" },
  { key: "chain", header: "chain", value: (n) => n.data.chain ?? "" },
  { key: "variant", header: "variant", value: (n) => n.data.variant ?? "" },
  { key: "badge", header: "badge", value: (n) => n.data.badge ?? "" }
];
var EDGE_COLUMNS = [
  { key: "id", header: "edge_id" },
  { key: "source", header: "source" },
  { key: "target", header: "target" },
  { key: "label", header: "label", value: (e) => e.data?.label ?? "" },
  { key: "variant", header: "variant", value: (e) => e.data?.variant ?? "" },
  { key: "timestamp", header: "timestamp", value: (e) => e.data?.timestamp ?? "" }
];
function exportGraphNodes(nodes, opts = {}) {
  return exportDataset({
    rows: nodes,
    columns: NODE_COLUMNS,
    format: opts.format ?? "csv",
    filenameBase: opts.filenameBase ?? "graph-nodes",
    download: opts.download
  });
}
function exportGraphEdges(edges, opts = {}) {
  return exportDataset({
    rows: edges,
    columns: EDGE_COLUMNS,
    format: opts.format ?? "csv",
    filenameBase: opts.filenameBase ?? "graph-edges",
    download: opts.download
  });
}
async function exportGraphData(graph, opts = {}) {
  const elements = graphExportElements(graph);
  const nodes = await exportGraphNodes(elements.nodes, opts);
  const edges = await exportGraphEdges(elements.edges, opts);
  return { nodes, edges };
}
function resolveTarget(container) {
  return container.querySelector(".react-flow__viewport") ?? container;
}
function applyWatermark(dataUrl, text2, background) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0);
      const pad = Math.round(canvas.width * 0.012) + 8;
      const fontSize = Math.max(12, Math.round(canvas.width * 0.014));
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = "right";
      ctx.textBaseline = "bottom";
      ctx.fillStyle = "rgba(100, 116, 139, 0.65)";
      ctx.fillText(text2, canvas.width - pad, canvas.height - pad);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => reject(new Error("Failed to load graph image for watermarking"));
    image.src = dataUrl;
  });
}
function triggerDownload(dataUrl, fileName) {
  const link = document.createElement("a");
  link.download = fileName.endsWith(".png") ? fileName : `${fileName}.png`;
  link.href = dataUrl;
  link.click();
}
async function exportGraphPng(container, options = {}) {
  const { fileName, backgroundColor = "#ffffff", pixelRatio = 2, watermark } = options;
  const target = resolveTarget(container);
  let dataUrl = await toPng(target, { backgroundColor, pixelRatio, cacheBust: true });
  if (watermark) {
    dataUrl = await applyWatermark(dataUrl, watermark, backgroundColor);
  }
  if (fileName) {
    triggerDownload(dataUrl, fileName);
  }
  return dataUrl;
}
var KIND_LABELS = {
  png: "Image (PNG)",
  nodes: "Nodes (CSV)",
  edges: "Edges (CSV)",
  json: "Graph snapshot (JSON)"
};
function GraphExportMenu({
  graph,
  containerRef,
  filenameBase = "graph",
  watermark,
  exportContext,
  label = "Export",
  buttonVariant = "outline",
  buttonSize = "sm",
  disabled
}) {
  const ledger = useExportLedger();
  const [busy, setBusy] = React9.useState(false);
  const { nodes, edges } = React9.useMemo(() => graphExportElements(graph), [graph]);
  const record = React9.useCallback(
    async (resource, filename, rowCount, columns, error) => {
      if (!ledger) return;
      await ledger.record({
        ...exportContext,
        resource,
        format: resource === "graph-image" ? "json" : "csv",
        filename,
        rowCount,
        columns,
        status: error ? ExportLedgerStatus.FAILED : ExportLedgerStatus.RECORDED,
        error
      }).catch(() => null);
    },
    [ledger, exportContext]
  );
  const run = React9.useCallback(
    async (kind) => {
      setBusy(true);
      try {
        if (kind === "png") {
          const container = containerRef?.current;
          if (!container) throw new Error("the graph canvas is not mounted yet");
          const filename = buildExportFilename(filenameBase, "json", { timestamp: true }).replace(
            /\.json$/,
            ".png"
          );
          await exportGraphPng(container, {
            fileName: filename,
            ...watermark ? { watermark } : {},
            pixelRatio: 2
          });
          await record("graph-image", filename, nodes.length, []);
          return;
        }
        if (kind === "json") {
          const filename = buildExportFilename(filenameBase, "json", { timestamp: true });
          const blob = new Blob([JSON.stringify(graph ?? {}, null, 2)], {
            type: "application/json"
          });
          downloadBlob(blob, filename);
          await record("graph-snapshot", filename, nodes.length + edges.length, ["nodes", "edges"]);
          return;
        }
        const isNodes = kind === "nodes";
        const result = isNodes ? await exportGraphNodes([...nodes], { filenameBase: `${filenameBase}-nodes` }) : await exportGraphEdges([...edges], { filenameBase: `${filenameBase}-edges` });
        await record(
          isNodes ? "graph-nodes" : "graph-edges",
          result.filename,
          result.rowCount,
          result.columns
        );
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        notifyError(error, { title: `Couldn't export the graph as ${KIND_LABELS[kind]}.` });
        await record(`graph-${kind}`, "", 0, [], error.message);
      } finally {
        setBusy(false);
      }
    },
    [containerRef, filenameBase, watermark, graph, nodes, edges, record]
  );
  const kinds = [
    ...containerRef ? ["png"] : [],
    "nodes",
    "edges",
    "json"
  ];
  const empty = nodes.length === 0 && edges.length === 0;
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      Button,
      {
        variant: buttonVariant,
        size: buttonSize,
        disabled: disabled || busy || empty,
        title: empty ? "Apply or import a graph to export it" : "Export this graph",
        children: busy ? "Exporting\u2026" : label
      }
    ) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", children: [
      /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Download as" }),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      kinds.map((kind) => /* @__PURE__ */ jsx(DropdownMenuItem, { disabled: busy, onSelect: () => void run(kind), children: KIND_LABELS[kind] }, kind))
    ] })
  ] });
}
function downloadBlob(blob, filename) {
  if (typeof document === "undefined" || typeof URL?.createObjectURL !== "function") return;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1e4);
}

// src/share/catalogSnapshot.ts
function overlayKey(address, chainId) {
  return `${chainId ?? ""}|${address.toLowerCase()}`;
}
function addressOf(node) {
  const candidate = node.address ?? node.attributes?.address;
  return typeof candidate === "string" && candidate ? candidate : void 0;
}
function freezeCatalogSnapshot(scene, resolve) {
  if (!scene || !resolve) return null;
  const frozen = {};
  for (const node of scene.nodes) {
    const address = addressOf(node);
    if (!address) continue;
    const chainId = typeof node.chain_id === "string" ? node.chain_id : void 0;
    const key = overlayKey(address, chainId);
    if (key in frozen) continue;
    const overlay = resolve(address, chainId);
    if (overlay) frozen[key] = overlay;
  }
  return Object.keys(frozen).length ? frozen : null;
}
function catalogResolverFrom(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return void 0;
  const entries = snapshot;
  if (!Object.keys(entries).length) return void 0;
  return (address, chainId) => entries[overlayKey(address, chainId)] ?? entries[overlayKey(address)];
}
function effectiveVariant(node) {
  return node.data.variant ?? variantForKind(node.data.kind);
}
function computeGraphStats(nodes, edges) {
  const byVariant = /* @__PURE__ */ new Map();
  const byChain = /* @__PURE__ */ new Map();
  let offChainCount = 0;
  for (const node of nodes) {
    const variant = effectiveVariant(node);
    byVariant.set(variant, (byVariant.get(variant) ?? 0) + 1);
    const chain = node.data.chain;
    if (typeof chain === "string" && chain) byChain.set(chain, (byChain.get(chain) ?? 0) + 1);
    if (isOffChainKind(node.data.kind)) offChainCount += 1;
  }
  return {
    nodeCount: nodes.length,
    edgeCount: edges.length,
    byVariant: rankedCounts(byVariant),
    byChain: rankedCounts(byChain),
    offChainCount
  };
}
function StatBlock({ value, label }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col", children: [
    /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold tabular-nums text-foreground", children: value.toLocaleString() }),
    /* @__PURE__ */ jsx("span", { className: "text-[10px] uppercase tracking-wide text-muted-foreground", children: label })
  ] });
}
function CountRow({
  swatch,
  logo,
  label,
  count
}) {
  return /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-2 text-muted-foreground", children: [
    logo ? /* @__PURE__ */ jsx("img", { src: logo, alt: "", "aria-hidden": true, className: "h-3 w-3 shrink-0 rounded-full object-cover" }) : swatch ? /* @__PURE__ */ jsx("span", { className: "h-2.5 w-2.5 shrink-0 rounded-full", style: { backgroundColor: swatch } }) : /* @__PURE__ */ jsx("span", { className: "h-2.5 w-2.5 shrink-0" }),
    /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1 truncate text-foreground", children: label }),
    /* @__PURE__ */ jsx("span", { className: "tabular-nums", children: count.toLocaleString() })
  ] });
}
function GraphStatsPanel({
  nodes,
  edges,
  stats: statsProp,
  title = "Chain stats",
  maxRows = 6,
  className,
  onClose
}) {
  const stats = React9.useMemo(
    () => statsProp ?? computeGraphStats(nodes ?? [], edges ?? []),
    [statsProp, nodes, edges]
  );
  const chains = stats.byChain.slice(0, maxRows);
  const chainsHidden = stats.byChain.length - chains.length;
  const variants = stats.byVariant.slice(0, maxRows);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "relative select-none rounded-md border bg-card/90 p-2 text-xs shadow-sm backdrop-blur",
        onClose && "pr-6",
        className
      ),
      children: [
        onClose ? /* @__PURE__ */ jsx(PanelCloseButton, { className: "absolute right-1 top-1", onClick: onClose, label: "Hide chain stats" }) : null,
        title ? /* @__PURE__ */ jsx("p", { className: "mb-1.5 font-medium text-foreground", children: title }) : null,
        /* @__PURE__ */ jsxs("div", { className: "mb-2 flex gap-4", children: [
          /* @__PURE__ */ jsx(StatBlock, { value: stats.nodeCount, label: "Nodes" }),
          /* @__PURE__ */ jsx(StatBlock, { value: stats.edgeCount, label: "Edges" }),
          stats.offChainCount > 0 && /* @__PURE__ */ jsx(StatBlock, { value: stats.offChainCount, label: "Off-chain" })
        ] }),
        chains.length > 0 && /* @__PURE__ */ jsxs("div", { className: "mb-2", children: [
          /* @__PURE__ */ jsxs("p", { className: "mb-1 text-[10px] uppercase tracking-wide text-muted-foreground", children: [
            "Chains",
            stats.byChain.length > 1 ? ` (${stats.byChain.length})` : ""
          ] }),
          /* @__PURE__ */ jsxs("ul", { className: "space-y-1", children: [
            chains.map((row) => {
              const chain = resolveChain(row.key);
              return /* @__PURE__ */ jsx(
                CountRow,
                {
                  logo: chain?.logoUrl,
                  swatch: chain?.brandColor ?? void 0,
                  label: chain?.name ?? row.key,
                  count: row.count
                },
                row.key
              );
            }),
            chainsHidden > 0 && /* @__PURE__ */ jsxs("li", { className: "text-[11px] text-muted-foreground", children: [
              "+",
              chainsHidden,
              " more"
            ] })
          ] })
        ] }),
        variants.length > 0 && /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsx("p", { className: "mb-1 text-[10px] uppercase tracking-wide text-muted-foreground", children: "Node types" }),
          /* @__PURE__ */ jsx("ul", { className: "space-y-1", children: variants.map((row) => /* @__PURE__ */ jsx(
            CountRow,
            {
              swatch: GRAPH_NODE_VARIANT_COLOR[row.key],
              label: GRAPH_NODE_VARIANT_LABELS[row.key],
              count: row.count
            },
            row.key
          )) })
        ] })
      ]
    }
  );
}
function useGraphDetailTier(view, options = {}) {
  const { tier: controlledTier, defaultTier, expandable = true } = options;
  const [internalTier, setInternalTier] = React9.useState(
    // Compact-first fallback (mirrors the scene's `default_detail_tier` default);
    // reached only if neither an explicit default nor the view carries a tier.
    defaultTier ?? view.defaultDetailTier ?? "compact"
  );
  const tier = controlledTier ?? internalTier;
  const [expandedNodeIds, setExpandedNodeIds] = React9.useState(
    () => /* @__PURE__ */ new Set()
  );
  const toggleNode = React9.useCallback(
    (id) => setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    }),
    []
  );
  const collapseAll = React9.useCallback(() => setExpandedNodeIds(/* @__PURE__ */ new Set()), []);
  const effectiveExpanded = expandable ? expandedNodeIds : EMPTY;
  const filteredView = React9.useMemo(
    () => filterSceneViewByDetailTier(view, tier, {
      expandedNodeIds: effectiveExpanded
    }),
    [view, tier, effectiveExpanded]
  );
  const expandableNodeIds = React9.useMemo(
    () => expandable ? nodesWithHiddenChildren(view, tier, { expandedNodeIds: effectiveExpanded }) : EMPTY,
    [view, tier, effectiveExpanded, expandable]
  );
  const isExpandable = React9.useCallback(
    (id) => expandableNodeIds.has(id),
    [expandableNodeIds]
  );
  return {
    view: filteredView,
    tier,
    setTier: setInternalTier,
    expandedNodeIds,
    toggleNode,
    collapseAll,
    expandableNodeIds,
    isExpandable
  };
}
var EMPTY = /* @__PURE__ */ new Set();

// src/share/serialize.ts
function compactGraphState(input) {
  const { nodes: _nodes, edges: _edges, scene: _scene, catalog: _catalog, ...rest } = input;
  return rest;
}
function graphShareStateHasContent(state) {
  if (!state) return false;
  return Boolean(
    state.scene?.nodes?.length || state.scene?.edges?.length || state.nodes?.length || state.edges?.length || state.rootIds?.length || state.expanded?.length
  );
}
function parseGraphSnapshot(json) {
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}
var GraphDefaultsContext = React9.createContext({});
function GraphDefaultsProvider({
  value,
  children
}) {
  const outer = React9.useContext(GraphDefaultsContext);
  const merged = React9.useMemo(() => ({ ...outer, ...value }), [outer, value]);
  return /* @__PURE__ */ jsx(GraphDefaultsContext.Provider, { value: merged, children });
}
function useGraphDefaults() {
  return React9.useContext(GraphDefaultsContext);
}
var DEFAULT_AUTHORED_KIND = "entity";
function hiddenLabel(ref) {
  const label = ref.label?.trim();
  if (label) return label;
  return shortenAddress(ref.id, { lead: 10, trail: 6 });
}
function HiddenMenu({
  count,
  hidden,
  onUnhide,
  onRestoreAll
}) {
  const perElement = onUnhide && hidden.length > 0;
  if (!perElement) {
    return /* @__PURE__ */ jsxs(
      ToolbarButton,
      {
        shape: "text",
        label: `Restore ${count}`,
        hint: `${count} element${count === 1 ? "" : "s"} hidden`,
        onClick: onRestoreAll,
        children: [
          "Restore ",
          count
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "sm",
        className: "h-7 px-2 text-xs",
        "aria-label": `${count} element${count === 1 ? "" : "s"} hidden`,
        children: [
          "Hidden ",
          count
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "w-60", children: [
      /* @__PURE__ */ jsxs(DropdownMenuLabel, { className: "flex flex-col gap-0.5", children: [
        /* @__PURE__ */ jsx("span", { children: "Hidden on this graph" }),
        /* @__PURE__ */ jsx("span", { className: "text-xs font-normal text-muted-foreground", children: "Left out of the view. Nothing was deleted." })
      ] }),
      hidden.map((ref) => /* @__PURE__ */ jsx(
        DropdownMenuItem,
        {
          onSelect: (event) => {
            event.preventDefault();
            onUnhide(ref);
          },
          children: /* @__PURE__ */ jsxs("span", { className: "flex w-full items-center justify-between gap-2", children: [
            /* @__PURE__ */ jsx("span", { className: "truncate", children: hiddenLabel(ref) }),
            /* @__PURE__ */ jsx("span", { className: "shrink-0 text-xs text-muted-foreground", children: ref.kind === "edge" ? "link" : "node" })
          ] })
        },
        `${ref.kind}:${ref.id}`
      )),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      /* @__PURE__ */ jsxs(DropdownMenuItem, { onSelect: () => onRestoreAll(), children: [
        "Restore all ",
        count
      ] })
    ] })
  ] });
}
function GraphAuthoringBar({
  api,
  selection,
  linking,
  linkSourceLabel,
  onToggleLinking,
  describing,
  onDescribed,
  onAuthored
}) {
  const [draftLabel, setDraftLabel] = React9.useState("");
  const [draftKind, setDraftKind] = React9.useState(DEFAULT_AUTHORED_KIND);
  const [adding, setAdding] = React9.useState(false);
  const [renaming, setRenaming] = React9.useState(false);
  const [renameLabel, setRenameLabel] = React9.useState("");
  const [reason, setReason] = React9.useState("");
  const describedId = React9.useRef(void 0);
  if (describedId.current !== describing?.id) {
    describedId.current = describing?.id;
    const existing = describing ? api.reasonFor?.({ kind: describing.kind, id: describing.id }) ?? "" : "";
    if (reason !== existing) setReason(existing);
  }
  const commitReason = React9.useCallback(() => {
    onDescribed?.(reason.trim());
    setReason("");
  }, [onDescribed, reason]);
  const commitAdd = React9.useCallback(() => {
    const label = draftLabel.trim();
    if (!label) return;
    const added = api.onAddNode?.({ label, kind: draftKind });
    setDraftLabel("");
    setDraftKind(DEFAULT_AUTHORED_KIND);
    setAdding(false);
    if (added) onAuthored?.({ id: added.id, kind: "node", ask: "added" });
  }, [api, draftLabel, draftKind, onAuthored]);
  const commitRename = React9.useCallback(() => {
    const label = renameLabel.trim();
    if (!label || !selection) {
      setRenaming(false);
      return;
    }
    api.onRelabel?.(selection, label);
    setRenaming(false);
    if (!selection.authored) {
      onAuthored?.({ id: selection.id, kind: selection.kind, ask: "renamed" });
    }
  }, [api, renameLabel, selection, onAuthored]);
  const explainSelection = React9.useCallback(() => {
    if (!selection) return;
    onAuthored?.({
      id: selection.id,
      kind: selection.kind,
      ask: selection.kind === "edge" ? "linked" : "added"
    });
  }, [onAuthored, selection]);
  const askedFor = describing ? {
    added: "Why is this on the graph?",
    linked: "Why are these connected?",
    renamed: "Why this name?"
  }[describing.ask] : "";
  const canEdit = selection != null;
  const renameTarget = React9.useRef(selection?.id);
  if (renameTarget.current !== selection?.id) {
    renameTarget.current = selection?.id;
    if (renaming) setRenaming(false);
  }
  const hidden = api.hiddenCount ?? 0;
  const showRestore = hidden > 0 && Boolean(api.onRestoreHidden);
  const showPromote = Boolean(api.onPromote) && api.canPromote === true;
  const published = api.publishedCount ?? 0;
  const showManage = Boolean(api.onManagePublished) && published > 0;
  const showRevert = selection != null && Boolean(api.onRevert) && !selection.authored && (api.isPatched?.(selection) ?? false);
  const showExplain = selection != null && Boolean(api.onExplain) && !describing;
  const showHide = canEdit && Boolean(api.onHide) && !selection.authored;
  const showRemove = canEdit && Boolean(api.onRemove) && selection.authored === true;
  const showPrompt = Boolean(describing && onDescribed);
  if (!api.onAddNode && !api.onConnect && !api.onRelabel && !showHide && !showRemove && !showRevert && !showExplain && !showPrompt && !showRestore && !showPromote && !showManage) {
    return null;
  }
  const controls = /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
    api.onAddNode ? adding ? /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          autoFocus: true,
          value: draftLabel,
          onChange: (e) => setDraftLabel(e.target.value),
          onKeyDown: (e) => {
            if (e.key === "Enter") commitAdd();
            if (e.key === "Escape") setAdding(false);
          },
          placeholder: "Node name\u2026",
          "aria-label": "New node name",
          className: "h-7 w-36 text-xs"
        }
      ),
      /* @__PURE__ */ jsx(
        NativeSelect,
        {
          value: draftKind,
          onChange: (e) => setDraftKind(e.target.value),
          "aria-label": "New node type",
          density: "sm",
          children: AUTHORABLE_NODE_KINDS.map((option) => /* @__PURE__ */ jsx("option", { value: option.kind, children: option.label }, option.kind))
        }
      ),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", className: "h-7 px-2 text-xs", onClick: commitAdd, children: "Add" })
    ] }) : /* @__PURE__ */ jsx(ToolbarButton, { label: "Add node", onClick: () => setAdding(true), children: /* @__PURE__ */ jsx(PlusIcon, { size: 13 }) }) : null,
    describing && onDescribed ? /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          autoFocus: true,
          value: reason,
          onChange: (e) => setReason(e.target.value),
          onKeyDown: (e) => {
            if (e.key === "Enter") commitReason();
            if (e.key === "Escape") onDescribed(null);
          },
          placeholder: askedFor,
          "aria-label": askedFor,
          className: "h-7 w-52 text-xs"
        }
      ),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", className: "h-7 px-2 text-xs", onClick: commitReason, children: "Save" }),
      /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "ghost",
          className: "h-7 px-2 text-xs",
          onClick: () => onDescribed(null),
          children: "Skip"
        }
      )
    ] }) : api.onConnect ? /* @__PURE__ */ jsx(
      ToolbarButton,
      {
        active: linking,
        label: linking ? "Cancel connecting" : "Connect two nodes",
        hint: linking ? linkSourceLabel ? `Click the target node (from ${linkSourceLabel})` : "Click the source node" : void 0,
        onClick: onToggleLinking,
        children: /* @__PURE__ */ jsx(LinkIcon, { size: 13 })
      }
    ) : null,
    canEdit && api.onRelabel ? renaming ? /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          autoFocus: true,
          value: renameLabel,
          onChange: (e) => setRenameLabel(e.target.value),
          onKeyDown: (e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") setRenaming(false);
          },
          "aria-label": "New name",
          className: "h-7 w-36 text-xs"
        }
      ),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", className: "h-7 px-2 text-xs", onClick: commitRename, children: "Save" })
    ] }) : /* @__PURE__ */ jsx(
      ToolbarButton,
      {
        shape: "text",
        label: "Rename",
        hint: `Rename this ${selection.kind}`,
        onClick: () => {
          setRenameLabel(selection.label ?? "");
          setRenaming(true);
        },
        children: "Rename"
      }
    ) : null,
    showExplain && !renaming ? /* @__PURE__ */ jsx(
      ToolbarButton,
      {
        shape: "text",
        label: "Why",
        hint: api.reasonFor?.(selection) ? "Revise why this is on the graph" : `Say why this ${selection.kind} is on the graph`,
        onClick: explainSelection,
        children: "Why"
      }
    ) : null,
    showRevert ? /* @__PURE__ */ jsx(
      ToolbarButton,
      {
        shape: "text",
        label: "Restore decoded name",
        hint: "Drop the rename and show what the decode called it",
        onClick: () => api.onRevert?.(selection),
        children: "Undo rename"
      }
    ) : null,
    showHide ? /* @__PURE__ */ jsx(
      ToolbarButton,
      {
        shape: "text",
        label: "Hide",
        hint: "Leave this out of the view (reversible)",
        onClick: () => api.onHide?.(selection),
        children: "Hide"
      }
    ) : null,
    showRemove ? /* @__PURE__ */ jsx(
      ToolbarButton,
      {
        shape: "text",
        label: "Remove",
        hint: "Delete this annotation",
        onClick: () => api.onRemove?.(selection),
        children: "Remove"
      }
    ) : null,
    showRestore ? /* @__PURE__ */ jsx(
      HiddenMenu,
      {
        count: hidden,
        hidden: api.hidden ?? [],
        onUnhide: api.onUnhide,
        onRestoreAll: () => api.onRestoreHidden?.()
      }
    ) : null,
    showManage ? /* @__PURE__ */ jsxs(
      ToolbarButton,
      {
        shape: "text",
        label: `Published ${published}`,
        hint: `${published} published annotation${published === 1 ? "" : "s"} on this graph`,
        onClick: () => api.onManagePublished?.(),
        children: [
          "Published ",
          published
        ]
      }
    ) : null,
    showPromote ? /* @__PURE__ */ jsx(
      ToolbarButton,
      {
        shape: "text",
        label: "Publish",
        hint: "Publish these annotations beyond this case",
        onClick: () => api.onPromote?.(),
        children: "Publish\u2026"
      }
    ) : null
  ] });
  return /* @__PURE__ */ jsx(TooltipProvider, { delayDuration: TOOLBAR_TOOLTIP_DELAY_MS, children: controls });
}
var VIEW_MODES = /* @__PURE__ */ new Set(["hierarchical", "temporal", "force"]);
var EMPTY_SEARCH_SPEC = {};
var EXPECTED_LEGEND_ITEM = {
  label: "Expected (not yet observed)",
  // The neutral/muted token the dashed synthesized elements read as (mirrors the
  // `default` edge variant); the *dashed* shape is what carries the meaning.
  color: "hsl(var(--muted-foreground))",
  shape: "dashed"
};
var FLAGGED_LEGEND_ITEM = {
  label: "Flagged address",
  color: "hsl(var(--risk-critical))",
  shape: "line"
};
var AUTHORED_LEGEND_ITEM = {
  // Matches `AUTHORED_ACCENT_CLASS` / the `authored` edge variant token, so one
  // colour means "somebody asserted this" throughout the graph.
  label: "Analyst-added",
  color: "hsl(var(--risk-medium))",
  shape: "line"
};
function legendForElements(nodes, edges) {
  const present = /* @__PURE__ */ new Set();
  for (const edge of edges) {
    present.add(edge.data?.variant ?? "call");
  }
  const items = GRAPH_EDGE_VARIANTS.filter((v) => present.has(v)).map(
    (variant) => ({
      label: GRAPH_EDGE_VARIANT_LABELS[variant],
      color: GRAPH_EDGE_VARIANT_COLOR[variant],
      shape: variant === "flow" ? "dashed" : "line"
    })
  );
  const hasExpected = edges.some((e) => e.data?.dashed === true) || nodes.some((n) => n.data?.virtual === true);
  if (hasExpected) items.push(EXPECTED_LEGEND_ITEM);
  if (nodes.some((n) => n.data?.origin === "user")) items.push(AUTHORED_LEGEND_ITEM);
  if (nodes.some((n) => n.data?.riskLevel === "high" || n.data?.riskLevel === "severe")) {
    items.push(FLAGGED_LEGEND_ITEM);
  }
  return items;
}
function GraphSettingsMenu({
  detail,
  grouping,
  toggles,
  settings
}) {
  const order = grouping?.value.dimensions ?? [];
  const groupable = grouping ? GROUPING_DIMENSION_LIST.filter(
    (dimension) => grouping.applicable.has(dimension.id) || order.includes(dimension.id)
  ) : [];
  const showGrouping = groupable.length > 0;
  const toggleDimension = (id, on) => {
    const next = on ? [...order.filter((d) => d !== id), id] : order.filter((d) => d !== id);
    grouping?.onChange({ ...grouping.value, dimensions: next });
  };
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "sm",
        "aria-label": "Graph settings",
        title: "Graph settings",
        className: "h-7 w-7 p-0",
        children: /* @__PURE__ */ jsx(SettingsIcon, { size: 14 })
      }
    ) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "w-52", children: [
      detail ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Detail level" }),
        GRAPH_DETAIL_TIERS.map((tier) => {
          const active = tier === detail.value;
          return /* @__PURE__ */ jsxs(
            DropdownMenuItem,
            {
              className: "relative pl-8",
              "aria-checked": active,
              onSelect: (event) => {
                event.preventDefault();
                detail.onChange(tier);
              },
              children: [
                /* @__PURE__ */ jsx("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: active ? /* @__PURE__ */ jsx(CheckIcon, { size: 14 }) : null }),
                GRAPH_DETAIL_TIER_LABELS[tier]
              ]
            },
            tier
          );
        })
      ] }) : null,
      detail && showGrouping ? /* @__PURE__ */ jsx(DropdownMenuSeparator, {}) : null,
      showGrouping && grouping ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(DropdownMenuLabel, { className: "flex flex-col gap-0.5", children: [
          /* @__PURE__ */ jsx("span", { children: "Group by" }),
          /* @__PURE__ */ jsx("span", { className: "text-xs font-normal text-muted-foreground", children: order.length > 1 ? "Outermost first" : "Pick fields to nest" })
        ] }),
        groupable.map((dimension) => {
          const position = order.indexOf(dimension.id);
          return /* @__PURE__ */ jsx(
            DropdownMenuCheckboxItem,
            {
              checked: position >= 0,
              onCheckedChange: (checked) => toggleDimension(dimension.id, checked === true),
              onSelect: (event) => event.preventDefault(),
              children: /* @__PURE__ */ jsxs("span", { className: "flex w-full items-center justify-between gap-2", children: [
                /* @__PURE__ */ jsx("span", { children: dimension.label }),
                position >= 0 && order.length > 1 ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: position + 1 }) : null
              ] })
            },
            dimension.id
          );
        })
      ] }) : null,
      (detail || showGrouping) && toggles.length > 0 ? /* @__PURE__ */ jsx(DropdownMenuSeparator, {}) : null,
      toggles.length > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Panels" }),
        toggles.map((toggle) => /* @__PURE__ */ jsx(
          DropdownMenuCheckboxItem,
          {
            checked: toggle.visible,
            onCheckedChange: (checked) => toggle.onChange(checked === true),
            onSelect: (event) => event.preventDefault(),
            children: toggle.label
          },
          toggle.key
        ))
      ] }) : null,
      settings.length > 0 ? /* @__PURE__ */ jsxs(Fragment, { children: [
        detail || showGrouping || toggles.length > 0 ? /* @__PURE__ */ jsx(DropdownMenuSeparator, {}) : null,
        /* @__PURE__ */ jsx(DropdownMenuLabel, { children: "Behaviour" }),
        settings.map((setting) => /* @__PURE__ */ jsx(
          DropdownMenuCheckboxItem,
          {
            checked: setting.checked,
            onCheckedChange: (checked) => setting.onChange(checked === true),
            onSelect: (event) => event.preventDefault(),
            children: setting.hint ? /* @__PURE__ */ jsxs("span", { className: "flex flex-col gap-0.5", children: [
              /* @__PURE__ */ jsx("span", { children: setting.label }),
              /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: setting.hint })
            ] }) : setting.label
          },
          setting.key
        ))
      ] }) : null
    ] })
  ] });
}
function SceneGraphView({
  scene,
  cdnBase: cdnBaseProp,
  resolvePlaceholder: resolvePlaceholderProp,
  chainLabel,
  resolveOverlay,
  viewMode,
  defaultViewMode,
  brandAccent: brandAccentProp,
  height = 480,
  className,
  onNodeClick,
  // Pulled out of `canvasProps` so authoring can select the clicked edge before
  // handing it on. A host that wires its own (the investigation canvas opens its
  // inspector on one) still gets every click.
  onEdgeClick,
  onClusterClick,
  onGroupClick,
  detailTier,
  defaultDetailTier: defaultDetailTierProp,
  decorateElements,
  onDetailTierChange,
  showDetailToggle: showDetailToggleProp,
  enableNodeExpand = true,
  renderHighlights,
  showSummary: showSummaryProp,
  showLegend: showLegendProp,
  extraLegend,
  showStats: showStatsProp,
  showFullscreen: showFullscreenProp,
  showFlowToggle: showFlowToggleProp,
  showEdgeBundleToggle: showEdgeBundleToggleProp,
  showRelayout: showRelayoutProp,
  showMiniMap: showMiniMapProp,
  hiddenPanels: hiddenPanelsProp,
  defaultHiddenPanels,
  onHiddenPanelsChange,
  grouping: groupingProp,
  defaultGrouping,
  onGroupingChange,
  showGroupingControl: showGroupingControlProp,
  showSearch: showSearchProp,
  showExport: showExportProp,
  search: searchProp,
  defaultSearchQuery,
  graphId,
  seed,
  onApplyView,
  showViewsBar = true,
  editable = false,
  authoring,
  settingToggles,
  // `legend` is a host override for the auto-derived edge legend below.
  legend: legendOverride,
  ...canvasProps
}) {
  const defaults = useGraphDefaults();
  const cdnBase = cdnBaseProp ?? defaults.cdnBase;
  const resolvePlaceholder = resolvePlaceholderProp ?? defaults.resolvePlaceholder;
  const brandAccent = brandAccentProp ?? defaults.brandAccent;
  const defaultDetailTier = defaultDetailTierProp ?? defaults.defaultDetailTier;
  const showDetailToggle = showDetailToggleProp ?? defaults.showDetailToggle ?? true;
  const showSummary = showSummaryProp ?? defaults.showSummary ?? true;
  const showLegend = showLegendProp ?? defaults.showLegend ?? true;
  const showStats = showStatsProp ?? defaults.showStats ?? false;
  const showFullscreen = showFullscreenProp ?? defaults.showFullscreen ?? true;
  const showFlowToggle = showFlowToggleProp ?? defaults.showFlowToggle ?? true;
  const showEdgeBundleToggle = showEdgeBundleToggleProp ?? defaults.showEdgeBundleToggle ?? true;
  const showRelayout = showRelayoutProp ?? defaults.showRelayout ?? true;
  const showMiniMap = showMiniMapProp ?? defaults.showMiniMap ?? true;
  const showGroupingControl = showGroupingControlProp ?? defaults.showGroupingControl ?? true;
  const showSearch = showSearchProp ?? defaults.showSearch ?? true;
  const showExport = showExportProp ?? defaults.showExport ?? true;
  const searchSpec = searchProp ?? defaults.search;
  const highlights = React9.useMemo(
    () => parseKeyAttributeGroups(scene.highlights),
    [scene.highlights]
  );
  const [internalHidden, setInternalHidden] = React9.useState(
    () => defaultHiddenPanels ?? []
  );
  const hiddenList = hiddenPanelsProp ?? internalHidden;
  const isHidden = React9.useCallback(
    (key) => hiddenList.includes(key),
    [hiddenList]
  );
  const setHiddenPanels = React9.useCallback(
    (next) => {
      if (hiddenPanelsProp === void 0) setInternalHidden(next);
      onHiddenPanelsChange?.(next);
    },
    [hiddenPanelsProp, onHiddenPanelsChange]
  );
  const setPanelVisible = React9.useCallback(
    (key, visible) => {
      const next = visible ? hiddenList.filter((k) => k !== key) : [...hiddenList, key];
      if (next.length === hiddenList.length && visible) return;
      if (!visible && hiddenList.includes(key)) return;
      setHiddenPanels(next);
    },
    [hiddenList, setHiddenPanels]
  );
  const [internalGrouping, setInternalGrouping] = React9.useState(
    () => defaultGrouping ?? defaults.grouping
  );
  const grouping = groupingProp ?? internalGrouping;
  const setGrouping = React9.useCallback(
    (next) => {
      if (groupingProp === void 0) setInternalGrouping(next);
      onGroupingChange?.(next);
    },
    [groupingProp, onGroupingChange]
  );
  const groupedScene = React9.useMemo(
    () => grouping ? applyGrouping(scene, grouping, { resolveOverlay }) : scene,
    [scene, grouping, resolveOverlay]
  );
  const sceneViewOptions = React9.useMemo(
    () => ({ cdnBase, resolvePlaceholder, chainLabel, resolveOverlay }),
    [cdnBase, resolvePlaceholder, chainLabel, resolveOverlay]
  );
  const fullView = React9.useMemo(
    () => toSceneView(groupedScene, sceneViewOptions),
    [groupedScene, sceneViewOptions]
  );
  const detail = useGraphDetailTier(fullView, {
    tier: detailTier,
    defaultTier: defaultDetailTier,
    expandable: enableNodeExpand
  });
  const { nodes, edges, groups, layoutHint } = React9.useMemo(() => {
    const elements = sceneViewToElements(detail.view, { brandAccent });
    return decorateElements ? decorateElements(elements) : elements;
  }, [detail.view, brandAccent, decorateElements]);
  const bandApi = React9.useMemo(() => {
    if (!onGroupClick) return null;
    return {
      enabled: true,
      open: (groupId) => {
        const group = groups.find((candidate) => candidate.id === groupId);
        if (group) {
          onGroupClick({ id: groupId, label: group.label, memberIds: group.memberNodeIds });
        }
      }
    };
  }, [onGroupClick, groups]);
  const search = useGraphSearch(nodes, searchSpec ?? EMPTY_SEARCH_SPEC, {
    initialQuery: defaultSearchQuery
  });
  const legend = React9.useMemo(() => {
    const base = legendOverride ?? (showLegend ? legendForElements(nodes, edges) : []);
    const extra = showLegend || legendOverride ? extraLegend ?? [] : [];
    if (base.length === 0 && extra.length === 0) return void 0;
    const taggedBase = extra.length > 0 ? base.map((item) => item.group ? item : { ...item, group: "Edge types" }) : base;
    return [...taggedBase, ...extra];
  }, [legendOverride, showLegend, nodes, edges, extraLegend]);
  const [appliedMode, setAppliedMode] = React9.useState(
    defaultViewMode
  );
  const canvasContainerRef = React9.useRef(null);
  const resolvedMode = viewMode ?? appliedMode ?? (VIEW_MODES.has(layoutHint) ? layoutHint : "hierarchical");
  const authoringOn = editable && authoring != null;
  const [linking, setLinking] = React9.useState(false);
  const [linkSource, setLinkSource] = React9.useState();
  const handleNodeMoved = React9.useMemo(
    () => authoringOn && authoring?.onMove ? authoring.onMove : void 0,
    [authoringOn, authoring]
  );
  const pinnedPositions = React9.useMemo(() => {
    const pins = {};
    for (const node of fullView.nodes) {
      if (node.position) pins[node.id] = node.position;
    }
    return pins;
  }, [fullView.nodes]);
  const [describing, setDescribing] = React9.useState();
  const [selection, setSelection] = React9.useState();
  const toggleLinking = React9.useCallback(() => {
    setLinking((on) => !on);
    setLinkSource(void 0);
  }, []);
  const connect = React9.useCallback(
    (source, target) => {
      const drawn = authoring?.onConnect?.(source, target);
      setDescribing(
        drawn ? { id: drawn.id, kind: "edge", ask: "linked", nameless: true } : void 0
      );
      return drawn;
    },
    [authoring]
  );
  const describe = React9.useCallback(
    (reason) => {
      if (describing && reason !== null) {
        const target = { kind: describing.kind, id: describing.id };
        authoring?.onExplain?.(target, reason);
        if (reason && describing.nameless) authoring?.onRelabel?.(target, reason);
      }
      setDescribing(void 0);
    },
    [authoring, describing]
  );
  const handleNodeClick = React9.useMemo(() => {
    if (!authoringOn && !onNodeClick && !onClusterClick) return void 0;
    return (node) => {
      if (authoringOn && linking) {
        if (!linkSource) {
          setLinkSource(node);
          return;
        }
        if (linkSource.id !== node.id) connect(linkSource.id, node.id);
        setLinking(false);
        setLinkSource(void 0);
        return;
      }
      const members = node.data?.memberIds;
      if (node.data?.cluster && members && members.length > 0) {
        onClusterClick?.({ id: node.id, label: node.data.label, memberIds: members });
        return;
      }
      if (authoringOn) {
        setSelection({
          kind: "node",
          id: node.id,
          authored: node.data?.origin === "user",
          label: node.data?.label
        });
      }
      onNodeClick?.(node.id);
    };
  }, [authoringOn, linking, linkSource, connect, onNodeClick, onClusterClick]);
  const handleEdgeClick = React9.useMemo(() => {
    if (!authoringOn && !onEdgeClick) return void 0;
    return (edge) => {
      if (authoringOn && !linking && !edge.data?.aggregated) {
        setSelection({
          kind: "edge",
          id: edge.id,
          authored: edge.data?.origin === "user",
          label: edge.data?.label
        });
      }
      onEdgeClick?.(edge);
    };
  }, [authoringOn, linking, onEdgeClick]);
  const handleConnect = React9.useMemo(
    () => authoringOn ? connect : void 0,
    [authoringOn, connect]
  );
  const liveSelection = React9.useMemo(() => {
    if (!selection) return void 0;
    const live = selection.kind === "edge" ? edges.find((e) => e.id === selection.id) : nodes.find((n) => n.id === selection.id);
    if (!live) return void 0;
    const label = live.data?.label;
    return label === selection.label ? selection : { ...selection, label };
  }, [selection, nodes, edges]);
  const setTier = React9.useCallback(
    (tier) => {
      detail.setTier(tier);
      onDetailTierChange?.(tier);
    },
    [detail, onDetailTierChange]
  );
  const catalogSnapshot = React9.useMemo(
    () => freezeCatalogSnapshot(scene, resolveOverlay),
    [scene, resolveOverlay]
  );
  const shareState = React9.useMemo(
    () => ({
      viewMode: resolvedMode,
      detailTier: detail.tier,
      hiddenPanels: [...hiddenList],
      ...grouping ? { grouping } : {},
      ...search.query.trim() ? { search: search.query } : {},
      scene,
      // The overlay this graph is being READ with, frozen alongside it. This is
      // the surface that resolves overlays, so it is the only place that can:
      // by the time a view reaches the share dialog the resolver is long gone,
      // which is why `owner_snapshot` had nothing to hand back.
      ...catalogSnapshot ? { catalog: catalogSnapshot } : {},
      // The re-fetch recipe, when the host has said what this graph IS. It rides
      // the same state the snapshot does, so one saved view backs both halves of
      // the share matrix.
      ...seed ? {
        resource: seed.resource,
        rootIds: [...seed.rootIds],
        ...seed.expandedIds?.length ? { expanded: [...seed.expandedIds] } : {},
        ...seed.filters ? { filters: seed.filters } : {}
      } : {}
    }),
    [resolvedMode, detail.tier, hiddenList, grouping, search.query, scene, catalogSnapshot, seed]
  );
  const openedWith = React9.useRef(null);
  if (openedWith.current === null) {
    openedWith.current = {
      viewMode: defaultViewMode,
      detailTier: defaultDetailTier ?? scene.default_detail_tier,
      hiddenPanels: [...defaultHiddenPanels ?? []],
      grouping: defaultGrouping ?? defaults.grouping,
      search: ""
    };
  }
  const applyView = React9.useCallback(
    (state) => {
      if (onApplyView) {
        onApplyView(state);
        return;
      }
      const next = state ?? openedWith.current;
      setAppliedMode(next.viewMode);
      if (next.detailTier) setTier(next.detailTier);
      setHiddenPanels([...next.hiddenPanels ?? []]);
      if (next.grouping) setGrouping(next.grouping);
      search.setQuery(next.search ?? "");
    },
    [onApplyView, setTier, setHiddenPanels, setGrouping, search]
  );
  const expandApi = React9.useMemo(
    () => ({
      enabled: enableNodeExpand,
      isExpandable: detail.isExpandable,
      isExpanded: (id) => detail.expandedNodeIds.has(id),
      toggle: detail.toggleNode
    }),
    [enableNodeExpand, detail.isExpandable, detail.expandedNodeIds, detail.toggleNode]
  );
  const legendAvailable = Boolean(legend && legend.length > 0);
  const highlightsAvailable = Boolean(renderHighlights) && highlights.length > 0;
  const statsAvailable = showStats;
  const minimapAvailable = showMiniMap && resolvedMode !== "force";
  const showLegendPanel = legendAvailable && !isHidden("legend");
  const showStatsPanel = statsAvailable && !isHidden("stats");
  const showHighlightsPanel = highlightsAvailable && !isHidden("highlights");
  const showMiniMapPanel = minimapAvailable && !isHidden("minimap");
  const panelToggles = [];
  const addToggle = (key, visible) => {
    panelToggles.push({
      key,
      label: GRAPH_PANEL_LABELS[key],
      visible,
      onChange: (v) => setPanelVisible(key, v)
    });
  };
  if (legendAvailable) addToggle("legend", showLegendPanel);
  if (highlightsAvailable) addToggle("highlights", showHighlightsPanel);
  if (statsAvailable) addToggle("stats", showStatsPanel);
  if (minimapAvailable) addToggle("minimap", showMiniMapPanel);
  const detailControl = showDetailToggle ? { value: detail.tier, onChange: setTier } : void 0;
  const applicable = React9.useMemo(
    () => applicableDimensions(scene, { resolveOverlay }),
    [scene, resolveOverlay]
  );
  const groupingControl = showGroupingControl ? { value: grouping ?? { dimensions: [] }, onChange: setGrouping, applicable } : void 0;
  const hostSettings = React9.useMemo(() => [...settingToggles ?? []], [settingToggles]);
  const settingsGear = detailControl || groupingControl || panelToggles.length > 0 || hostSettings.length > 0 ? /* @__PURE__ */ jsx(
    GraphSettingsMenu,
    {
      detail: detailControl,
      grouping: groupingControl,
      toggles: panelToggles,
      settings: hostSettings
    }
  ) : void 0;
  const authoringBar = authoringOn ? /* @__PURE__ */ jsx(
    GraphAuthoringBar,
    {
      api: authoring,
      selection: liveSelection,
      linking,
      linkSourceLabel: linkSource?.data?.label,
      onToggleLinking: toggleLinking,
      describing,
      onDescribed: describe,
      onAuthored: setDescribing
    }
  ) : null;
  const searchBox = showSearch ? /* @__PURE__ */ jsx(GraphSearchBox, { search }) : null;
  const exportMenu = showExport ? /* @__PURE__ */ jsx(
    GraphExportMenu,
    {
      graph: shareState,
      containerRef: canvasContainerRef,
      filenameBase: graphId ?? "graph",
      ...defaults.exportContext ? { exportContext: defaults.exportContext } : {}
    }
  ) : null;
  const viewsBar = showViewsBar && defaults.renderViewsBar ? defaults.renderViewsBar({ graphId, state: shareState, onApplyState: applyView }) : null;
  const toolbarExtra = viewsBar || authoringBar || searchBox || exportMenu || settingsGear ? /* @__PURE__ */ jsxs(Fragment, { children: [
    viewsBar,
    authoringBar,
    searchBox,
    exportMenu,
    settingsGear
  ] }) : void 0;
  const hasCanvasToolbar = showFullscreen || showFlowToggle || showEdgeBundleToggle || showRelayout || toolbarExtra != null;
  const canvasInner = /* @__PURE__ */ jsxs(Fragment, { children: [
    showLegendPanel && legend ? (
      // `pointer-events-none` so panning still passes through the legend's box;
      // the `GraphLegend` root and its × button re-enable events where needed.
      /* @__PURE__ */ jsx("div", { className: cn("pointer-events-none absolute right-2 z-10", hasCanvasToolbar ? "top-12" : "top-2"), children: /* @__PURE__ */ jsx(GraphLegend, { items: legend, onClose: () => setPanelVisible("legend", false) }) })
    ) : null,
    showStatsPanel ? /* @__PURE__ */ jsx("div", { className: "absolute bottom-2 left-2 z-10 max-w-[16rem]", children: /* @__PURE__ */ jsx(GraphStatsPanel, { nodes, edges, onClose: () => setPanelVisible("stats", false) }) }) : null,
    nodes.length === 0 ? (
      // Not a failure: a message-only receive or an approval genuinely has
      // nothing to draw. The backend says WHICH kind of nothing on
      // `attributes.empty_reason`, and rendering that is the difference
      // between an answer and a blank frame that reads as a broken build.
      /* @__PURE__ */ jsx(GraphEmptyState, { reason: fullView.emptyReason, className: "h-full" })
    ) : null,
    /* @__PURE__ */ jsx(GraphExpandContext.Provider, { value: expandApi, children: /* @__PURE__ */ jsx(GraphGroupBandContext.Provider, { value: bandApi, children: /* @__PURE__ */ jsx(
      GraphCanvas,
      {
        ...canvasProps,
        containerRef: canvasContainerRef,
        nodes,
        edges,
        chainGroups: groups,
        viewMode: resolvedMode,
        showMiniMap: showMiniMapPanel,
        showFullscreen,
        showFlowToggle,
        showEdgeBundleToggle,
        showRelayout,
        extraToolbar: toolbarExtra,
        searchQuery: search.query || canvasProps.searchQuery,
        focusNodeId: search.focusId ?? canvasProps.focusNodeId,
        onNodeClick: handleNodeClick,
        onEdgeClick: handleEdgeClick,
        onConnect: handleConnect,
        pinnedPositions,
        onNodePositionChange: handleNodeMoved
      }
    ) }) })
  ] });
  const summarySource = showSummary ? scene.summary?.trim() || void 0 : void 0;
  const summary = React9.useMemo(
    () => summarySource ? displayChainText(summarySource, { maxChars: 240 }) : void 0,
    [summarySource]
  );
  const summaryText = summary?.text;
  if (!summaryText && !showHighlightsPanel) {
    return /* @__PURE__ */ jsx("div", { className, style: { position: "relative", height }, children: canvasInner });
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className,
      style: { height, display: "flex", flexDirection: "column", minHeight: 0, gap: "0.75rem" },
      children: [
        /* @__PURE__ */ jsxs("div", { className: "space-y-2 px-3 pt-1", children: [
          summaryText ? /* @__PURE__ */ jsx(
            "p",
            {
              className: "text-sm font-medium text-foreground",
              "data-testid": "scene-intent",
              title: summary?.title,
              children: summaryText
            }
          ) : null,
          showHighlightsPanel ? /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx(
              PanelCloseButton,
              {
                className: "absolute right-0 top-0 z-10",
                onClick: () => setPanelVisible("highlights", false),
                label: "Hide transaction highlights"
              }
            ),
            /* @__PURE__ */ jsx("div", { className: "pr-5", children: renderHighlights(highlights) })
          ] }) : null
        ] }),
        /* @__PURE__ */ jsx("div", { style: { position: "relative", flex: "1 1 0%", minHeight: 0 }, children: canvasInner })
      ]
    }
  );
}
function titleRole(role) {
  return role.length ? role.charAt(0).toUpperCase() + role.slice(1) : role;
}
function LifecycleBadge({ group }) {
  if (group.awaiting) {
    const roles = group.missingRoles.length ? group.missingRoles.map(titleRole).join(" + ") : "next leg";
    return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700", children: [
      /* @__PURE__ */ jsxs("span", { className: "relative inline-flex h-2 w-2", children: [
        /* @__PURE__ */ jsx("span", { className: "absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" }),
        /* @__PURE__ */ jsx("span", { className: "relative inline-flex h-2 w-2 rounded-full bg-amber-500" })
      ] }),
      "Awaiting ",
      roles
    ] });
  }
  if (group.state === "completed") {
    return /* @__PURE__ */ jsx("span", { className: "inline-flex items-center rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700", children: "Complete" });
  }
  if (group.partial) {
    return /* @__PURE__ */ jsx("span", { className: "inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700", children: "Partial \u2014 awaiting other leg" });
  }
  return null;
}
function shortHash(value) {
  if (!value) return void 0;
  return value.length > 14 ? `${value.slice(0, 8)}\u2026${value.slice(-4)}` : value;
}
function shortCorrelationId(value) {
  if (!value) return void 0;
  return value.split(":").map((part) => shortHash(part) ?? part).join(":");
}
function shortAddr(value) {
  if (!value) return void 0;
  return value.length > 12 ? `${value.slice(0, 6)}\u2026${value.slice(-4)}` : value;
}
function chainName(chainId, chainLabel) {
  return chainLabel?.(chainId) ?? getChainGroupColors(chainId).name;
}
function LegChip({
  leg,
  chainLabel
}) {
  const isSource = leg.role === "source";
  const tx = shortHash(leg.txHash);
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs",
      style: {
        borderColor: getChainGroupColors(leg.chainId).brandColor,
        background: getChainGroupColors(leg.chainId).fill
      },
      title: leg.txHash,
      children: [
        /* @__PURE__ */ jsx("span", { className: "font-medium uppercase tracking-wide opacity-70", children: isSource ? "Source" : leg.role === "destination" ? "Destination" : roleLabel(leg.role) }),
        /* @__PURE__ */ jsx("span", { className: "font-medium", children: leg.label ?? chainName(leg.chainId, chainLabel) }),
        tx && /* @__PURE__ */ jsx("span", { className: "font-mono opacity-60", children: tx })
      ]
    }
  );
}
function GroupGraphView({
  group,
  renderHighlights,
  chainLabel,
  live,
  ...sceneProps
}) {
  const { scene, highlights, legs, origin, originChain, messageId, idLabel } = group;
  const originName = origin ? shortAddr(origin) : void 0;
  const originChainName = originChain ? chainName(originChain, chainLabel) : void 0;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    renderHighlights && highlights.length > 0 && /* @__PURE__ */ jsx("div", { children: renderHighlights(highlights) }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
      legs.map((leg, i) => /* @__PURE__ */ jsx(LegChip, { leg, chainLabel }, `${leg.role}:${leg.chainId}:${i}`)),
      live ?? /* @__PURE__ */ jsx(LifecycleBadge, { group })
    ] }),
    (originName || messageId) && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground", children: [
      originName && /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("span", { className: "opacity-70", children: "Origin" }),
        " ",
        /* @__PURE__ */ jsx("span", { className: "font-mono", children: originName }),
        originChainName && /* @__PURE__ */ jsxs("span", { className: "opacity-70", children: [
          " on ",
          originChainName
        ] })
      ] }),
      messageId && /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("span", { className: "opacity-70", children: sagaIdLabel(idLabel) }),
        " ",
        /* @__PURE__ */ jsx("span", { className: "font-mono", children: shortCorrelationId(messageId) })
      ] })
    ] }),
    /* @__PURE__ */ jsx(SceneGraphView, { scene, chainLabel, ...sceneProps })
  ] });
}
function GraphTimeline({
  controller,
  locale,
  showLabels = true,
  emptyState = null,
  className
}) {
  const { min, max, cursor, hasTimeline, setCursor } = controller;
  if (!hasTimeline) return /* @__PURE__ */ jsx(Fragment, { children: emptyState });
  return /* @__PURE__ */ jsxs("div", { className: cn("flex w-full flex-col gap-1", className), children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        type: "range",
        min,
        max,
        step: 1,
        value: cursor,
        onChange: (event) => setCursor(Number(event.target.value)),
        "aria-label": "Replay timeline",
        className: "h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted accent-[hsl(var(--brand))]"
      }
    ),
    showLabels && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between text-[11px] text-muted-foreground tabular-nums", children: [
      /* @__PURE__ */ jsx("span", { children: formatTimestamp(min, { locale }) }),
      /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: formatTimestamp(cursor, { locale }) }),
      /* @__PURE__ */ jsx("span", { children: formatTimestamp(max, { locale }) })
    ] })
  ] });
}
function clampRange([from, to], min, max) {
  const lo = Math.max(min, Math.min(from, to));
  const hi = Math.min(max, Math.max(from, to));
  return [lo, hi];
}
function TimeRangeSlider({
  min,
  max,
  value,
  onChange,
  step = 1,
  locale,
  showLabels = true,
  emptyState = null,
  className,
  "aria-label": ariaLabel = "Time range filter"
}) {
  if (!(max > min)) return /* @__PURE__ */ jsx(Fragment, { children: emptyState });
  const [from, to] = clampRange(value, min, max);
  const isFullRange = from <= min && to >= max;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex w-full flex-col gap-1", className), children: [
    /* @__PURE__ */ jsx(
      Slider,
      {
        min,
        max,
        step,
        value: [from, to],
        onValueChange: (next) => {
          const a = next[0] ?? min;
          const b = next[1] ?? max;
          onChange(clampRange([a, b], min, max));
        },
        "aria-label": ariaLabel
      }
    ),
    showLabels && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between text-[11px] text-muted-foreground tabular-nums", children: [
      /* @__PURE__ */ jsx("span", { children: formatTimestamp(min, { locale }) }),
      /* @__PURE__ */ jsx("span", { className: cn("font-medium", isFullRange ? "text-muted-foreground" : "text-foreground"), children: isFullRange ? "Full range" : `${formatTimestamp(from, { locale })} \u2013 ${formatTimestamp(to, { locale })}` }),
      /* @__PURE__ */ jsx("span", { children: formatTimestamp(max, { locale }) })
    ] })
  ] });
}

// src/timeline/timeRange.ts
function edgeTime(edge) {
  const t = edge.data?.timestamp;
  return typeof t === "number" && Number.isFinite(t) ? t : void 0;
}
function graphTimeDomain(edges) {
  let min = Infinity;
  let max = -Infinity;
  for (const edge of edges) {
    const t = edgeTime(edge);
    if (t === void 0) continue;
    if (t < min) min = t;
    if (t > max) max = t;
  }
  if (min === Infinity) return null;
  return { min, max };
}
function filterGraphByTimeRange(nodes, edges, range, options = {}) {
  const [from, to] = range[0] <= range[1] ? range : [range[1], range[0]];
  const keptEdges = edges.filter((edge) => {
    const t = edgeTime(edge);
    if (t === void 0) return true;
    return t >= from && t <= to;
  });
  if (options.keepOrphanNodes) return { nodes, edges: keptEdges };
  const connected = /* @__PURE__ */ new Set();
  for (const edge of keptEdges) {
    connected.add(edge.source);
    connected.add(edge.target);
  }
  const keptNodes = nodes.filter((node) => connected.has(node.id));
  return { nodes: keptNodes, edges: keptEdges };
}
function useGraphTimeRange({
  nodes,
  edges,
  keepOrphanNodes
}) {
  const domain = React9.useMemo(() => graphTimeDomain(edges), [edges]);
  const domainKey = domain ? `${domain.min}:${domain.max}` : "";
  const [selection, setSelection] = React9.useState(null);
  const selected = selection && selection.key === domainKey ? selection.range : null;
  const effectiveRange = selected ?? (domain ? [domain.min, domain.max] : [0, 0]);
  const setRange = React9.useCallback(
    (next) => {
      setSelection(domainKey ? { key: domainKey, range: next } : null);
    },
    [domainKey]
  );
  const reset = React9.useCallback(() => setSelection(null), []);
  const filtered = React9.useMemo(() => {
    if (!domain) return { nodes, edges };
    return filterGraphByTimeRange(nodes, edges, effectiveRange, { keepOrphanNodes });
  }, [domain, nodes, edges, effectiveRange[0], effectiveRange[1], keepOrphanNodes]);
  const isFullRange = !domain || effectiveRange[0] <= domain.min && effectiveRange[1] >= domain.max;
  return {
    domain,
    hasTimeline: domain != null && domain.max > domain.min,
    range: effectiveRange,
    setRange,
    reset,
    isFullRange,
    nodes: filtered.nodes,
    edges: filtered.edges
  };
}
function useReplay({ edges, intervalMs = 700, autoPlay = false }) {
  const stamps = React9.useMemo(() => {
    const set = /* @__PURE__ */ new Set();
    for (const edge of edges) {
      const ts = edge.data?.timestamp;
      if (typeof ts === "number" && Number.isFinite(ts)) set.add(ts);
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [edges]);
  const hasTimeline = stamps.length > 0;
  const min = hasTimeline ? stamps[0] : 0;
  const max = hasTimeline ? stamps[stamps.length - 1] : 0;
  const [cursor, setCursorRaw] = React9.useState(max);
  const [requested, setPlaying] = React9.useState(autoPlay && hasTimeline);
  const timelineKey = `${min}:${max}:${stamps.length}`;
  if (useDepsChanged([timelineKey])) {
    setCursorRaw(max);
    setPlaying(autoPlay && stamps.length > 0);
  }
  const nextStamp = React9.useCallback(
    (from) => stamps.find((s) => s > from) ?? from,
    [stamps]
  );
  const prevStamp = React9.useCallback(
    (from) => [...stamps].reverse().find((s) => s < from) ?? from,
    [stamps]
  );
  const playing = requested && nextStamp(cursor) > cursor;
  React9.useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setCursorRaw((current) => nextStamp(current)), intervalMs);
    return () => clearInterval(id);
  }, [playing, intervalMs, nextStamp]);
  const setCursor = React9.useCallback(
    (t) => setCursorRaw(Math.max(min, Math.min(max, t))),
    [min, max]
  );
  const pause = React9.useCallback(() => setPlaying(false), []);
  const play = React9.useCallback(() => {
    if (!hasTimeline) return;
    setCursorRaw((current) => current >= max ? min : current);
    setPlaying(true);
  }, [hasTimeline, min, max]);
  const toggle = React9.useCallback(() => playing ? pause() : play(), [playing, pause, play]);
  const stepForward = React9.useCallback(() => {
    setPlaying(false);
    setCursorRaw((current) => nextStamp(current));
  }, [nextStamp]);
  const stepBackward = React9.useCallback(() => {
    setPlaying(false);
    setCursorRaw((current) => prevStamp(current));
  }, [prevStamp]);
  const reset = React9.useCallback(() => {
    setPlaying(false);
    setCursorRaw(max);
  }, [max]);
  const visibleEdges = React9.useMemo(
    () => edges.filter((edge) => edge.data?.timestamp == null || edge.data.timestamp <= cursor),
    [edges, cursor]
  );
  const visibleEdgeIds = React9.useMemo(() => new Set(visibleEdges.map((e) => e.id)), [visibleEdges]);
  const progress = max > min ? (cursor - min) / (max - min) : 1;
  return {
    min,
    max,
    cursor,
    progress,
    playing,
    hasTimeline,
    setCursor,
    play,
    pause,
    toggle,
    stepForward,
    stepBackward,
    reset,
    visibleEdges,
    visibleEdgeIds
  };
}
function ReplayControls({
  controller,
  showStep = true,
  className
}) {
  const { playing, hasTimeline, play, pause, stepForward, stepBackward, reset } = controller;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-1", className), children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "sm",
        onClick: reset,
        disabled: !hasTimeline,
        "aria-label": "Reset replay",
        title: "Reset",
        children: "\u23EE"
      }
    ),
    showStep && /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "sm",
        onClick: stepBackward,
        disabled: !hasTimeline,
        "aria-label": "Step backward",
        title: "Step back",
        children: "\u25C0"
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "sm",
        onClick: playing ? pause : play,
        disabled: !hasTimeline,
        "aria-label": playing ? "Pause replay" : "Play replay",
        title: playing ? "Pause" : "Play",
        children: playing ? "\u23F8" : "\u25B6"
      }
    ),
    showStep && /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        variant: "outline",
        size: "sm",
        onClick: stepForward,
        disabled: !hasTimeline,
        "aria-label": "Step forward",
        title: "Step forward",
        children: "\u25B6"
      }
    )
  ] });
}
function collectDescendants(parentId, provenance) {
  const direct = provenance[parentId] ?? [];
  return direct.flatMap((id) => [id, ...collectDescendants(id, provenance)]);
}
function dropErrors(errors, ids) {
  if (!ids.some((id) => id in errors)) return errors;
  const next = { ...errors };
  for (const id of ids) delete next[id];
  return next;
}
function graphExpansionReducer(state, action) {
  switch (action.type) {
    case "SET_GRAPH":
    case "RESET":
      return {
        nodes: action.nodes,
        edges: action.edges,
        provenance: {},
        expanded: [],
        loading: [],
        errors: {}
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: action.value ? state.loading.includes(action.id) ? state.loading : [...state.loading, action.id] : state.loading.filter((id) => id !== action.id)
      };
    case "FAILED": {
      const { [action.id]: _prior, ...rest } = state.errors;
      return {
        ...state,
        loading: state.loading.filter((id) => id !== action.id),
        errors: { ...rest, [action.id]: action.message }
      };
    }
    case "MERGE": {
      const nodeIds = new Set(state.nodes.map((node) => node.id));
      const edgeIds = new Set(state.edges.map((edge) => edge.id));
      const addedNodes = action.nodes.filter((node) => !nodeIds.has(node.id));
      const addedEdges = action.edges.filter((edge) => !edgeIds.has(edge.id));
      return {
        nodes: [...state.nodes, ...addedNodes],
        edges: [...state.edges, ...addedEdges],
        provenance: { ...state.provenance, [action.parentId]: addedNodes.map((node) => node.id) },
        expanded: state.expanded.includes(action.parentId) ? state.expanded : [...state.expanded, action.parentId],
        loading: state.loading.filter((id) => id !== action.parentId),
        errors: dropErrors(state.errors, [action.parentId])
      };
    }
    case "COLLAPSE": {
      const removeIds = new Set(collectDescendants(action.parentId, state.provenance));
      if (removeIds.size === 0) {
        return {
          ...state,
          expanded: state.expanded.filter((id) => id !== action.parentId),
          errors: dropErrors(state.errors, [action.parentId])
        };
      }
      const provenance = {};
      for (const [key, value] of Object.entries(state.provenance)) {
        if (key === action.parentId || removeIds.has(key)) continue;
        provenance[key] = value;
      }
      return {
        ...state,
        nodes: state.nodes.filter((node) => !removeIds.has(node.id)),
        edges: state.edges.filter((edge) => !removeIds.has(edge.source) && !removeIds.has(edge.target)),
        provenance,
        expanded: state.expanded.filter((id) => id !== action.parentId && !removeIds.has(id)),
        loading: state.loading.filter((id) => !removeIds.has(id)),
        errors: dropErrors(state.errors, [action.parentId, ...removeIds])
      };
    }
    default:
      return state;
  }
}
function useGraphReducer(options = {}) {
  const { initialNodes, initialEdges, loadNeighborhood } = options;
  const [state, dispatch] = React9.useReducer(graphExpansionReducer, void 0, () => ({
    nodes: initialNodes ?? [],
    edges: initialEdges ?? [],
    provenance: {},
    expanded: [],
    loading: [],
    errors: {}
  }));
  const inFlight = React9.useRef(/* @__PURE__ */ new Set());
  const expand = React9.useCallback(
    async (nodeId) => {
      if (!loadNeighborhood || inFlight.current.has(nodeId)) return;
      inFlight.current.add(nodeId);
      dispatch({ type: "SET_LOADING", id: nodeId, value: true });
      try {
        const neighborhood = await loadNeighborhood(nodeId);
        dispatch({ type: "MERGE", parentId: nodeId, nodes: neighborhood.nodes, edges: neighborhood.edges });
      } catch (error) {
        dispatch({
          type: "FAILED",
          id: nodeId,
          message: error instanceof Error ? error.message : String(error)
        });
        throw error;
      } finally {
        inFlight.current.delete(nodeId);
      }
    },
    [loadNeighborhood]
  );
  const collapse = React9.useCallback((nodeId) => {
    dispatch({ type: "COLLAPSE", parentId: nodeId });
  }, []);
  const expandedSet = React9.useMemo(() => new Set(state.expanded), [state.expanded]);
  const toggle = React9.useCallback(
    (nodeId) => {
      if (expandedSet.has(nodeId)) collapse(nodeId);
      else void expand(nodeId).catch(() => void 0);
    },
    [expandedSet, collapse, expand]
  );
  const setGraph = React9.useCallback((nodes, edges) => {
    dispatch({ type: "SET_GRAPH", nodes, edges });
  }, []);
  const reset = React9.useCallback(() => {
    dispatch({ type: "RESET", nodes: initialNodes ?? [], edges: initialEdges ?? [] });
  }, [initialNodes, initialEdges]);
  const loadingSet = React9.useMemo(() => new Set(state.loading), [state.loading]);
  const errorMap = React9.useMemo(() => new Map(Object.entries(state.errors)), [state.errors]);
  return {
    nodes: state.nodes,
    edges: state.edges,
    expanded: expandedSet,
    loading: loadingSet,
    errors: errorMap,
    expand,
    collapse,
    toggle,
    setGraph,
    reset
  };
}
var log2 = createLogger("tx-graph:drilldown");
function NodeDrilldownPanel({
  node,
  onClose,
  onExpand,
  onCollapse,
  expanded = false,
  loading = false,
  children,
  className
}) {
  const handleExpand = React9.useCallback(async () => {
    if (!node) return;
    try {
      if (expanded) onCollapse?.(node.id);
      else await onExpand?.(node.id);
    } catch (error) {
      log2.error("neighbourhood expand failed", { nodeId: node.id });
      notifyError(error);
    }
  }, [node, expanded, onExpand, onCollapse]);
  if (!node) return null;
  const variant = node.data.variant ?? "tx";
  return /* @__PURE__ */ jsxs(
    "aside",
    {
      className: cn(
        "flex w-80 flex-col gap-3 rounded-md border bg-card p-4 text-sm shadow-sm",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("header", { className: "flex items-start justify-between gap-2", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
            /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "w-fit font-normal", children: GRAPH_NODE_VARIANT_LABELS[variant] }),
            /* @__PURE__ */ jsx("h3", { className: "font-medium leading-tight", children: node.data.label }),
            node.data.subtitle && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: node.data.subtitle })
          ] }),
          onClose && /* @__PURE__ */ jsx(
            Button,
            {
              type: "button",
              variant: "ghost",
              size: "sm",
              onClick: onClose,
              "aria-label": "Close details",
              title: "Close",
              children: "\u2715"
            }
          )
        ] }),
        node.data.chain && /* @__PURE__ */ jsx(ChainAvatar, { chain: node.data.chain, size: "sm" }),
        (onExpand || onCollapse) && /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            variant: "outline",
            size: "sm",
            className: "w-full",
            onClick: handleExpand,
            disabled: loading,
            children: loading ? "Loading\u2026" : expanded ? "Collapse neighbourhood" : "Expand neighbourhood"
          }
        ),
        children
      ]
    }
  );
}

// src/share/dynamicSeed.ts
var GRAPH_DYNAMIC_SEED_KIND = "tx-graph";
function asStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((v) => v != null).map((v) => String(v));
}
function asRecord(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}
function firstDefined(...values) {
  for (const v of values) if (v !== void 0 && v !== null) return v;
  return void 0;
}
function normalizeDynamicSeed(raw) {
  if (!raw || typeof raw !== "object") return null;
  const r = raw;
  const rootIds = asStringArray(firstDefined(r.root_ids, r.rootIds));
  const expandedIds = asStringArray(firstDefined(r.expanded_ids, r.expandedIds, r.expanded));
  const chainIds = asStringArray(firstDefined(r.chain_ids, r.chainIds));
  const filters = asRecord(firstDefined(r.filters));
  const grouping = firstDefined(r.grouping);
  const hiddenPanels = asStringArray(
    firstDefined(r.hidden_panels, r.hiddenPanels)
  );
  const rawChannel = firstDefined(r.stream_channel, r.streamChannel);
  const rawRange = firstDefined(r.time_range, r.timeRange);
  if (!rootIds.length && !expandedIds.length && !rawChannel && !r.resource) return null;
  const streamChannel = rawChannel ? {
    id: rawChannel.id != null ? String(rawChannel.id) : void 0,
    kind: rawChannel.kind != null ? String(rawChannel.kind) : void 0,
    params: asRecord(rawChannel.params)
  } : null;
  return {
    kind: r.kind != null ? String(r.kind) : GRAPH_DYNAMIC_SEED_KIND,
    resource: r.resource != null ? String(r.resource) : null,
    rootIds,
    expandedIds,
    chainIds,
    viewMode: firstDefined(r.view_mode, r.viewMode) ?? null,
    engine: firstDefined(r.engine) ?? null,
    expandDepth: Number(firstDefined(r.expand_depth, r.expandDepth) ?? 1) || 1,
    timeRange: rawRange ? { start: rawRange.start ?? null, end: rawRange.end ?? null } : null,
    filters,
    streamChannel,
    detailTier: firstDefined(r.detail_tier, r.detailTier) ?? null,
    grouping: grouping ? grouping : null,
    search: firstDefined(r.search) != null ? String(firstDefined(r.search)) : null,
    hiddenPanels
  };
}
function dynamicSeedToChannel(seed) {
  const channel = seed?.streamChannel;
  if (!channel) return null;
  const id = channel.id ?? channel.kind;
  if (!id) return null;
  const variables = {
    ...channel.kind ? { kind: channel.kind } : {},
    ...channel.params ?? {}
  };
  return { id, ...Object.keys(variables).length ? { variables } : {} };
}

// src/share/viewServing.ts
function defaultStreamChannel(rootIds) {
  if (!rootIds.length) return null;
  return { kind: "graph", params: { rootIds } };
}
function buildGraphViewServing(state, options = {}) {
  const {
    dataMode = "static",
    payloadVisibility,
    catalogResolution = "viewer",
    resource = GRAPH_DYNAMIC_SEED_KIND,
    streamChannel = defaultStreamChannel,
    frozenAt = Date.now()
  } = options;
  if (!state) {
    return {
      dataMode,
      payloadVisibility,
      catalogResolution,
      catalogSnapshot: null,
      dynamicSeed: null,
      snapshot: null
    };
  }
  const nodes = Array.isArray(state.nodes) ? state.nodes : [];
  const edges = Array.isArray(state.edges) ? state.edges : [];
  const rootIds = Array.isArray(state.rootIds) ? state.rootIds.map(String) : [];
  const expandedIds = Array.isArray(state.expanded) ? state.expanded.map(String) : [];
  const descriptor = compactGraphState(state);
  const snapshot = state.scene ? { ...descriptor, scene: state.scene, frozenAt } : nodes.length || edges.length ? { ...descriptor, nodes, edges, frozenAt } : null;
  let dynamicSeed = null;
  if (rootIds.length || expandedIds.length) {
    const channel = streamChannel(rootIds);
    dynamicSeed = {
      kind: GRAPH_DYNAMIC_SEED_KIND,
      resource: state.resource ?? resource,
      root_ids: rootIds,
      expanded_ids: expandedIds,
      view_mode: state.viewMode ?? null,
      engine: state.engine ?? null,
      filters: state.filters ?? {},
      // The client-side view state travels with the recipe, not just with the
      // snapshot. A scene is a superset the client filters, bands and searches,
      // so a dynamic link that replays only the fetch reopens at Compact,
      // ungrouped and unsearched — the same saved view showing a different graph
      // depending on which way the link happened to serve it. `GraphShareState`
      // spells out why each of these has to survive; the snapshot honoured that
      // and the seed did not.
      detail_tier: state.detailTier ?? null,
      grouping: state.grouping ?? null,
      search: state.search ?? null,
      hidden_panels: state.hiddenPanels ?? [],
      ...channel ? { stream_channel: channel } : {}
    };
  }
  return {
    dataMode,
    payloadVisibility,
    catalogResolution,
    catalogSnapshot: state.catalog ?? null,
    dynamicSeed,
    snapshot
  };
}
var LiveGraphLayer2 = React9.lazy(() => Promise.resolve().then(() => (init_LiveGraphLayer(), LiveGraphLayer_exports)));
function readViewState(source) {
  if (!source || typeof source !== "object") return {};
  const viewMode = source.viewMode ?? source.view_mode;
  const engine = source.engine;
  const detailTier = source.detailTier ?? source.detail_tier;
  const hiddenPanels = source.hiddenPanels ?? source.hidden_panels;
  const search = source.search;
  const grouping = source.grouping;
  const frozenAt = Number(source.frozenAt ?? source.frozen_at);
  return {
    viewMode: typeof viewMode === "string" ? viewMode : void 0,
    engine: typeof engine === "string" ? engine : void 0,
    detailTier: typeof detailTier === "string" ? detailTier : void 0,
    grouping: grouping && typeof grouping === "object" ? grouping : void 0,
    search: typeof search === "string" && search ? search : void 0,
    hiddenPanels: Array.isArray(hiddenPanels) ? hiddenPanels : void 0,
    frozenAt: Number.isFinite(frozenAt) && frozenAt > 0 ? frozenAt : void 0
  };
}
function readSnapshot(snap) {
  if (!snap || typeof snap !== "object") return { nodes: [], edges: [], view: {} };
  const s = snap;
  const view = readViewState(s);
  if (s.scene && typeof s.scene === "object") {
    return { nodes: [], edges: [], scene: s.scene, view };
  }
  return {
    nodes: Array.isArray(s.nodes) ? s.nodes : [],
    edges: Array.isArray(s.edges) ? s.edges : [],
    view
  };
}
function describeUnservableShare(input) {
  if (input.dataMode !== "dynamic") return null;
  if (!input.hasSeed) {
    return "This link shares a live view, but it carries no re-fetch recipe \u2014 it may have been created before the view had one. Ask the owner to re-share it as a snapshot.";
  }
  if (!input.hasResolver && !input.hasChannel) {
    return "This link shares a live view, and this viewer isn't wired to fetch live graph data. Ask the owner for a snapshot link instead.";
  }
  return null;
}
function defaultParseLive(message) {
  const payload = message.payload;
  if (!payload || typeof payload !== "object") return null;
  const nodes = Array.isArray(payload.nodes) ? payload.nodes : [];
  const edges = Array.isArray(payload.edges) ? payload.edges : [];
  if (!nodes.length && !edges.length) return null;
  return { nodes, edges };
}
function SharedGraphView({
  dataMode,
  snapshot,
  dynamicSeed,
  seedResolver,
  parseLiveMessage,
  live = true,
  catalog,
  showExport,
  viewMode,
  className,
  loadingSlot,
  emptySlot,
  errorSlot
}) {
  const seed = React9.useMemo(() => {
    if (!dynamicSeed) return null;
    if (Array.isArray(dynamicSeed.rootIds)) {
      return dynamicSeed;
    }
    return normalizeDynamicSeed(dynamicSeed);
  }, [dynamicSeed]);
  const staticGraph = React9.useMemo(
    () => dataMode === "static" ? readSnapshot(snapshot) : { nodes: [], edges: [], view: {} },
    [dataMode, snapshot]
  );
  const view = dataMode === "static" ? staticGraph.view : readViewState(seed);
  const [nodes, setNodes] = React9.useState(staticGraph.nodes);
  const [edges, setEdges] = React9.useState(staticGraph.edges);
  const [scene, setScene] = React9.useState(staticGraph.scene);
  const [fetching, setFetching] = React9.useState(dataMode === "dynamic");
  const [fetchError, setFetchError] = React9.useState(void 0);
  const nodesRef = React9.useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = React9.useRef(edges);
  edgesRef.current = edges;
  React9.useEffect(() => {
    if (dataMode === "static") {
      setNodes(staticGraph.nodes);
      setEdges(staticGraph.edges);
      setScene(staticGraph.scene);
      setFetching(false);
    }
  }, [dataMode, staticGraph]);
  React9.useEffect(() => {
    if (dataMode !== "dynamic" || !seed || !seedResolver) {
      if (dataMode === "dynamic") setFetching(false);
      return;
    }
    const controller = new AbortController();
    let active = true;
    setFetching(true);
    setFetchError(void 0);
    seedResolver(seed, { signal: controller.signal }).then((snap) => {
      if (!active) return;
      setNodes(snap.nodes ?? []);
      setEdges(snap.edges ?? []);
      setScene(snap.scene);
    }).catch((err) => {
      if (!active) return;
      setFetchError(err instanceof Error ? err.message : String(err));
    }).finally(() => {
      if (active) setFetching(false);
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [dataMode, seed, seedResolver]);
  const channel = React9.useMemo(
    () => dataMode === "dynamic" && live ? dynamicSeedToChannel(seed) : null,
    [dataMode, live, seed]
  );
  const resolveOverlay = React9.useMemo(() => catalogResolverFrom(catalog), [catalog]);
  const canvasContainerRef = React9.useRef(null);
  const onMerge = React9.useCallback((merged) => {
    setNodes(merged.nodes);
    setEdges(merged.edges);
  }, []);
  const effectiveViewMode = viewMode ?? seed?.viewMode ?? void 0;
  if (fetching) {
    return /* @__PURE__ */ jsx(Fragment, { children: loadingSlot ?? null });
  }
  if (fetchError) {
    return /* @__PURE__ */ jsx(Fragment, { children: errorSlot ? errorSlot(fetchError) : null });
  }
  const unservable = describeUnservableShare({
    dataMode,
    hasSeed: Boolean(seed),
    hasResolver: Boolean(seedResolver),
    hasChannel: Boolean(channel)
  });
  if (unservable) {
    return /* @__PURE__ */ jsx(Fragment, { children: errorSlot ? errorSlot(unservable) : emptySlot ?? null });
  }
  if (scene) {
    return /* @__PURE__ */ jsx(
      SceneGraphView,
      {
        scene,
        resolveOverlay,
        viewMode,
        defaultViewMode: view.viewMode,
        defaultDetailTier: view.detailTier,
        defaultGrouping: view.grouping,
        defaultHiddenPanels: view.hiddenPanels,
        defaultSearchQuery: view.search,
        ...showExport === void 0 ? {} : { showExport },
        className
      }
    );
  }
  const liveProps = channel ? {
    channel,
    nodesRef,
    edgesRef,
    parse: parseLiveMessage ?? defaultParseLive,
    onMerge
  } : null;
  const liveLayer = liveProps ? /* @__PURE__ */ jsx(React9.Suspense, { fallback: null, children: /* @__PURE__ */ jsx(LiveGraphLayer2, { ...liveProps }) }) : null;
  if (!nodes.length && !edges.length) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      emptySlot ?? null,
      liveLayer
    ] });
  }
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      GraphCanvas,
      {
        containerRef: canvasContainerRef,
        nodes,
        edges,
        viewMode: effectiveViewMode,
        engine: view.engine,
        showViewModeToggle: true,
        showFullscreen: true,
        showRelayout: true,
        searchQuery: view.search,
        fitView: true,
        className,
        extraToolbar: showExport === false ? void 0 : /* @__PURE__ */ jsx(
          GraphExportMenu,
          {
            graph: { nodes, edges },
            containerRef: canvasContainerRef,
            filenameBase: "shared-graph"
          }
        )
      }
    ),
    liveLayer
  ] });
}
function ShareHeading({
  name,
  frozenAt
}) {
  if (!name && !frozenAt) return null;
  return /* @__PURE__ */ jsxs("div", { className: "mb-3 flex flex-wrap items-baseline gap-x-2 gap-y-1", children: [
    name ? /* @__PURE__ */ jsx("h2", { className: "text-base font-semibold", children: name }) : null,
    frozenAt ? /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
      "Snapshot frozen ",
      new Date(frozenAt).toLocaleString()
    ] }) : null
  ] });
}
function viewName(view) {
  const name = view?.name;
  return typeof name === "string" && name.trim() ? name.trim() : void 0;
}
function frozenAtOf(snapshot) {
  const raw = Number(snapshot?.frozenAt);
  return Number.isFinite(raw) && raw > 0 ? raw : void 0;
}
function PasswordGate({
  onSubmit,
  error
}) {
  const [value, setValue] = React9.useState("");
  return /* @__PURE__ */ jsxs(
    "form",
    {
      className: "mx-auto flex w-full max-w-sm flex-col gap-3 py-10",
      onSubmit: (e) => {
        e.preventDefault();
        if (value) onSubmit(value);
      },
      children: [
        /* @__PURE__ */ jsx(Label, { htmlFor: "share-password", children: "This shared view is password-protected" }),
        /* @__PURE__ */ jsx(
          Input,
          {
            id: "share-password",
            type: "password",
            autoFocus: true,
            value,
            onChange: (e) => setValue(e.target.value),
            placeholder: "Enter password"
          }
        ),
        error ? /* @__PURE__ */ jsx("p", { className: "text-sm text-destructive", children: error }) : null,
        /* @__PURE__ */ jsx(Button, { type: "submit", disabled: !value, children: "View" })
      ]
    }
  );
}
function GraphShareViewer({
  source,
  token,
  seedResolver,
  parseLiveMessage,
  live = true,
  viewMode,
  className,
  onAuthRequired
}) {
  const { status, resolved, snapshot, snapshotError, warnings, error, errorCode, submitPassword } = useResolveShare({ source, token });
  if (!token) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No share link", description: "This page needs a share token." });
  }
  switch (status) {
    case "idle":
    case "loading":
      return /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-16", children: /* @__PURE__ */ jsx(Spinner, { label: "Opening shared view" }) });
    case "password-required":
      return /* @__PURE__ */ jsx(PasswordGate, { onSubmit: submitPassword, error });
    case "auth-required":
      return /* @__PURE__ */ jsx(Fragment, { children: onAuthRequired ? onAuthRequired() : /* @__PURE__ */ jsx(
        EmptyState,
        {
          title: "Sign in to view",
          description: "This shared view is limited to specific people. Sign in to open it."
        }
      ) });
    case "forbidden":
      return /* @__PURE__ */ jsx(
        EmptyState,
        {
          title: "No access",
          description: "You don't have access to this shared view."
        }
      );
    case "not-found":
      return /* @__PURE__ */ jsx(
        EmptyState,
        {
          title: "Link not found",
          description: "This share link doesn't exist or was revoked."
        }
      );
    case "expired":
      return /* @__PURE__ */ jsx(
        EmptyState,
        {
          title: "Link expired",
          description: "This share link has expired or reached its view limit."
        }
      );
    case "error":
      return /* @__PURE__ */ jsx(ErrorState, { error: error ?? errorCode ?? "Failed to open the shared view" });
    case "ready":
    default:
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(ShareHeading, { name: viewName(resolved?.view), frozenAt: frozenAtOf(snapshot) }),
        warnings.length ? /* @__PURE__ */ jsx(AccessWarningBanner, { warnings, className: "mb-3" }) : null,
        /* @__PURE__ */ jsx("div", { className, children: /* @__PURE__ */ jsx(
          SharedGraphView,
          {
            dataMode: resolved?.dataMode ?? "static",
            snapshot,
            dynamicSeed: resolved?.dynamicSeed ?? null,
            catalog: resolved?.catalog ?? null,
            seedResolver,
            parseLiveMessage,
            live,
            viewMode,
            loadingSlot: /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-16", children: /* @__PURE__ */ jsx(Spinner, { label: "Loading live graph" }) }),
            emptySlot: (
              // A snapshot that could not be FETCHED is not an empty graph.
              // `snapshotError` names the difference (expired presign, 403,
              // never written); without it every one of those told the reader
              // the view had no data, which is the one thing it did not say.
              /* @__PURE__ */ jsx(
                EmptyState,
                {
                  title: snapshotError ? "Couldn't load this view" : "Empty graph",
                  description: snapshotError ?? "This shared view has no data."
                }
              )
            ),
            errorSlot: (e) => /* @__PURE__ */ jsx(ErrorState, { error: e })
          }
        ) })
      ] });
  }
}

// src/index.ts
init_live();
function toNode2(view, options) {
  const short = view.address ? shortenAddress(view.address) : "";
  const subtitle = view.address && view.label !== short ? short : `${view.txCount} tx`;
  const kind = (view.address ? options.resolveKind?.(view.address) : void 0) ?? domainKindForSceneKind(view.nodeType);
  return txNode(view.id, {
    label: view.label,
    subtitle,
    chain: view.chainId,
    kind,
    // Catalog overlay brand/attribution (from the host's resolveOverlay seam):
    // the winning logo, brand tags, and the primary entity id (which also seeds
    // the in-graph assignment menu's "currently mapped entity").
    icon: view.logo,
    tags: view.tags,
    entityId: view.primaryEntityId
  });
}
function toEdge2(view, options) {
  const demix = options.isDemix?.(view) ?? false;
  return txEdge(view.id, view.from, view.to, {
    variant: edgeVariantForKind(view.kind),
    amount: view.amountDisplay,
    ticker: view.symbol,
    valueUsd: view.valueUsd,
    demix
  });
}
function toGraph(graph, options = {}) {
  const view = toGraphView(graph, options);
  return {
    nodes: view.nodes.map((n) => toNode2(n, options)),
    edges: view.edges.map((e) => toEdge2(e, options))
  };
}
function previewGraph(rows, options = {}) {
  const nodeIds = /* @__PURE__ */ new Set();
  const nodes = [];
  const edges = [];
  const addNode = (address) => {
    if (!address || nodeIds.has(address)) return;
    nodeIds.add(address);
    nodes.push(
      txNode(address, {
        label: options.resolveLabel?.(address) ?? shortenAddress(address),
        subtitle: shortenAddress(address),
        chain: options.chainId
      })
    );
  };
  const seenEdges = /* @__PURE__ */ new Set();
  for (const row of rows) {
    addNode(row.from_address);
    addNode(row.to_address);
    if (!row.from_address || !row.to_address) continue;
    const id = `${row.from_address}->${row.to_address}`;
    if (seenEdges.has(id)) continue;
    seenEdges.add(id);
    edges.push(
      txEdge(id, row.from_address, row.to_address, {
        variant: "default",
        dashed: true,
        timestamp: row.block_timestamp
      })
    );
  }
  return { nodes, edges };
}
function graphNodeId(chainId, address) {
  const addr = normalizeAddress(address, chainId || null);
  return chainId ? `${chainId}#${addr}` : addr;
}
function text(value) {
  return value == null ? "" : String(value);
}
function endpoints(row) {
  const summary = row.participant_summary ?? {};
  return {
    from: text(summary.primary_from) || text(row.from),
    to: text(summary.primary_to) || text(row.to)
  };
}
function rowNodeIds(row) {
  const { from, to } = endpoints(row);
  if (!from || !to) return [];
  const chainId = text(row.chain_id);
  const fromId = graphNodeId(chainId, from);
  const toId = graphNodeId(chainId, to);
  return fromId === toId ? [fromId] : [fromId, toId];
}
function toBigInt(value) {
  if (value == null || value === "") return 0n;
  try {
    return BigInt(typeof value === "number" ? Math.trunc(value) : String(value).trim());
  } catch {
    return 0n;
  }
}
function interactionGraphFromRows(rows) {
  const nodes = /* @__PURE__ */ new Map();
  const edges = /* @__PURE__ */ new Map();
  const chains = /* @__PURE__ */ new Set();
  const touchNode = (address, chainId, txHash) => {
    const id = graphNodeId(chainId, address);
    let node = nodes.get(id);
    if (!node) {
      node = { id, address, chainId: chainId || null, degree: 0, txHashes: /* @__PURE__ */ new Set() };
      nodes.set(id, node);
    }
    node.degree += 1;
    if (txHash) node.txHashes.add(txHash);
    return id;
  };
  for (const row of rows) {
    const { from, to } = endpoints(row);
    if (!from || !to) continue;
    const chainId = text(row.chain_id);
    if (chainId) chains.add(chainId);
    const txHash = text(row.tx_hash) || text(row.canonical_tx_id);
    const fromId = touchNode(from, chainId, txHash);
    const toId = touchNode(to, chainId, txHash);
    const key = `${fromId}\0${toId}\0native`;
    let edge = edges.get(key);
    if (!edge) {
      edge = {
        fromAddress: from,
        toAddress: to,
        fromId,
        toId,
        chainId: chainId || null,
        interactionCount: 0,
        rawAmountTotal: 0n
      };
      edges.set(key, edge);
    }
    edge.interactionCount += 1;
    edge.rawAmountTotal += toBigInt(row.total_value_raw);
  }
  const nodeList = [...nodes.values()].sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0).map(
    (node) => ({
      id: node.id,
      node_type: "address",
      address: node.address,
      chain_id: node.chainId,
      label: null,
      degree: node.degree,
      tx_count: node.txHashes.size
    })
  );
  const edgeList = [...edges.values()].sort(
    (a, b) => a.fromId < b.fromId ? -1 : a.fromId > b.fromId ? 1 : a.toId < b.toId ? -1 : a.toId > b.toId ? 1 : 0
  ).map(
    (edge) => ({
      from_address: edge.fromAddress,
      to_address: edge.toAddress,
      from_id: edge.fromId,
      to_id: edge.toId,
      from_chain: edge.chainId,
      to_chain: edge.chainId,
      token_address: null,
      token_chain_id: null,
      kind: "native",
      interaction_count: edge.interactionCount,
      raw_amount_total: edge.rawAmountTotal.toString()
    })
  );
  const chainIds = [...chains].sort();
  return {
    nodes: nodeList,
    edges: edgeList,
    node_count: nodeList.length,
    edge_count: edgeList.length,
    chain_ids: chainIds,
    multi_chain: chainIds.length > 1
  };
}
function emptyScene(options = {}) {
  const { kind = "semantic", layoutHint = "hierarchical" } = options;
  return {
    version: "1",
    kind,
    summary: null,
    nodes: [],
    edges: [],
    groups: [],
    highlights: [],
    chain_ids: [],
    multi_chain: false,
    layout_hint: layoutHint,
    default_detail_tier: "compact",
    attributes: {}
  };
}
function unionMembers(existing, next) {
  const members = [...existing.member_node_ids];
  const seen = new Set(members);
  for (const member of next.member_node_ids) {
    if (!seen.has(member)) {
      members.push(member);
      seen.add(member);
    }
  }
  return { ...existing, member_node_ids: members };
}
function usdOf(asset) {
  if (asset?.usd == null || asset.usd === "") return void 0;
  const parsed = Number(asset.usd);
  return Number.isFinite(parsed) ? parsed : void 0;
}
function accumulateEdge(existing, next) {
  const seen = existing.attributes[MERGED_COUNT_ATTR];
  const count = (typeof seen === "number" ? seen : 1) + 1;
  const asset = existing.asset ?? next.asset ?? null;
  let merged = asset;
  if (asset) {
    const raw = sumAmounts(
      [existing.asset?.amount_raw, next.asset?.amount_raw].filter(
        (v) => typeof v === "string" && v !== ""
      )
    );
    const decimals = existing.asset?.decimals ?? next.asset?.decimals ?? null;
    const usdParts = [usdOf(existing.asset), usdOf(next.asset)].filter(
      (v) => v !== void 0
    );
    merged = {
      ...asset,
      amount_raw: raw.toString(),
      amount: decimals != null ? formatTokenDisplay(raw, decimals) : null,
      usd: usdParts.length > 0 ? String(usdParts.reduce((a, b) => a + b, 0)) : null,
      // A quoted floor belongs to the swap that quoted it; summing floors across
      // transactions would invent a guarantee nobody gave.
      min_amount_raw: null,
      min_amount: null
    };
  }
  const { netted: _netted, net_zero: _netZero, ...attributes } = existing.attributes;
  return {
    ...existing,
    // An arrow that stands for at least one OBSERVED movement is observed, even
    // when an inferred leg folded into it.
    origin: existing.origin === "decoded" || next.origin === "decoded" ? "decoded" : existing.origin,
    label: existing.label && existing.label === next.label ? existing.label : null,
    facts: [],
    asset: merged,
    attributes: { ...attributes, [MERGED_COUNT_ATTR]: count }
  };
}
function mergeScenes(scenes, options = {}) {
  const first = scenes[0];
  if (!first) return emptyScene(options);
  if (scenes.length === 1) return first;
  const { kind = "semantic", layoutHint = "hierarchical", duplicateEdges = "first" } = options;
  const nodesById = /* @__PURE__ */ new Map();
  const edgesById = /* @__PURE__ */ new Map();
  const groupsById = /* @__PURE__ */ new Map();
  const summaries = [];
  for (const scene of scenes) {
    for (const node of scene.nodes) if (!nodesById.has(node.id)) nodesById.set(node.id, node);
    for (const edge of scene.edges) {
      const existing = edgesById.get(edge.id);
      if (!existing) edgesById.set(edge.id, edge);
      else if (duplicateEdges === "accumulate") {
        edgesById.set(edge.id, accumulateEdge(existing, edge));
      }
    }
    for (const group of scene.groups) {
      const existing = groupsById.get(group.id);
      groupsById.set(group.id, existing ? unionMembers(existing, group) : group);
    }
    if (scene.summary) summaries.push(scene.summary);
  }
  const nodes = [...nodesById.values()];
  const chainIds = [
    ...new Set(nodes.filter((n) => n.chain_id && !n.off_chain).map((n) => n.chain_id))
  ].sort();
  return {
    ...emptyScene({ kind, layoutHint }),
    summary: summaries.length > 0 ? summaries.join("; ") : null,
    nodes,
    edges: [...edgesById.values()],
    groups: [...groupsById.values()],
    chain_ids: chainIds,
    multi_chain: chainIds.length > 1
  };
}
var EDGE_KIND = {
  native: "transfer",
  erc20: "transfer",
  erc721: "transfer",
  erc1155: "transfer",
  internal: "call",
  bridge_in: "bridge_in",
  bridge_out: "bridge_out",
  cross_chain: "cross_chain"
};
var VIRTUAL_EDGE_KINDS = /* @__PURE__ */ new Set(["bridge_out", "bridge_in", "cross_chain"]);
function endpoint(id, address) {
  return id || address || "";
}
function nativeToken(chainId) {
  if (!chainId) return {};
  const chain = resolveChain(chainId);
  if (!chain) return {};
  return { symbol: chain.nativeToken, decimals: chain.decimals };
}
function humanAmount(raw, decimals) {
  if (decimals == null || raw == null || raw === "") return void 0;
  try {
    return formatUnits(raw, decimals);
  } catch {
    return void 0;
  }
}
function sceneNode(node, defaultChainId) {
  const address = node.address || null;
  const chainId = node.chain_id ?? defaultChainId ?? null;
  const txCount = node.tx_count ?? 0;
  return {
    id: endpoint(node.id, address),
    kind: node.node_type || "address",
    label: node.label || address || "",
    // `"<n> tx"` is the coarse tier's only extra signal — how many of the
    // transactions in scope touched this address. The scene renderer keeps an
    // explicit sublabel over its own short-address derivation.
    sublabel: txCount > 0 ? `${txCount} tx` : null,
    chain_id: chainId,
    address,
    group_id: null,
    off_chain: false,
    entity_id: null,
    icon: null,
    assets: [],
    value: null,
    badge: null,
    // Counters aren't part of the scene contract, but dropping them would lose
    // the aggregate's only quantitative content — keep them addressable.
    attributes: { degree: node.degree ?? 0, tx_count: txCount },
    source_refs: [],
    confidence: 1,
    detail_tier: "compact",
    facts: [],
    // Observed: every edge here comes from a real transaction in the feed.
    origin: "decoded"
  };
}
function sceneEdge(edge, defaultChainId, options) {
  const source = endpoint(edge.from_id, edge.from_address);
  const target = endpoint(edge.to_id, edge.to_address);
  const rawKind = edge.kind ?? "";
  const token = edge.token_address ?? "native";
  const tokenChain = edge.token_chain_id ?? edge.from_chain ?? defaultChainId ?? void 0;
  const native = edge.token_address ? {} : nativeToken(tokenChain);
  const symbol = options.resolveSymbol?.(edge.token_address, tokenChain) ?? native.symbol;
  const decimals = options.resolveDecimals?.(edge.token_address, tokenChain) ?? native.decimals;
  const hasAsset = Boolean(edge.token_address) || edge.raw_amount_total != null && edge.raw_amount_total !== "0";
  const asset = hasAsset ? {
    token_address: edge.token_address ?? null,
    token_chain_id: edge.token_chain_id ?? null,
    symbol: symbol ?? null,
    decimals: decimals ?? null,
    amount_raw: edge.raw_amount_total ?? null,
    amount: humanAmount(edge.raw_amount_total, decimals) ?? null
  } : null;
  const kind = EDGE_KIND[rawKind] ?? "flow";
  const virtual = VIRTUAL_EDGE_KINDS.has(kind);
  return {
    id: `${source}->${target}:${token}:${rawKind}`,
    source,
    target,
    kind,
    label: null,
    asset,
    direction: null,
    from_chain: edge.from_chain ?? null,
    to_chain: edge.to_chain ?? null,
    cross_chain: Boolean(edge.from_chain && edge.to_chain && edge.from_chain !== edge.to_chain) || virtual,
    virtual,
    icon: null,
    attributes: rawKind ? { flow_kind: rawKind, interaction_count: edge.interaction_count ?? 0 } : { interaction_count: edge.interaction_count ?? 0 },
    detail_tier: "compact",
    facts: [],
    origin: virtual ? "synthesized" : "decoded"
  };
}
function interactionGraphToScene(graph, options = {}) {
  const { kind = "interaction", layoutHint = "hierarchical", chainId } = options;
  const base = emptyScene({ kind, layoutHint });
  if (!graph) return base;
  const nodes = (graph.nodes ?? []).map((n) => sceneNode(n, chainId));
  const edges = (graph.edges ?? []).map((e) => sceneEdge(e, chainId, options));
  const chainIds = [...new Set(nodes.filter((n) => n.chain_id).map((n) => n.chain_id))].sort();
  return {
    ...base,
    nodes,
    edges,
    chain_ids: chainIds,
    multi_chain: chainIds.length > 1
  };
}

// src/transpose/chainGroups.ts
var BY_CHAIN = { dimensions: ["chain"] };
function groupNodesByChain(nodes) {
  const grouped = applyGrouping({ ...emptyScene(), nodes: [...nodes] }, BY_CHAIN);
  return { nodes: grouped.nodes, groups: grouped.groups };
}
function regroupSceneByChain(scene) {
  const grouped = applyGrouping(scene, BY_CHAIN);
  const preserved = scene.groups.filter((group) => group.group_type !== "chain");
  return preserved.length === 0 ? grouped : { ...grouped, groups: [...preserved, ...grouped.groups] };
}

// src/transpose/sagaGroups.ts
var SAGA_GROUP_TYPE = "saga";
function sagaGroupId(key) {
  return `saga:${key}`;
}
function sagaGhostNodeId(key, role) {
  return `saga-ghost:${key}:${role.toLowerCase()}`;
}
function ghostNode(band, role) {
  return {
    id: sagaGhostNodeId(band.key, role),
    kind: "virtual",
    label: `${role} leg`,
    sublabel: band.confirmed ? "not observed yet" : "not in this view",
    chain_id: band.expectedChainId ?? null,
    address: null,
    group_id: null,
    off_chain: true,
    entity_id: null,
    icon: null,
    assets: [],
    value: null,
    badge: "expected",
    attributes: {
      [SAGA_KEY_ATTR]: band.key,
      [SAGA_LABEL_ATTR]: band.label,
      [SAGA_AWAITING_ATTR]: true,
      saga_role: role,
      saga_confirmed: Boolean(band.confirmed)
    },
    source_refs: [],
    // Not observed: an inference from the protocol's declared completion set, so
    // it must not read as evidence of a transaction.
    confidence: 0,
    // Only at the expanded tier: a compact view is the case's headline, and a leg
    // that doesn't exist yet isn't it.
    detail_tier: "standard",
    facts: [],
    origin: "synthesized"
  };
}
function ghostEdge(band, role, anchorNodeId) {
  return {
    id: `saga-ghost-edge:${band.key}:${role.toLowerCase()}`,
    source: anchorNodeId,
    target: sagaGhostNodeId(band.key, role),
    kind: "cross_chain",
    label: band.confirmed ? `awaiting ${role.toLowerCase()}` : `${role.toLowerCase()} not in view`,
    asset: null,
    direction: null,
    from_chain: null,
    to_chain: band.expectedChainId ?? null,
    cross_chain: true,
    virtual: true,
    icon: null,
    attributes: { saga_key: band.key, saga_role: role },
    detail_tier: "standard",
    facts: [],
    origin: "synthesized"
  };
}
function sagaAnchorNodeId(memberNodeIds, nodesById) {
  for (const id of memberNodeIds) {
    if (nodesById.get(id)?.off_chain) return id;
  }
  return memberNodeIds[0];
}
function applySagaBands(scene, bands) {
  if (bands.length === 0) return scene;
  const nodesById = new Map(scene.nodes.map((node) => [node.id, node]));
  const claimed = /* @__PURE__ */ new Map();
  const nodes = [];
  const edges = [];
  for (const band of bands) {
    const members = band.memberNodeIds.filter((id) => nodesById.has(id));
    if (members.length === 0) continue;
    for (const id of members) {
      if (!claimed.has(id)) claimed.set(id, band);
    }
    const missing = band.missingRoles ?? [];
    const anchor = band.anchorNodeId ?? sagaAnchorNodeId(members, nodesById);
    if (anchor) {
      for (const role of missing) {
        const ghost = ghostNode(band, role);
        if (nodesById.has(ghost.id)) continue;
        nodesById.set(ghost.id, ghost);
        nodes.push(ghost);
        edges.push(ghostEdge(band, role, anchor));
        claimed.set(ghost.id, band);
      }
    }
  }
  if (claimed.size === 0) return scene;
  const awaiting = new Set(
    bands.filter((band) => (band.missingRoles?.length ?? 0) > 0).map((band) => band.key)
  );
  const marked = [...scene.nodes, ...nodes].map((node) => {
    const band = claimed.get(node.id);
    if (!band) return node;
    return {
      ...node,
      attributes: {
        ...node.attributes,
        [SAGA_KEY_ATTR]: band.key,
        [SAGA_LABEL_ATTR]: band.label,
        [SAGA_AWAITING_ATTR]: awaiting.has(band.key)
      }
    };
  });
  return {
    ...scene,
    nodes: marked,
    edges: edges.length > 0 ? [...scene.edges, ...edges] : scene.edges
  };
}

// src/transpose/mergeElements.ts
function dedupeById(primary, secondary) {
  const out = [...primary];
  const seen = new Set(out.map((item) => item.id));
  for (const item of secondary) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}
function mergeGraphElements(primary, secondary) {
  return {
    nodes: dedupeById(primary.nodes, secondary.nodes),
    edges: dedupeById(primary.edges, secondary.edges)
  };
}
var FINALITY_FORWARD = [
  FinalityState.PENDING,
  FinalityState.CONFIRMED,
  FinalityState.FINALIZED
];
var FINALITY_STATES = [...FINALITY_FORWARD, FinalityState.REORGED];
var FINALITY_TRANSITIONS = [
  { from: FinalityState.PENDING, to: FinalityState.CONFIRMED, event: "included" },
  { from: FinalityState.CONFIRMED, to: FinalityState.FINALIZED, event: "depth-reached" },
  { from: FinalityState.CONFIRMED, to: FinalityState.REORGED, event: "reorg", reorg: true },
  { from: FinalityState.PENDING, to: FinalityState.REORGED, event: "dropped", reorg: true },
  { from: FinalityState.REORGED, to: FinalityState.PENDING, event: "replay" },
  { from: FinalityState.REORGED, to: FinalityState.CONFIRMED, event: "re-included" }
];
function transitionsFrom(state) {
  return FINALITY_TRANSITIONS.filter((t) => t.from === state);
}
function nextStates(state) {
  return transitionsFrom(state).map((t) => t.to);
}
function canTransition(from, to) {
  return FINALITY_TRANSITIONS.some((t) => t.from === from && t.to === to);
}
function isTerminal(state) {
  return state === FinalityState.FINALIZED;
}
function advanceFinality(state, event) {
  return FINALITY_TRANSITIONS.find((t) => t.from === state && t.event === event)?.to ?? null;
}
function finalityProgress(state) {
  switch (state) {
    case FinalityState.PENDING:
      return 0.1;
    case FinalityState.CONFIRMED:
      return 0.6;
    case FinalityState.FINALIZED:
      return 1;
    case FinalityState.REORGED:
      return 0;
    default:
      return 0;
  }
}
function buildFinalityHistory(steps) {
  const normalized = steps.map((s) => ({
    ...s,
    reorg: s.reorg ?? s.state === FinalityState.REORGED
  }));
  const current = normalized.length > 0 ? normalized[normalized.length - 1].state : FinalityState.PENDING;
  const visited = new Set(normalized.map((s) => s.state));
  const reorgCount = normalized.filter((s) => s.reorg).length;
  return { steps: normalized, current, visited, reorgCount, finalized: current === FinalityState.FINALIZED };
}
function historyTraversed(history, from, to) {
  for (let i = 1; i < history.steps.length; i++) {
    if (history.steps[i - 1].state === from && history.steps[i].state === to) return true;
  }
  return false;
}
var REORGED = FinalityState.REORGED;
function FinalityStateMachineView({
  state,
  history,
  confirmations,
  compact,
  className
}) {
  const current = history?.current ?? state ?? FinalityState.PENDING;
  const reorgCount = history?.reorgCount ?? (current === REORGED ? 1 : 0);
  const reached = React9.useMemo(() => {
    const set = new Set(history?.visited ?? []);
    const ci = FINALITY_FORWARD.indexOf(current);
    if (ci >= 0) for (let i = 0; i <= ci; i++) set.add(FINALITY_FORWARD[i]);
    if (current === REORGED) set.add(REORGED);
    return set;
  }, [history, current]);
  const reorgActive = reorgCount > 0 || current === REORGED;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-4", children: [
      /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: FINALITY_FORWARD.map((s, i) => /* @__PURE__ */ jsxs(React9.Fragment, { children: [
        i > 0 && /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            className: cn("shrink-0 text-sm", reached.has(s) ? "text-foreground" : "text-muted-foreground/40"),
            children: "\u2192"
          }
        ),
        /* @__PURE__ */ jsx(
          StateChip,
          {
            state: s,
            current: current === s,
            reached: reached.has(s),
            confirmations: s === FinalityState.CONFIRMED ? confirmations : void 0
          }
        )
      ] }, s)) }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            className: cn("shrink-0 text-sm", reorgActive ? "text-finality-reorged" : "text-muted-foreground/30"),
            children: "\u21BA"
          }
        ),
        /* @__PURE__ */ jsx(
          "span",
          {
            className: cn(
              "rounded-sm border border-dashed px-1 text-[10px]",
              reorgActive ? "border-finality-reorged/40 text-finality-reorged" : "border-muted-foreground/20 text-muted-foreground/40"
            ),
            children: "reorg"
          }
        ),
        /* @__PURE__ */ jsx(StateChip, { state: REORGED, current: current === REORGED, reached: reached.has(REORGED) })
      ] })
    ] }),
    !compact && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Progress, { value: finalityProgress(current), reorged: current === REORGED }),
      history && history.steps.length > 0 && /* @__PURE__ */ jsx(Trail, { history })
    ] })
  ] });
}
function StateChip({
  state,
  current,
  reached,
  confirmations
}) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex rounded-full",
        current && "ring-2 ring-ring ring-offset-1",
        !reached && "opacity-40"
      ),
      children: /* @__PURE__ */ jsx(FinalityPill, { state, confirmations })
    }
  );
}
function Progress({ value, reorged }) {
  return /* @__PURE__ */ jsx("div", { className: "h-1.5 w-full overflow-hidden rounded-full bg-muted", children: /* @__PURE__ */ jsx(
    "div",
    {
      className: cn("h-full rounded-full transition-all", reorged ? "bg-finality-reorged" : "bg-finality-finalized"),
      style: { width: `${Math.round(value * 100)}%` }
    }
  ) });
}
function Trail({ history }) {
  return /* @__PURE__ */ jsx("ol", { className: "flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground", children: history.steps.map((step, i) => /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-2", children: [
    i > 0 && /* @__PURE__ */ jsx("span", { className: "text-muted-foreground/40", children: "\u2192" }),
    /* @__PURE__ */ jsx("span", { className: cn("font-medium", step.reorg ? "text-finality-reorged" : "text-foreground"), children: step.event ?? step.state }),
    step.blockNumber != null && /* @__PURE__ */ jsxs("span", { className: "tabular-nums", children: [
      "#",
      step.blockNumber
    ] })
  ] }, i)) });
}
var NODE_ACCENT = {
  [FinalityState.MEMPOOL]: "border-finality-pending/70",
  [FinalityState.PENDING]: "border-finality-pending/70",
  [FinalityState.OBSERVED]: "border-finality-pending/70",
  [FinalityState.CONFIRMED]: "border-finality-confirmed/70",
  [FinalityState.FINALIZED]: "border-finality-finalized/70",
  [FinalityState.REORGED]: "border-risk-critical/70"
};
function isInFlight(state) {
  return state === FinalityState.MEMPOOL || state === FinalityState.PENDING || state === FinalityState.OBSERVED;
}
function decorateEdge(edge, state) {
  const data = edge.data ?? {};
  if (state === FinalityState.REORGED) {
    return { ...edge, data: { ...data, dashed: true, animated: false, failed: true, variant: "failed" } };
  }
  if (state === FinalityState.FINALIZED) {
    return { ...edge, data: { ...data, dashed: false, animated: false } };
  }
  return { ...edge, data: { ...data, dashed: isInFlight(state) } };
}
function decorateNode(node, state) {
  if (node.data?.failed) return node;
  const accentClassName = NODE_ACCENT[state];
  if (!accentClassName) return node;
  return { ...node, data: { ...node.data, accentClassName } };
}
function decorateFinality(elements, { nodeState, edgeState }) {
  return {
    ...elements,
    nodes: nodeState ? elements.nodes.map((node) => {
      const state = nodeState(node);
      return state ? decorateNode(node, state) : node;
    }) : elements.nodes,
    edges: edgeState ? elements.edges.map((edge) => {
      const state = edgeState(edge);
      return state ? decorateEdge(edge, state) : edge;
    }) : elements.edges
  };
}
function finalityLegendItems(group = "Finality") {
  return [
    { label: "In-flight (mempool / pending)", color: "hsl(var(--finality-pending))", shape: "dashed", group },
    { label: "Confirmed", color: "hsl(var(--finality-confirmed))", shape: "line", group },
    { label: "Finalized", color: "hsl(var(--finality-finalized))", shape: "line", group },
    { label: "Failed / reorged", color: "hsl(var(--risk-critical))", shape: "line", group }
  ];
}

export { ALL_SEARCH_FIELDS, CHAIN_LABEL_ATTR, DEFAULT_EDGE_LEGEND, DEFAULT_SEARCH_FIELDS, EDGE_STROKE, FINALITY_FORWARD, FINALITY_STATES, FINALITY_TRANSITIONS, FLOW_EDGE_KINDS, FinalityStateMachineView, ForceGraph, GRAPH_DYNAMIC_SEED_KIND, GRAPH_SEARCH_FIELDS, GRAPH_SEARCH_MODES, GROUPING_DIMENSION_LIST, GraphAuthoringBar, GraphCanvas, GraphDefaultsProvider, GraphExportMenu, GraphLegend, GraphSearchBox, GraphShareViewer, GraphStatsPanel, GraphTimeline, GraphViewModeToggle, GroupGraphView, NODE_KIND_REGISTRY, NODE_KIND_VARIANT, NodeDrilldownPanel, ReplayControls, SAGA_AWAITING_ATTR, SAGA_GROUP_TYPE, SAGA_KEY_ATTR, SAGA_LABEL_ATTR, SceneGraphView, SharedGraphView, TimeRangeSlider, TransactionGraph, advanceFinality, allNodesMatch, applyClusters, applyGrouping, applySagaBands, buildChainGroupNodes, buildClusters, buildFinalityHistory, buildGraphViewServing, canTransition, chainGroupNode, clusterByChain, clusterByEntity, clusterByKind, clusterIdFromNodeId, clusterNodeId, compactGraphState, computeGraphStats, decorateFinality, dynamicSeedToChannel, edgeTypes, edgeVariantForKind, emptyScene, engineForView, exportGraphData, exportGraphEdges, exportGraphNodes, exportGraphPng, filterGraphByTimeRange, finalityLegendItems, finalityProgress, graphExpansionReducer, graphNodeId, graphShareStateHasContent, graphTimeDomain, groupNodesByChain, groupingDimension, groupingDimensionLabel, historyTraversed, interactionGraphFromRows, interactionGraphToScene, isFlowEdgeKind, isTerminal, layoutDirectionForView, layoutForView, layoutWithDagre, layoutWithElk, matchGraphNodes, mergeGraphElements, mergeNeighborhood, mergeScenes, nextStates, nodeTypes, normalizeDynamicSeed, parseGraphSnapshot, previewGraph, regroupSceneByChain, rowNodeIds, sagaAnchorNodeId, sagaGhostNodeId, sagaGroupId, sceneViewToElements, toGraph, toScene, transitionsFrom, txEdge, txNode, useGraphClusters, useGraphDefaults, useGraphDetailTier, useGraphLiveUpdates, useGraphReducer, useGraphSearch, useGraphTimeRange, useReplay, variantForKind };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map